import { ArrowLeft, ArrowRight, FileDown, Lock, Sparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { AtelierUnlockPopup } from '../components/AtelierUnlockPopup';
import api from "../config/axiosConfig";
import { hasFullAccess } from '../config/plans';
import { useAuth } from "../contexts/AuthContext";
import { ATELIERS_LIBRARY, TEMPLATE_TO_ATELIERS, useAtelierUnlock } from '../hooks/useAtelierUnlock';
import { useRunUsage } from '../hooks/useRunUsage';
import { useVisitorRuns } from '../hooks/useVisitorRuns';
import ACCOMP_ENF_CASE from "../templates/accompagnement-enfance.json";
import ART_CASE from "../templates/art-societe.json";
import ASSMAT2_CASE from "../templates/ass-mat-v3.json";
import ASSMAT_CASE from "../templates/ass-mat.json";
import BAC_CASE from "../templates/bac-test.json";
import DNB_CASE from "../templates/brevet-revision.json";
import ACCOMP_ENF_MED_CASE from "../templates/civilisation-education.json";
import CORPS_CASE from "../templates/corps-langage.json";
import EDU_CASE from "../templates/education-societe.json";
import CIT_CASE from "../templates/engagement-citoyen.json";
import TRADE_CASE from "../templates/finance-temps.json";
import IMMO_CASE from "../templates/immobilier-gestion.json";
import LIENS_CASE from "../templates/lien-transmission.json";
import ORI_CASE from "../templates/orientation-identite.json";
import PARENT_EMP_CASE from "../templates/parent-employeur.json";
import PAR_CASE from "../templates/parentalite-mission.json";
import ENF_CASE from "../templates/protection-enfance.json";
import RETINA_CASE from "../templates/retina-view.json";
import TRADE2_CASE from "../templates/trading-market.json";
import VENTE_CASE from "../templates/vente-commerce.json";
import VOIE_CASE from "../templates/voie-souplesse.json";

const T = {
  bg:          "#04080f",
  bgNoise:     "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")",
  surface:     "rgba(10,18,32,0.9)",
  surfaceGlass:"rgba(12,20,36,0.72)",
  surfaceAlt:  "rgba(15,24,42,0.95)",
  surfaceHov:  "rgba(22,34,58,0.98)",
  glassBlur:   "blur(16px)",

  // Textes — hiérarchie renforcée
  text:        "#f7faff",                 // ~17:1
  textSub:     "#b3c4da",                 // ~10:1  (était #8ca0bc)
  textFaint:   "#cad6e6",                 // texte "fantôme" mais lisible
  textMuted:   "#8095b0",                 // ~7:1   (était #5a6e88 → critique sur bleu)

  // Bordures légèrement plus lisibles
  border:      "rgba(255,255,255,0.07)",
  borderMed:   "rgba(255,255,255,0.12)",
  borderHov:   "rgba(255,255,255,0.20)",

  // Accents — plus lumineux, moins saturés
  orange:      "#ff9d70",                 // était #ff8c5a
  orangeDim:   "rgba(255,157,112,0.14)",
  orangeGlow:  "rgba(255,157,112,0.75)",
  sky:         "#74bfff",                 // était #5fb3ff
  skyDim:      "rgba(116,191,255,0.12)",
  amber:       "#ffd45c",                 // était #f5c842
  amberDim:    "rgba(255,212,92,0.12)",
  amberGlow:   "rgba(255,212,92,0.26)",
  emerald:     "#62e3ab",                 // était #4fd5a0
  emeraldDim:  "rgba(98,227,171,0.12)",
  danger:      "#ff8585",                 // était #ff6b6b
  dangerDim:   "rgba(255,133,133,0.12)",

  shadow:      "0 24px 64px rgba(0,0,0,0.7), 0 4px 16px rgba(0,0,0,0.4)",
  shadowSm:    "0 2px 12px rgba(0,0,0,0.4)",
  shadowGlow:  (c) => `0 0 0 1px ${c}33, 0 4px 24px ${c}40, 0 0 48px ${c}1f`,

  r:           "14px",
  rSm:         "9px",
  rXs:         "5px",
  rPill:       "999px",
};

const TL = {
  bg:          "#f4f6fa",
  bgNoise:     T.bgNoise,
  surface:     "#ffffff",
  surfaceGlass:"rgba(255,255,255,0.9)",
  surfaceAlt:  "#eef2f8",
  surfaceHov:  "#e0e7f1",
  glassBlur:   "blur(16px)",

  // Textes — tous remontés en contraste
  text:        "#0f1a2b",                 // ~16:1
  textSub:     "#3b4f68",                 // ~9:1   (était #4e637c)
  textFaint:   "#6f859c",                 // ~3.4:1 (était #9aadbe → ~2.5:1)
  textMuted:   "#5e7590",                 // ~5:1   (était #7a90a4 → ~3:1)

  // Bordures plus franches pour se démarquer du blanc
  border:      "rgba(15,26,43,0.08)",
  borderMed:   "rgba(15,26,43,0.14)",
  borderHov:   "rgba(15,26,43,0.22)",

  // Accents — plus profonds, plus saturés
  orange:      "#c94e1e",                 // était #d95f2b
  orangeDim:   "rgba(201,78,30,0.09)",
  orangeGlow:  "rgba(201,78,30,0.22)",
  sky:         "#0e6cb5",                 // était #1d7ec7
  skyDim:      "rgba(14,108,181,0.09)",
  amber:       "#9c6a00",                 // était #c08a00 (trop jaune-clair)
  amberDim:    "rgba(156,106,0,0.09)",
  amberGlow:   "rgba(156,106,0,0.22)",
  emerald:     "#0f7a55",                 // était #1a8f67
  emeraldDim:  "rgba(15,122,85,0.09)",
  danger:      "#bd2424",                 // était #c83030
  dangerDim:   "rgba(189,36,36,0.09)",

  shadow:      "0 8px 32px rgba(100,120,150,0.16), 0 2px 8px rgba(0,0,0,0.06)",
  shadowSm:    "0 2px 10px rgba(100,120,150,0.12)",
  shadowGlow:  (c) => `0 0 0 1px ${c}22, 0 4px 20px ${c}2a`,

  r:           "14px",
  rSm:         "9px",
  rXs:         "5px",
  rPill:       "999px",
};

const Mono = ({ children, size = 11, color = T.textMuted, w = false, style = {} }) => (
  <span
    style={{
      fontFamily: "'DM Mono', 'Roboto Mono', monospace",
      fontSize: size,
      letterSpacing: '0.06em',
      color,
      fontWeight: w ? 700 : 400,
      lineHeight: 1,
      ...style,
    }}
  >
    {children}
  </span>
);

const FAMILY_PALETTE = [
  "#60a5fa","#fb923c","#4ade80","#f472b6", "#4fd5a0",
  "#a78bfa","#2dd4bf","#94a3b8","#fb7185", "#321575", "#fa7122"
];

const TONE_COLOR = {
  radieux:    "#f59e0b",
  lucide:     "#0ea5e9",
  tragique:   "#ef4444",
  cynique:    "#8b5cf6",
  rassurant:  "#10b981",
  analytique: "#64748b"
};

const AMBIANCE_ACCENT = {
  tension:    "orange",
  hope:       "sky",
  resolution: "amber",
  default:    "orange",
};

const DEBRIEF_SECTIONS = {
  full:     ["overview","metrics","parcours","signaux","theorie","decisions","transfert","next"],
  standard: ["overview","metrics","parcours","signaux","theorie","decisions","next"],
  starter:  ["overview","parcours","signaux","theorie","decisions","transfert","next"],
};

const SECTION_META = {
  overview:   { label: "Vue d'ensemble",        icon: "◎" },
  metrics:    { label: "Indicateurs",           icon: "⬡" },
  parcours:   { label: "Parcours complet",      icon: "◈" },
  signaux:    { label: "Signaux diagnostiques", icon: "△" },
  theorie:    { label: "Éclairage théorique",   icon: "◇" },
  decisions:  { label: "Analyse décisionnelle", icon: "▣" },
  transfert:  { label: "Transférabilité",       icon: "◉" },
  next:       { label: "Étapes suivantes",      icon: "→" },
};

const TEMPLATE_VISUAL = {
  "bac-revision-v1": {
    icon: "🎓",
    color: "#5fb3ff",
    audience: "Lycéen · Parent · Enseignant · Educateur",
    locked: false,
  },
  "assistante-maternelle-identite-v1": {
    icon: "🏠",
    color: "#4fd5a0",
    audience: "Assistante maternelle · Famille · Institution",
    locked: true,
  },
  "orientation-identite": {
    icon: "🧭",
    color: "#f5c842",
    audience: "Élève · Parent · Conseiller · Formateur",
    locked: true,
  },
  "lien-transmission": {
    icon: "📡",
    color: "#fa7122",
    audience: "Parent · Thérapeute · Educateur · Ressource",
    locked: true,
  },
  "assistante-maternelle-identite-v2": {
    icon: "🧒",
    color: "#a78bfa",
    audience: "Travailleur social · Institution · Parent",
    locked: false,
  },
  "education-societe": {
    icon: "🧮",
    color: "#a03aff",
    audience: "Citoyen · Institution · Famille · Formateur",
    locked: true,
  },
  "parentalite-mission": {
    icon: "🎫",
    color: "#e73f15",
    audience: "Père · Mère · Famille · Parent · Formateur",
    locked: true,
  },
  "art-societe": {
    icon: "🎭",
    color: "#e71590",
    audience: "Artiste · Citoyen · Médiateur · Famille",
    locked: true,
  },
  "corps-langage": {
    icon: "💭",
    color: "#09612b",
    audience: "Educateur · Praticien · Médiateur · Participant",
    locked: true,
  },
  "brevet-revision": {
    icon: "🎓",
    color: "#7cc47c",
    audience: "Collégien · Parent · Enseignant · Educateur",
    locked: false,
  },
  "engagement-citoyen": {
    icon: "🧮",
    color: "#6a64c4",
    audience: "Citoyen · Médiateur · Association · Sentinelle",
    locked: true,
  },
  "protection-enfance-systeme-frontiere": {
    icon: "📡",
    color: "#ffd19d",
    audience: "Famille · Travailleur social · Institution",
    locked: true,
  },
  "parent-employeur-frontiere": {
    icon: "📡",
    color: "#ffa49d",
    audience: "Parents · Employeur · AssMat · Institution",
    locked: true,
  }, 
  "immobilier-gestion": {
    icon: "📡",
    color: "#9c1515",
    audience: "Locataire · Propriétaire · Institution",
    locked: true,
  },
  "voie-souplesse": {
    icon: "💭",
    color: "#09612b",
    audience: "Tous publics · Praticien · Médiateur",
    locked: true,
  },
  "accompagnement-enfance": {
    icon: "💭",
    color: "#ec6dd1",
    audience: "Tous publics · Citoyen · Médiateur",
    locked: true,
  },
  "accompagnement-enfance-mediateurs": {
    icon: "🧮",
    color: "#005383",
    audience: "Tous publics · Citoyen · Médiateur",
    locked: true,
  },
  "vente-commerce": {
    icon: "💹​",
    color: "#321575",
    audience: "Apprentis · Formateur · Artisans · Commerçants · Vendeur",
    locked: true,
  },
  "retina-view": {
    icon: "​🩺​​",
    color: "#0ed8ac",
    audience: "Formateur · Ophtalmologue · Interne · Chercheur · Patient",
    locked: true,
  },
  "finance-temps": {
    icon: "​​​💹​​​",
    color: "#321575",
    audience: "Citoyen · Banquier · Trader · Enseignant · Institution",
    locked: true,
  },
  "trader-decision-incertitude": {
    icon: "​​​💹​​​",
    color: "#321575",
    audience: "Broker · Trader · RiskManager · Sales · Indépendant",
    locked: true,
  },

};

const getTemplateMeta = (slug) =>
  TEMPLATE_VISUAL[slug] || {
    icon: "💭",
    color: "#ff8c5a",
    audience: "Multi-profils",
    hook: "Une préparation aux parcours et aux ateliers.",
    locked: true,
  };

const ALL_TEMPLATES = [IMMO_CASE, TRADE2_CASE, TRADE_CASE, RETINA_CASE, VENTE_CASE, ENF_CASE, PAR_CASE, PARENT_EMP_CASE, IMMO_CASE, ACCOMP_ENF_MED_CASE, ACCOMP_ENF_CASE, BAC_CASE, DNB_CASE, CIT_CASE, VOIE_CASE, ART_CASE, ASSMAT_CASE, CORPS_CASE, LIENS_CASE, ASSMAT2_CASE, ORI_CASE, EDU_CASE];

// Fonction principale
const isTemplateLocked = (template, user) => {
  const meta = getTemplateMeta(template.slug);

  // Templates toujours gratuits 
  const freeForEveryone = [
    'assistante-maternelle-identite-v2',
    'bac-revision-v1',
    'brevet-revision-v1',
    'parentalite-mission',
    'engagement-citoyen',
    'accompagnement-enfance',
    'art-societe',
  ];

  if (freeForEveryone.includes(template.slug)) {
    return false;
  }

  // Accès total
  if (hasFullAccess(user)) {
    return false;
  }

  // Visiteur non connecté
  if (!user) {
    return !!meta.locked;
  }

  // Utilisateur connecté sans accès total (decouverte ou équivalent)
  return !!meta.locked && !(user.unlockedRuns || []).includes(template.slug);
};


const EXPORT_ROLES = ['superadmin', 'admin', 'entreprise', 'collectivité', 'pro', 'staff',];

export const GUIDE_CONTENT = {
  zones: [
    {
      id: "landing",
      label: "Avant de commencer",
      hint: "Choisir un profil n'est pas une formalité. Il oriente ce que vous verrez — certains contenus, certaines notes, certaines lectures vous seront adressées directement. Si vous n'êtes pas sûr, choisissez le profil qui décrit votre position réelle, pas celle que vous aimeriez avoir.",
      full: "La simulation que vous allez traverser a été conçu pour un profil spécifique — le vôtre. Chaque profil reçoit les mêmes situations, mais les lit depuis une position différente. Ce n'est pas un filtre : c'est une mise en contexte. Vous pouvez relancer l'expérience avec un autre profil à tout moment pour changer de point de vue."
    },
    {
      id: "narrative",
      label: "Les phases de lecture",
      hint: "Ces phases ne sont pas du remplissage. Elles posent la tension que vous allez traverser — sans elles, les diagnostics n'ont pas de sol. Prenez le temps de lire, même si vous avez envie de cliquer.",
      full: "Certaines phases durent quelques secondes avant de vous laisser avancer. Ce délai est intentionnel : il laisse le texte s'installer. La problématisation et la question épistémologique qui accompagnent chaque étape sont les clés de lecture du run — elles disent pourquoi cette étape existe, pas seulement ce qu'elle contient."
    },
    {
      id: "actions",
      label: "Les diagnostics",
      hint: "Vous ne verrez pas toujours tous les diagnostics disponibles. Certains n'apparaissent qu'après que vous en ayez activé d'autres — parce que dans la réalité, certaines prises de conscience en précèdent d'autres. Ce n'est pas un bug : c'est la logique de causalité du run.",
      full: "Chaque diagnostic que vous activez a un effet sur les indicateurs — parfois positif, parfois négatif. Un diagnostic négatif n'est pas un échec : c'est un signal que vous avez identifié quelque chose de difficile. Dans la vie réelle aussi, nommer un problème le fait d'abord apparaître avant qu'on puisse le résoudre. Vous pouvez activer plusieurs diagnostics dans la même phase : leurs effets se cumulent."
    },
    {
      id: "deepfeedback",
      label: "L'analyse d'impact",
      hint: "Ce panneau est le cœur du run. Il ne résume pas — il approfondit. Chaque section a une logique : ce qui s'est passé, pourquoi ça compte, ce que ça coûte, et ce qui peut changer.",
      full: "L'analyse d'impact est structurée en quatre niveaux. 'Ce qui s'est passé' décrit le mécanisme concret — souvent ancré dans un texte de loi, une étude ou une réalité de terrain. 'Pourquoi ça compte' monte en abstraction : qu'est-ce que ça dit du système ? 'Ce que ça coûte' nomme les effets réels pour chaque partie. 'Ce qui peut changer' propose un levier — pas une solution magique, mais un premier geste possible. Ces quatre niveaux forment une unité : lire seulement le feedback court, c'est passer à côté de l'essentiel."
    },
    {
      id: "indicateurs",
      label: "Les indicateurs",
      hint: "Les indicateurs bougent dans les deux sens. Une baisse n'est pas un échec — c'est souvent le signe que vous venez de nommer quelque chose de difficile. Ce qui compte, c'est la trajectoire sur l'ensemble du run.",
      full: "Chaque indicateur mesure une dimension spécifique du système que vous explorez — pas votre performance personnelle. Ils sont interdépendants : améliorer l'un peut en affecter un autre. À la fin du run, leur évolution globale sert à calculer votre configuration et à identifier la solution la plus proche de ce que vous avez traversé."
    },
    {
      id: "debrief",
      label: "Le débrief final",
      hint: "Le débrief n'est pas une note. C'est une lecture de ce que vous avez traversé — et de ce que ça dit de votre position dans le système. La solution matchée décrit la configuration la plus cohérente avec vos choix, pas la 'meilleure' réponse.",
      full: "Le score et la solution matchée sont des miroirs, pas des verdicts. Ils synthétisent vos diagnostics, vos leviers activés et l'évolution de vos indicateurs pour produire une lecture systémique de votre parcours. Relancer La simulation avec d'autres choix produit une lecture différente — c'est là que la réflexion commence vraiment."
    }
  ]
};

const canExport = (user, userPlan) => {
  if (!user) return false;
  if (EXPORT_ROLES.includes(user?.role)) return true;
  return EXPORT_ROLES.includes(userPlan);
};


async function loadJsPDF() {
  if (window.jspdf) return window.jspdf.jsPDF;
  return new Promise((resolve, reject) => {
    const s1 = document.createElement('script');
    s1.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    s1.onload = () => {
      const s2 = document.createElement('script');
      s2.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';
      s2.onload = () => resolve(window.jspdf.jsPDF);
      s2.onerror = reject;
      document.head.appendChild(s2);
    };
    s1.onerror = reject;
    document.head.appendChild(s1);
  });
}

async function exportDebriefPDF({ config, sol, cd, eng }) {
  const jsPDF = await loadJsPDF();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const W             = 210;
  const M_X           = 18;
  const CW            = W - M_X * 2;
  const PAGE_H        = 297;
  const MAX_Y         = PAGE_H - 25;

  let y = 22;

  const accentHex = sol?.interpretation?.tone
    ? (TONE_COLOR[sol.interpretation.tone] || '#ff8c5a')
    : '#ff8c5a';
  const hex2rgb = h => {
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h);
    return r ? [parseInt(r[1],16), parseInt(r[2],16), parseInt(r[3],16)] : [255,140,90];
  };
  const [ar, ag, ab] = hex2rgb(accentHex);

  const sanitize = (str) => {
    if (!str) return '';

    const translitMap = {
      '✓': '+',
      '○': '-',
      '△': '!',
      '💡': '',
      '\u2019': "'",   // ’
      '\u2018': "'",   // ‘
      '\u201C': '"',   // “
      '\u201D': '"',   // ”
      '\u2013': '-',   // –
      '\u2014': '-',   // —
      '\u2026': '...', // …
      '\u00B0': 'deg', // °
      '\u20AC': 'EUR', // €
      '\u2022': '-',   // •
      '\u2192': '>',   // →
    };

    const replacer = (char) => translitMap[char] ?? '';

    return String(str)
      .replace(/✓|○|△|💡/g, replacer)
      .replace(/[\u0100-\uFFFF]/g, replacer);   // ← ici la correction
  };

  const setStyle = (variant) => {
    const styles = {
      cover_title:   { font: 'bold',   size: 26, color: [240, 244, 255] },
      cover_tag:     { font: 'bold',   size: 12, color: [ar, ag, ab]    },
      cover_meta:    { font: 'normal', size: 10, color: [140, 160, 188] },
      cover_name:    { font: 'bold',   size: 14, color: [ar, ag, ab]    },
      cover_summary: { font: 'italic', size: 11, color: [190, 210, 230] },
      section:       { font: 'bold',   size: 13, color: [ar, ag, ab]    },
      h3:            { font: 'bold',   size: 11, color: [ar, ag, ab]    },
      body:          { font: 'normal', size: 10, color: [200, 220, 240] },
      body_dim:      { font: 'normal', size: 10, color: [160, 185, 210] },
      small:         { font: 'normal', size:  9, color: [140, 160, 188] },
      small_accent:  { font: 'normal', size:  9, color: [95, 179, 255]  },
      small_dim:     { font: 'normal', size:  9, color: [90, 110, 136]  },
      small_success: { font: 'normal', size:  9, color: [200, 220, 240] },
      label_green:   { font: 'bold',   size:  9, color: [79, 213, 160]  },
      label_gray:    { font: 'bold',   size:  9, color: [140, 160, 188] },
      label_red:     { font: 'bold',   size: 10, color: [255, 107, 107] },
      chip_label:    { font: 'bold',   size:  9, color: [140, 160, 188] },
      chip_val:      { font: 'bold',   size:  9, color: [240, 244, 255] },
      stat_label:    { font: 'bold',   size: 10, color: [200, 220, 240] },
      stat_val:      { font: 'bold',   size: 10, color: [ar, ag, ab]    },
    };
    const s = styles[variant] || styles.body;
    doc.setFont('helvetica', s.font);
    doc.setFontSize(s.size);
    doc.setTextColor(...s.color);
  };

  // ── Layout ──────────────────────────────────────────────────────────────────
  const paintBg = () => {
    doc.setFillColor(4, 8, 15);
    doc.rect(0, 0, W, PAGE_H, 'F');
  };

  // Saut de page si nécessaire + fond repeint
  const ensureSpace = (needed = 7) => {
    if (y + needed > MAX_Y) {
      doc.addPage();
      paintBg();
      y = 22;
    }
  };

  // Écrit du texte wrappé, réapplique le style après chaque saut de page
  const wrappedLines = (text, x, maxW, lineH, styleVariant) => {
    if (!text) return;
    const clean = sanitize(text);
    setStyle(styleVariant);
    const lines = doc.splitTextToSize(clean, maxW);
    lines.forEach(l => {
      ensureSpace(lineH + 1);
      setStyle(styleVariant);  // toujours réappliquer après ensureSpace
      doc.text(l, x, y);
      y += lineH;
    });
  };

  // ── Composants ──────────────────────────────────────────────────────────────
  const sectionTitle = (title) => {
    ensureSpace(22);
    doc.setFillColor(ar, ag, ab);
    doc.rect(M_X, y - 1, 3, 9, 'F');
    setStyle('section');
    doc.text(sanitize(title), M_X + 7, y + 6);
    y += 14;
  };

  const chip = (label, val) => {
    ensureSpace(10);
    setStyle('chip_label');
    doc.text(sanitize(label).toUpperCase(), M_X, y);
    setStyle('chip_val');
    doc.text(sanitize(String(val)), M_X + 52, y);
    y += 7;
  };

  const statBar = (label, value, delta) => {
    ensureSpace(13);
    setStyle('stat_label');
    doc.text(sanitize(label), M_X, y);
    setStyle('stat_val');
    doc.text(String(value), M_X + 90, y, { align: 'right' });
    if (delta !== 0) {
      doc.setTextColor(
        delta > 0 ? 245 : 255,
        delta > 0 ? 200 : 107,
        delta > 0 ? 66  : 107
      );
      doc.text(`${delta > 0 ? '+' : ''}${delta}`, M_X + 110, y, { align: 'right' });
    }
    const barY = y + 2;
    doc.setFillColor(40, 55, 80);
    doc.roundedRect(M_X, barY, CW, 3, 1, 1, 'F');
    doc.setFillColor(ar, ag, ab);
    doc.roundedRect(M_X, barY, Math.max(0, Math.min(1, value / 100)) * CW, 3, 1, 1, 'F');
    y += 11;
  };

  // ── Page 1 : Couverture ─────────────────────────────────────────────────────
  paintBg();
  doc.setFillColor(ar, ag, ab);
  doc.rect(0, 0, 5, PAGE_H, 'F');

  let cy = 55;
  setStyle('cover_title');
  doc.splitTextToSize(sanitize(cd.name), CW).forEach(l => {
    doc.text(l, M_X, cy); cy += 10;
  });

  setStyle('cover_tag');
  doc.text('RAPPORT DE DEBRIEF - PESACORE Architect', M_X, cy + 4);

  setStyle('cover_meta');
  doc.text(`Score de configuration : ${config.configScore}%`, M_X, cy + 16);
  doc.text(`Type : ${config.debriefType.toUpperCase()}`,       M_X, cy + 23);
  doc.text(`Genere le ${new Date().toLocaleDateString('fr-FR')}`, M_X, cy + 30);

  if (sol?.name) {
    setStyle('cover_name');
    doc.splitTextToSize(sanitize(sol.name), CW).forEach((l, i) => {
      doc.text(l, M_X, cy + 50 + i * 7);
    });
  }
  if (sol?.interpretation?.shortSummary) {
    setStyle('cover_summary');
    doc.splitTextToSize(sanitize(sol.interpretation.shortSummary), CW).forEach((l, i) => {
      doc.text(l, M_X, cy + 65 + i * 6);
    });
  }

  // ── Page 2 : Vue d'ensemble ─────────────────────────────────────────────────
  doc.addPage(); paintBg(); y = 22;
  sectionTitle("Vue d'ensemble");

  if (sol?.description)                        wrappedLines(sol.description,                       M_X, CW, 5.5, 'body_dim');
  if (sol?.interpretation) {
    y += 4;
    chip('Potentiel theorique',   `${sol.interpretation.theoreticalPromise    ?? '-'}%`);
    chip('Probabilite reelle',    `${sol.interpretation.realWorldProbability  ?? '-'}%`);
    chip('Score de configuration', `${config.configScore}%`);
    if (sol.interpretation.keyInsight)         { y += 4; wrappedLines(`"${sol.interpretation.keyInsight}"`, M_X, CW, 5.5, 'body'); }
    if (sol.interpretation.pedagogicalMessage) wrappedLines(sol.interpretation.pedagogicalMessage, M_X, CW, 5.5, 'body');
  }

  // ── Indicateurs ─────────────────────────────────────────────────────────────
  y += 6;
  sectionTitle('Indicateurs');
  const statMeta = buildStatMeta(cd);
  Object.entries(eng.params).forEach(([k, v]) => {
    statBar(statMeta[k]?.label || k, v, v - (eng.initialParams[k] ?? 50));
  });

  // ── Page 3 : Parcours ────────────────────────────────────────────────────────
  doc.addPage(); paintBg(); y = 22;
  sectionTitle('Reconstruction du parcours');

  cd.steps.forEach((step, si) => {
    wrappedLines(`${si + 1}. ${step.title}`, M_X, CW, 6.5, 'h3');
    y += 2;

    const acts = (step.actions || []).filter(a => eng.completed.has(a.id));
    if (acts.length) {
      acts.forEach(act => {
        wrappedLines(`+ ${act.label}`, M_X + 4, CW - 6, 5, 'small');
        if (act.feedback) wrappedLines(act.feedback, M_X + 8, CW - 10, 4.5, 'small_accent');
        y += 2;
      });
    } else {
      ensureSpace(7);
      setStyle('small_dim');
      doc.text('Aucune action activee.', M_X + 4, y);
      y += 7;
    }
    y += 3;
  });

  // ── Page : Signaux diagnostiques ─────────────────────────────────────────────
  if (config.diagSignals?.length) {
    doc.addPage(); paintBg(); y = 22;
    sectionTitle('Signaux diagnostiques');

    config.diagSignals.forEach(sig => {
      wrappedLines(`! ${sig.label}`, M_X, CW, 5.5, 'label_red');
      y += 1;
      if (sig.feedback)                  wrappedLines(sig.feedback,                    M_X + 4, CW - 4, 5, 'small_accent');
      if (sig.deepFeedback?.whatItCosts) wrappedLines(sig.deepFeedback.whatItCosts,    M_X + 4, CW - 4, 5, 'body');
      y += 4;
    });
  }

  // ── Page : Eclairage théorique ───────────────────────────────────────────────
  const frameworks = sol?.interpretation?.theoreticalFrameworks;
  const hasFrameworks = Array.isArray(frameworks) && frameworks.length > 0;
  const epistemologicalSteps = cd.steps.filter(s => s.epistemologicalQuestion);
  
  if (hasFrameworks || epistemologicalSteps.length > 0) {
    doc.addPage(); paintBg(); y = 22;
    sectionTitle('Eclairage theorique');

    if (hasFrameworks) {
      frameworks.forEach(fw => {
        // fw peut être une string ou un objet { name, description }
        const name = typeof fw === 'string' ? fw : (fw.name || '');
        const desc = typeof fw === 'object' ? (fw.description || '') : '';
        
        if (name) wrappedLines(name, M_X, CW, 6, 'h3');
        y += 2;
        if (desc) wrappedLines(desc, M_X, CW, 5.5, 'body');
        y += 4;
      });
    } else {
      // Fallback : questions épistémologiques par étape
      epistemologicalSteps.forEach(step => {
        wrappedLines(step.title, M_X, CW, 6, 'h3');
        y += 2;
        wrappedLines(step.epistemologicalQuestion, M_X, CW, 5.5, 'body');
        y += 4;
      });
    }
  }

  // ── Page : Analyse décisionnelle ─────────────────────────────────────────────
  doc.addPage(); paintBg(); y = 22;
  sectionTitle('Analyse decisionnelle');

  const { diagIds, profileIds } = buildActionSets(cd);
  const allFeature = (cd.steps || []).flatMap(s => s.actions || [])
    .filter(a => !diagIds.has(a.id) && !profileIds.has(a.id));
  const done   = allFeature.filter(a =>  eng.completed.has(a.id));
  const undone = allFeature.filter(a => !eng.completed.has(a.id));

  wrappedLines(
    `${done.length} action(s) activee(s) sur ${allFeature.length} disponibles - Score : ${config.configScore}%`,
    M_X, CW, 5.5, 'body'
  );
  y += 2;

  if (done.length) {
    ensureSpace(10);
    setStyle('label_green');
    doc.text('LEVIERS ACTIVES', M_X, y);
    y += 7;
    done.forEach(act => {
      wrappedLines(`+ ${act.label}`, M_X, CW, 4.5, 'small_success');
      if (act.feedback) wrappedLines(act.feedback, M_X + 4, CW - 4, 4.5, 'small_accent');
      y += 1;
    });
  }

  if (undone.length) {
    y += 4;
    ensureSpace(10);
    setStyle('label_gray');
    doc.text('NON ACTIVES', M_X, y);
    y += 7;
    undone.forEach(act => {
      wrappedLines(`- ${act.label}`, M_X, CW, 4.5, 'small_dim');
      y += 1;
    });
  }

  // ── Page : Transférabilité ───────────────────────────────────────────────────
  const transferItems = sol?.interpretation?.transferability?.length
    ? sol.interpretation.transferability
    : (cd.counterfactuals || []).map(c => c.description);

  if (transferItems.length) {
    doc.addPage(); paintBg(); y = 22;
    sectionTitle('Transferabilite et etapes suivantes');
    transferItems.forEach(item => {
      wrappedLines(typeof item === 'string' ? item : (item.description || ''), M_X, CW, 5.5, 'body');
    });
  }

  // ── Footer ───────────────────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(60, 80, 100);
    doc.text(`PESACORE - ${sanitize(cd.name)}`, M_X, 290);
    doc.text(`${p} / ${totalPages}`, W - M_X, 290, { align: 'right' });
    doc.setFillColor(ar, ag, ab);
    doc.rect(0, 293, W, 1.5, 'F');
  }

  doc.save(`debrief-${cd.slug}-${new Date().toISOString().split('T')[0]}.pdf`);
}

async function loadPptxGenJs() {
  if (window.PptxGenJS) return window.PptxGenJS;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    // Version la plus stable et recommandée actuellement
    script.src = 'https://cdn.jsdelivr.net/gh/gitbrent/pptxgenjs@3.12.0/dist/pptxgen.bundle.js';
    
    script.onload = () => {
      // Petite pause pour laisser le bundle s'initialiser complètement
      setTimeout(() => {
        if (window.PptxGenJS) {
          console.log('✅ PptxGenJS loaded successfully from jsDelivr');
          resolve(window.PptxGenJS);
        } else {
          reject(new Error('PptxGenJS not found on window after loading'));
        }
      }, 400);
    };

    script.onerror = () => {
      reject(new Error('Failed to load pptxgenjs from jsDelivr'));
    };

    document.head.appendChild(script);
  });
}

async function exportDebriefPPTX({ config, sol, cd, eng }) {
  const PptxGenJS = await loadPptxGenJs();
  const prs = new PptxGenJS();
  prs.layout = 'LAYOUT_16x9';
  prs.title = `Débrief — ${cd.name}`;
  prs.author = 'PESACORE';

  const accentRaw = sol?.interpretation?.tone ? (TONE_COLOR[sol.interpretation.tone] || '#ff8c5a') : '#ff8c5a';
  const accent = accentRaw.replace('#', '');
  const BG = '04080F';
  const BG2 = '0A1220';
  const LIGHT = 'F0F4FF';
  const SUB = '8CA0BC';
  const FAINT = '5A6E88';

  const addBg = (slide, color = BG) => slide.addShape(prs.ShapeType.rect, { x: 0, y: 0, w: '100%', h: '100%', fill: { color } });
  const accentBar = (slide, h = 0.06) => slide.addShape(prs.ShapeType.rect, { x: 0, y: 0, w: 0.06, h: 5.625, fill: { color: accent } });
  const tag = (slide, text, x, y, color = accent) => slide.addText(text, { x, y, w: 3, h: 0.28, fontSize: 8, bold: true, color, charSpacing: 3, fontFace: 'DM Sans' });

  // ── Slide 1 : Couverture ──
  {
    const s = prs.addSlide();
    addBg(s);
    accentBar(s);
    s.addShape(prs.ShapeType.rect, { x: 0, y: 4.8, w: '100%', h: 0.03, fill: { color: accent } });
    s.addText('RAPPORT DE DÉBRIEF', { x: 0.4, y: 0.6, w: 9, h: 0.35, fontSize: 9, bold: true, color: accent, charSpacing: 4, fontFace: 'DM Sans' });
    s.addText(cd.name, { x: 0.4, y: 1.2, w: 8.5, h: 1.4, fontSize: 32, bold: true, color: LIGHT, fontFace: 'DM Sans', valign: 'top' });
    if (sol?.name) s.addText(sol.name, { x: 0.4, y: 2.7, w: 8, h: 0.5, fontSize: 16, color: accent, fontFace: 'DM Sans' });
    if (sol?.interpretation?.shortSummary) s.addText(sol.interpretation.shortSummary, { x: 0.4, y: 3.35, w: 8, h: 0.9, fontSize: 10, color: SUB, italic: true, fontFace: 'DM Sans' });
    s.addText([
      { text: `Score  `, options: { color: FAINT, bold: false } },
      { text: `${config.configScore}%`, options: { color: LIGHT, bold: true } },
      { text: `   ·   Type  `, options: { color: FAINT, bold: false } },
      { text: config.debriefType.toUpperCase(), options: { color: accent, bold: true } },
      { text: `   ·   ${new Date().toLocaleDateString('fr-FR')}`, options: { color: FAINT, bold: false } },
    ], { x: 0.4, y: 4.9, w: 9, h: 0.3, fontSize: 9, fontFace: 'DM Sans' });
  }

  // ── Slide 2 : Indicateurs clés ──
  if (sol?.interpretation) {
    const s = prs.addSlide();
    addBg(s); accentBar(s);
    tag(s, 'INDICATEURS CLÉS', 0.4, 0.3);
    const metrics3 = [
      { label: 'Potentiel théorique', val: `${sol.interpretation.theoreticalPromise ?? '—'}%` },
      { label: 'Probabilité réelle',  val: `${sol.interpretation.realWorldProbability ?? '—'}%` },
      { label: 'Score de config.',    val: `${config.configScore}%` },
    ];
    metrics3.forEach((m, i) => {
      const bx = 0.4 + i * 3.2;
      s.addShape(prs.ShapeType.roundRect, { x: bx, y: 0.75, w: 3, h: 2, fill: { color: BG2 }, line: { color: accent, width: 0.5 }, rectRadius: 0.1 });
      s.addText(m.val, { x: bx, y: 0.95, w: 3, h: 0.9, fontSize: 36, bold: true, color: accent, align: 'center', fontFace: 'DM Sans' });
      s.addText(m.label, { x: bx, y: 1.95, w: 3, h: 0.5, fontSize: 9, color: SUB, align: 'center', fontFace: 'DM Sans' });
    });
    if (sol.interpretation.keyInsight) {
      s.addShape(prs.ShapeType.roundRect, { x: 0.4, y: 2.95, w: 9.2, h: 1.1, fill: { color: '1A0E00' }, line: { color: accent, width: 0.5 }, rectRadius: 0.1 });
      s.addText([
        { text: '💡  ', options: { fontSize: 11 } },
        { text: sol.interpretation.keyInsight, options: { italic: true, color: LIGHT } },
      ], { x: 0.6, y: 3.05, w: 8.8, h: 0.9, fontSize: 10, fontFace: 'DM Sans' });
    }
    if (sol.interpretation.pedagogicalMessage) {
      s.addText(sol.interpretation.pedagogicalMessage, { x: 0.4, y: 4.25, w: 9.2, h: 0.8, fontSize: 9.5, color: SUB, italic: true, fontFace: 'DM Sans' });
    }
  }

  // ── Slide 3 : Métriques du système ──
  {
    const s = prs.addSlide();
    addBg(s); accentBar(s);
    tag(s, 'MÉTRIQUES DU SYSTÈME', 0.4, 0.3);
    const statMeta = buildStatMeta(cd);
    const entries = Object.entries(eng.params);
    entries.forEach(([k, v], i) => {
      const init = eng.initialParams[k] ?? 50;
      const delta = v - init;
      const row = Math.floor(i / 2);
      const col = i % 2;
      const bx = 0.4 + col * 4.7;
      const by = 0.75 + row * 1.2;
      s.addShape(prs.ShapeType.roundRect, { x: bx, y: by, w: 4.5, h: 1.05, fill: { color: BG2 }, line: { color: '1A2840', width: 0.5 }, rectRadius: 0.08 });
      s.addText(statMeta[k]?.label || k, { x: bx + 0.12, y: by + 0.08, w: 3.5, h: 0.28, fontSize: 9, color: SUB, fontFace: 'DM Sans' });
      s.addText(`${v}`, { x: bx + 0.12, y: by + 0.38, w: 1.2, h: 0.45, fontSize: 28, bold: true, color: LIGHT, fontFace: 'DM Sans' });
      if (delta !== 0) {
        const dc = delta > 0 ? 'F5C842' : 'FF6B6B';
        s.addText(`${delta > 0 ? '+' : ''}${delta}`, { x: bx + 1.5, y: by + 0.5, w: 0.8, h: 0.28, fontSize: 10, bold: true, color: dc, fontFace: 'DM Sans' });
      }
      // Mini bar
      s.addShape(prs.ShapeType.rect, { x: bx + 0.12, y: by + 0.88, w: 4.26, h: 0.07, fill: { color: '1A2840' } });
      s.addShape(prs.ShapeType.rect, { x: bx + 0.12, y: by + 0.88, w: Math.max(0.02, 4.26 * v / 100), h: 0.07, fill: { color: accent } });
    });
  }

  // ── Slide 4 : Parcours ──
  {
    const s = prs.addSlide();
    addBg(s); accentBar(s);
    tag(s, 'PARCOURS COMPLET', 0.4, 0.3);
    let by = 0.72;
    cd.steps.forEach((step, si) => {
      const acts = (step.actions || []).filter(a => eng.completed.has(a.id));
      s.addShape(prs.ShapeType.ellipse, { x: 0.4, y: by + 0.02, w: 0.28, h: 0.28, fill: { color: acts.length ? accent : FAINT } });
      s.addText(`${si + 1}`, { x: 0.4, y: by + 0.02, w: 0.28, h: 0.28, fontSize: 8, bold: true, color: acts.length ? BG : LIGHT, align: 'center', valign: 'middle', fontFace: 'DM Sans' });
      s.addText(step.title, { x: 0.8, y: by, w: 4.8, h: 0.32, fontSize: 10, bold: true, color: acts.length ? LIGHT : FAINT, fontFace: 'DM Sans' });
      s.addText(`${acts.length} action${acts.length !== 1 ? 's' : ''}`, { x: 5.7, y: by, w: 1.5, h: 0.32, fontSize: 9, color: acts.length ? accent : FAINT, align: 'right', fontFace: 'DM Sans' });
      if (acts.length && by < 4.5) {
        const preview = acts[0].label.length > 80 ? acts[0].label.substring(0, 80) + '…' : acts[0].label;
        s.addText(`  ✓  ${preview}`, { x: 0.8, y: by + 0.32, w: 8.8, h: 0.28, fontSize: 8.5, color: SUB, italic: true, fontFace: 'DM Sans' });
        by += 0.28;
      }
      by += 0.72;
    });
  }

  // ── Slide 5 : Signaux diagnostiques ──
  if (config.diagSignals?.length) {
    const s = prs.addSlide();
    addBg(s); accentBar(s);
    tag(s, 'SIGNAUX DIAGNOSTIQUES', 0.4, 0.3);
    const MAX = 5;
    config.diagSignals.slice(0, MAX).forEach((sig, i) => {
      const by = 0.72 + i * 0.9;
      s.addShape(prs.ShapeType.roundRect, { x: 0.4, y: by, w: 9.2, h: 0.78, fill: { color: '1A0000' }, line: { color: 'FF6B6B', width: 0.4 }, rectRadius: 0.07 });
      s.addText('△', { x: 0.52, y: by + 0.08, w: 0.4, h: 0.5, fontSize: 12, color: 'FF6B6B', fontFace: 'DM Sans' });
      const label = sig.label.length > 100 ? sig.label.substring(0, 100) + '…' : sig.label;
      s.addText(label, { x: 1.0, y: by + 0.08, w: 8.2, h: 0.38, fontSize: 9.5, color: LIGHT, fontFace: 'DM Sans' });
      if (sig.feedback) {
        const fb = sig.feedback.length > 100 ? sig.feedback.substring(0, 100) + '…' : sig.feedback;
        s.addText(fb, { x: 1.0, y: by + 0.46, w: 8.2, h: 0.22, fontSize: 8.5, color: '5FB3FF', italic: true, fontFace: 'DM Sans' });
      }
    });
    if (config.diagSignals.length > MAX) {
      s.addText(`+ ${config.diagSignals.length - MAX} autres signaux`, { x: 0.4, y: 5.1, w: 9.2, h: 0.3, fontSize: 9, color: FAINT, align: 'center', fontFace: 'DM Sans' });
    }
  }

  // ── Slide 6 : Analyse décisionnelle ──
  {
    const s = prs.addSlide();
    addBg(s); accentBar(s);
    tag(s, 'ANALYSE DÉCISIONNELLE', 0.4, 0.3);
    const { diagIds, profileIds } = buildActionSets(cd);
    const allF = (cd.steps || []).flatMap(st => st.actions || []).filter(a => !diagIds.has(a.id) && !profileIds.has(a.id));
    const doneA = allF.filter(a => eng.completed.has(a.id));
    const undoneA = allF.filter(a => !eng.completed.has(a.id));
    const pct = config.configScore;
    // Score gauge
    s.addShape(prs.ShapeType.rect, { x: 0.4, y: 0.68, w: 9.2, h: 0.22, fill: { color: '1A2840' } });
    s.addShape(prs.ShapeType.rect, { x: 0.4, y: 0.68, w: Math.max(0.05, 9.2 * pct / 100), h: 0.22, fill: { color: accent } });
    s.addText(`${doneA.length} activée${doneA.length > 1 ? 's' : ''} sur ${allF.length}  ·  Score ${pct}%`, { x: 0.4, y: 0.94, w: 9.2, h: 0.28, fontSize: 10, color: SUB, fontFace: 'DM Sans' });
    // Deux colonnes
    const colW = 4.4;
    s.addText('ACTIVÉS', { x: 0.4, y: 1.32, w: colW, h: 0.25, fontSize: 8, bold: true, color: '4FD5A0', charSpacing: 2, fontFace: 'DM Sans' });
    s.addText('NON ACTIVÉS', { x: 5.2, y: 1.32, w: colW, h: 0.25, fontSize: 8, bold: true, color: FAINT, charSpacing: 2, fontFace: 'DM Sans' });
    doneA.slice(0, 7).forEach((act, i) => {
      const by = 1.65 + i * 0.47;
      if (by > 5.1) return;
      s.addShape(prs.ShapeType.roundRect, { x: 0.4, y: by, w: colW, h: 0.38, fill: { color: '004020' }, line: { color: '4FD5A0', width: 0.3 }, rectRadius: 0.06 });
      const lbl = act.label.length > 60 ? act.label.substring(0, 60) + '…' : act.label;
      s.addText(`✓  ${lbl}`, { x: 0.52, y: by + 0.05, w: colW - 0.22, h: 0.28, fontSize: 8.5, color: LIGHT, fontFace: 'DM Sans' });
    });
    undoneA.slice(0, 7).forEach((act, i) => {
      const by = 1.65 + i * 0.47;
      if (by > 5.1) return;
      s.addShape(prs.ShapeType.roundRect, { x: 5.2, y: by, w: colW, h: 0.38, fill: { color: BG2 }, line: { color: '1A2840', width: 0.3 }, rectRadius: 0.06 });
      const lbl = act.label.length > 60 ? act.label.substring(0, 60) + '…' : act.label;
      s.addText(`○  ${lbl}`, { x: 5.32, y: by + 0.05, w: colW - 0.22, h: 0.28, fontSize: 8.5, color: FAINT, fontFace: 'DM Sans' });
    });
  }

  // ── Slide 7 : Étapes suivantes ──
  {
    const s = prs.addSlide();
    addBg(s); accentBar(s);
    tag(s, 'ÉTAPES SUIVANTES', 0.4, 0.3);
    const missed = (cd.counterfactuals || []).filter(cf => !eng.completed.has(cf.actionId));
    missed.slice(0, 4).forEach((cf, i) => {
      const by = 0.7 + i * 1.18;
      s.addShape(prs.ShapeType.roundRect, { x: 0.4, y: by, w: 9.2, h: 1.06, fill: { color: BG2 }, line: { color: '1A2840', width: 0.5 }, rectRadius: 0.08 });
      s.addText(`+${Math.round(cf.deltaProbability * 100)}%`, { x: 0.52, y: by + 0.12, w: 1.1, h: 0.5, fontSize: 28, bold: true, color: accent, fontFace: 'DM Sans' });
      s.addText(cf.actionTitle, { x: 1.72, y: by + 0.1, w: 7.6, h: 0.32, fontSize: 10, bold: true, color: LIGHT, fontFace: 'DM Sans' });
      if (cf.description) {
        const desc = cf.description.length > 110 ? cf.description.substring(0, 110) + '…' : cf.description;
        s.addText(desc, { x: 1.72, y: by + 0.46, w: 7.6, h: 0.46, fontSize: 8.5, color: SUB, fontFace: 'DM Sans' });
      }
    });
    if (!missed.length) {
      s.addText('Toutes les actions clés ont été activées. Configuration complète.', { x: 0.4, y: 1.5, w: 9.2, h: 1, fontSize: 14, color: accent, align: 'center', fontFace: 'DM Sans' });
    }
  }

  // ── Footer sur toutes les slides ──
  prs.slides.forEach(s => {
    s.addShape(prs.ShapeType.rect, { x: 0, y: 5.575, w: '100%', h: 0.05, fill: { color: accent } });
    s.addText(`PESACORE  ·  ${cd.name}`, { x: 0.4, y: 5.34, w: 7, h: 0.22, fontSize: 7, color: FAINT, fontFace: 'DM Sans' });
    s.addText(new Date().toLocaleDateString('fr-FR'), { x: 7.4, y: 5.34, w: 2.2, h: 0.22, fontSize: 7, color: FAINT, align: 'right', fontFace: 'DM Sans' });
  });

  await prs.writeFile({ fileName: `debrief-${cd.slug}-${new Date().toISOString().split('T')[0]}.pptx` });
}

function ExportButton({ config, sol, cd, eng, th, userPlan, user }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(null); // 'pdf' | 'pptx' | null
  const ref = useRef(null);
  const allowed = canExport(user, userPlan);

  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  if (!allowed) return null;

  const doExport = async (type) => {
    setLoading(type);
    setOpen(false);
    try {
      if (type === 'pdf') await exportDebriefPDF({ config, sol, cd, eng, th });
      else await exportDebriefPPTX({ config, sol, cd, eng });
    } catch (e) {
      console.error('Export error:', e);
      alert(`Erreur lors de l'export ${type.toUpperCase()}. Veuillez réessayer.`);
    }
    setLoading(null);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={!!loading}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '8px 14px', borderRadius: T.rSm,
          background: open ? `${th.orange}14` : th.surfaceAlt,
          border: `1px solid ${open ? `${th.orange}40` : th.border}`,
          color: open ? th.orange : th.textSub,
          fontSize: '12px', fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
          transition: 'all 0.14s',
        }}
        onMouseEnter={e => { if (!loading && !open) e.currentTarget.style.background = th.surfaceHov; }}
        onMouseLeave={e => { if (!loading && !open) e.currentTarget.style.background = th.surfaceAlt; }}
      >
        {loading ? (
          <div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid transparent', borderTopColor: th.orange, animation: 'ca-spin .65s linear infinite' }} />
        ) : <FileDown size={13} />}
        {loading ? `Export ${loading.toUpperCase()}…` : 'Exporter'}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: '180px',
          borderRadius: T.rSm, background: th.surface, border: `1px solid ${th.borderMed}`,
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)', zIndex: 9999,
          animation: 'ca-fadeUp 0.14s ease-out both',
          overflow: 'hidden',
        }}>
          <div style={{ padding: '6px 10px', borderBottom: `1px solid ${th.border}` }}>
            <span style={{ fontSize: '9px', fontWeight: 700, color: th.textFaint, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Exporter le débrief</span>
          </div>
          {[
            { type: 'pdf',  icon: '📄', label: 'Rapport PDF', sub: 'Document complet' },
            { type: 'pptx', icon: '📊', label: 'Présentation', sub: 'Format PowerPoint' },
          ].map(({ type, icon, label, sub }) => (
            <button key={type} onClick={() => doExport(type)} style={{
              width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', background: 'none',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', transition: 'background 0.1s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = th.surfaceHov}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <span style={{ fontSize: '16px', flexShrink: 0 }}>{icon}</span>
              <div>
                <p style={{ fontSize: '12px', fontWeight: 600, color: th.text, margin: 0 }}>{label}</p>
                <p style={{ fontSize: '10px', color: th.textFaint, margin: 0 }}>{sub}</p>
              </div>
            </button>
          ))}
          <div style={{ padding: '7px 12px', borderTop: `1px solid ${th.border}` }}>
            <span style={{ fontSize: '9px', color: th.textFaint }}>✦ Accès {userPlan.toUpperCase()}</span>
          </div>
        </div>
      )}
    </div>
  );
}

const Inp = ({ val, onChange, multiline = false, placeholder = '', mono = false, th, rows = 3 }) => {
  const base = {
    width: '100%', padding: '7px 10px', borderRadius: T.rXs,
    background: th.surfaceHov, border: `1px solid ${th.border}`,
    color: th.text, fontSize: mono ? '11px' : '12px', outline: 'none',
    boxSizing: 'border-box', fontFamily: mono ? "'DM Mono', monospace" : 'inherit',
    resize: multiline ? 'vertical' : 'none', minHeight: multiline ? `${rows * 20}px` : undefined,
    transition: 'border-color 0.14s',
  };
  if (multiline) return <textarea style={base} value={val || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />;
  return <input style={base} value={val || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />;
};
const Lbl = ({ children, th }) => <label style={{ fontSize: '11px', fontWeight: 500, color: th.textSub, display: 'block', marginBottom: '4px' }}>{children}</label>;
const Fd = ({ children }) => <div style={{ marginBottom: '14px' }}>{children}</div>;

const SubCard = ({ children, th, accent }) => (
  <div style={{ padding: '12px 14px', borderRadius: T.rSm, background: th.surface, border: `1px solid ${accent ? `${accent}22` : th.border}`, marginBottom: '10px', borderLeft: accent ? `3px solid ${accent}40` : undefined }}>
    {children}
  </div>
);

const CollapseSection = ({ title, icon, count, accent, th, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: '8px', borderRadius: T.rSm, border: `1px solid ${open ? `${accent}25` : th.border}`, overflow: 'hidden', transition: 'border-color 0.15s' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none',
        background: open ? `${accent}08` : th.surface, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.14s',
      }}>
        <span style={{ fontSize: '12px' }}>{icon}</span>
        <span style={{ flex: 1, fontSize: '12px', fontWeight: 600, color: open ? accent : th.text }}>{title}</span>
        {count !== undefined && <span style={{ fontSize: '10px', color: th.textFaint, background: th.surfaceAlt, padding: '1px 7px', borderRadius: T.rPill }}>{count}</span>}
        <span style={{ fontSize: '10px', color: th.textFaint, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
      </button>
      {open && <div style={{ padding: '12px', background: th.surfaceAlt }}>{children}</div>}
    </div>
  );
};

function EditorPanel({ caseData, onUpdate, onClose, userPlan, th }) {
  const [tab, setTab] = useState('info');
  const [data, setData] = useState(caseData);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setData(caseData); setDirty(false); }, [caseData]);

  const set = (path, val) => {
    setData(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let c = n;
      for (let i = 0; i < keys.length - 1; i++) {
        // handle array indices
        const k = isNaN(keys[i]) ? keys[i] : parseInt(keys[i]);
        c = c[k];
      }
      const lastKey = isNaN(keys[keys.length - 1]) ? keys[keys.length - 1] : parseInt(keys[keys.length - 1]);
      c[lastKey] = val;
      return n;
    });
    setDirty(true);
  };

  const TABS = [
    { id: 'info',        label: 'Info',          icon: '◎' },
    { id: 'metrics',     label: 'Métriques',     icon: '⬡' },
    { id: 'steps',       label: 'Étapes',        icon: '◈' },
    { id: 'actions',     label: 'Actions',       icon: '▣' },
    { id: 'families',    label: 'Familles',      icon: '◉' },
    { id: 'solutions',   label: 'Solutions',     icon: '◇' },
    //{ id: 'json',        label: 'JSON',          icon: '{ }' },
  ];

  const accent = th.orange;

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      background: th.surface,              
      border: `1px solid ${th.border}`,    
      borderRadius: T.r,                   
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ 
        padding: '10px 14px', 
        borderBottom: `1px solid ${th.border}`, 
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
        flexShrink: 0, 
        background: th.surfaceAlt,         // ← léger contraste avec le body, comme CollapseSection
      }}>
         <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: th.orange, animation: dirty ? 'ca-pulse 1.5s infinite' : 'none' }} />
          <span style={{ fontSize: '13px', fontWeight: 600, color: th.text }}>Éditeur avancé</span>
          <span style={{ fontSize: '10px', padding: '1px 8px', borderRadius: T.rPill, background: th.amberDim, color: th.amber, border: `1px solid ${th.amber}28` }}>{userPlan}</span>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {dirty && (
            <button onClick={() => { onUpdate(data); setDirty(false); }} style={{ padding: '5px 12px', borderRadius: T.rXs, background: th.orange, color: '#fff', fontSize: '12px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'opacity 0.14s' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
              Appliquer ✓
            </button>
          )}
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: T.rXs, background: th.surfaceAlt, border: `1px solid ${th.border}`, color: th.textSub, fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        borderBottom: `1px solid ${th.border}`, 
        flexShrink: 0, 
        background: th.surface, 
        overflowX: 'auto',
        scrollbarWidth: 'thin',        
        scrollbarColor: `${th.border} transparent`,
        WebkitOverflowScrolling: 'touch',
      }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 11px', border: 'none', background: 'none',
            borderBottom: `2px solid ${tab === t.id ? th.orange : 'transparent'}`,
            color: tab === t.id ? th.orange : th.textSub,
            fontSize: '11px', fontWeight: tab === t.id ? 700 : 400,
            cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.12s',
            display: 'flex', alignItems: 'center', gap: '4px',
          }}>
            <span style={{ fontSize: '11px' }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px', scrollbarWidth: 'thin' }}>

        {/* ── INFO ── */}
        {tab === 'info' && (
          <div>
            <Fd><Lbl th={th}>Titre complet</Lbl><Inp val={data.title} onChange={v => set('title', v)} th={th} /></Fd>
            <Fd><Lbl th={th}>Nom court (header)</Lbl><Inp val={data.name} onChange={v => set('name', v)} th={th} /></Fd>
            <Fd><Lbl th={th}>Slug (identifiant unique)</Lbl><Inp val={data.slug} onChange={v => set('slug', v)} th={th} mono /></Fd>
            <Fd><Lbl th={th}>Description</Lbl><Inp val={data.description} onChange={v => set('description', v)} th={th} multiline rows={4} /></Fd>
            <Fd><Lbl th={th}>Objectif pédagogique</Lbl><Inp val={data.goal} onChange={v => set('goal', v)} th={th} multiline rows={3} /></Fd>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <Fd><Lbl th={th}>Difficulté</Lbl>
                <select value={data.difficulty || ''} onChange={e => set('difficulty', e.target.value)} style={{ width: '100%', padding: '7px 10px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: th.text, fontSize: '12px', outline: 'none', cursor: 'pointer' }}>
                  <option value="facile">Facile</option>
                  <option value="intermédiaire">Intermédiaire</option>
                  <option value="avancé">Avancé</option>
                </select>
              </Fd>
              <Fd><Lbl th={th}>Durée estimée (min)</Lbl><input type="number" value={data.estimatedTime || ''} onChange={e => set('estimatedTime', parseInt(e.target.value, 10))} style={{ width: '100%', padding: '7px 10px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: th.text, fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} /></Fd>
            </div>
            <Fd><Lbl th={th}>Tags (séparés par des virgules)</Lbl><Inp val={(data.tags || []).join(', ')} onChange={v => set('tags', v.split(',').map(x => x.trim()).filter(Boolean))} th={th} /></Fd>
            <Fd><Lbl th={th}>Version</Lbl><input type="number" value={data.version || 1} onChange={e => set('version', parseInt(e.target.value, 10))} style={{ width: '80px', padding: '7px 10px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: th.text, fontSize: '12px', outline: 'none' }} /></Fd>
          </div>
        )}

        {/* ── MÉTRIQUES ── */}
        {tab === 'metrics' && (
          <div>
            <p style={{ fontSize: '11px', color: th.textFaint, marginBottom: '14px', lineHeight: '1.6' }}>Ces métriques définissent les indicateurs qui évoluent tout au long du parcours. Chaque action peut modifier ces valeurs via ses <code style={{ fontFamily: 'monospace', fontSize: '10px', background: th.surfaceAlt, padding: '1px 4px', borderRadius: '3px' }}>effect</code>.</p>
            {Object.entries(data.metrics || {}).map(([k, v]) => (
              <CollapseSection key={k} title={v.label || k} icon="⬡" accent={accent} th={th} defaultOpen>
                <Fd><Lbl th={th}>Identifiant (clé)</Lbl><Inp val={k} onChange={() => {}} th={th} mono placeholder="non modifiable ici" /></Fd>
                <Fd><Lbl th={th}>Label affiché</Lbl><Inp val={v.label} onChange={nv => set(`metrics.${k}.label`, nv)} th={th} /></Fd>
                <Fd><Lbl th={th}>Description (tooltip)</Lbl><Inp val={v.description} onChange={nv => set(`metrics.${k}.description`, nv)} th={th} multiline rows={2} /></Fd>
                <Fd>
                  <Lbl th={th}>Valeur initiale — {v.start ?? 50}</Lbl>
                  <input type="range" min={0} max={100} value={v.start || 0}
                    onChange={e => { const n = parseInt(e.target.value, 10); set(`metrics.${k}.start`, n); set(`initialState.parameters.${k}`, n); }}
                    style={{ width: '100%', accentColor: th.orange }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                    <span style={{ fontSize: '10px', color: th.textFaint }}>0</span>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: th.amber }}>{v.start ?? 50}</span>
                    <span style={{ fontSize: '10px', color: th.textFaint }}>100</span>
                  </div>
                </Fd>
              </CollapseSection>
            ))}
          </div>
        )}

        {/* ── ÉTAPES ── */}
        {tab === 'steps' && (
          <div>
            <p style={{ fontSize: '11px', color: th.textFaint, marginBottom: '14px', lineHeight: '1.6' }}>Structure narrative du parcours. Chaque étape contient des phases (narration / action / conséquences) et un pool d'actions.</p>
            {(data.steps || []).map((step, si) => (
              <CollapseSection key={si} title={`Étape ${si + 1} — ${step.title || '(sans titre)'}`} icon="◈" count={`${(step.actions || []).length} actions`} accent={accent} th={th}>
                <Fd><Lbl th={th}>Titre</Lbl><Inp val={step.title} onChange={v => set(`steps.${si}.title`, v)} th={th} /></Fd>
                <Fd><Lbl th={th}>Problématisation</Lbl><Inp val={step.problematisation} onChange={v => set(`steps.${si}.problematisation`, v)} th={th} multiline rows={3} /></Fd>
                <Fd><Lbl th={th}>Question épistémologique</Lbl><Inp val={step.epistemologicalQuestion} onChange={v => set(`steps.${si}.epistemologicalQuestion`, v)} th={th} multiline rows={2} /></Fd>
                <Fd><Lbl th={th}>Feedback de clôture</Lbl><Inp val={step.feedback} onChange={v => set(`steps.${si}.feedback`, v)} th={th} multiline rows={2} /></Fd>
                <Fd><Lbl th={th}>Override narratif (badge)</Lbl><Inp val={step.narrativeOverride} onChange={v => set(`steps.${si}.narrativeOverride`, v)} th={th} placeholder="ex: CARTOGRAPHIE DES TENSIONS" /></Fd>

                {/* Phases */}
                <div style={{ marginTop: '10px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: th.textFaint, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Phases ({(step.phases || []).length})</p>
                  {(step.phases || []).map((ph, pi) => (
                    <SubCard key={pi} th={th} accent={accent}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, color: th.textFaint, background: th.surfaceAlt, padding: '2px 8px', borderRadius: T.rPill, fontFamily: 'monospace' }}>Phase {pi + 1}</span>
                        <select value={ph.type || 'action'} onChange={e => set(`steps.${si}.phases.${pi}.type`, e.target.value)} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: accent, fontWeight: 600, cursor: 'pointer', outline: 'none' }}>
                          {['observation','action','consequences','narrative'].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <select value={ph.ambiance || 'tension'} onChange={e => set(`steps.${si}.phases.${pi}.ambiance`, e.target.value)} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: th.textSub, cursor: 'pointer', outline: 'none' }}>
                          {['tension','hope','resolution','default'].map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                      <Fd><Lbl th={th}>Titre de la phase</Lbl><Inp val={ph.title} onChange={v => set(`steps.${si}.phases.${pi}.title`, v)} th={th} /></Fd>
                      <Fd><Lbl th={th}>Narration</Lbl><Inp val={ph.narrative} onChange={v => set(`steps.${si}.phases.${pi}.narrative`, v)} th={th} multiline rows={3} /></Fd>
                      <Fd><Lbl th={th}>Indice (hint)</Lbl><Inp val={ph.hint} onChange={v => set(`steps.${si}.phases.${pi}.hint`, v)} th={th} multiline rows={2} placeholder="Conseil affiché en jaune…" /></Fd>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <Fd>
                          <Lbl th={th}>Durée auto (s)</Lbl>
                          <input type="number" min={0} value={ph.duration || 0} onChange={e => set(`steps.${si}.phases.${pi}.duration`, parseInt(e.target.value, 10) || 0)} style={{ width: '100%', padding: '7px 10px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: th.text, fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} />
                        </Fd>
                        <Fd>
                          <Lbl th={th}>Révéler delta ?</Lbl>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px' }}>
                            <input type="checkbox" checked={!!ph.revealDelta} onChange={e => set(`steps.${si}.phases.${pi}.revealDelta`, e.target.checked)} style={{ accentColor: th.orange, width: '14px', height: '14px', cursor: 'pointer' }} />
                            <span style={{ fontSize: '11px', color: th.textSub }}>Afficher les variations</span>
                          </div>
                        </Fd>
                      </div>
                      {ph.type === 'action' && (
                        <Fd>
                          <Lbl th={th}>IDs des actions (séparés par des virgules)</Lbl>
                          <Inp val={(ph.actionIds || []).join(', ')} onChange={v => set(`steps.${si}.phases.${pi}.actionIds`, v.split(',').map(x => x.trim()).filter(Boolean))} th={th} mono multiline rows={2} />
                        </Fd>
                      )}
                    </SubCard>
                  ))}
                </div>
              </CollapseSection>
            ))}
          </div>
        )}

        {/* ── ACTIONS (édition profonde) ── */}
        {tab === 'actions' && (
          <div>
            <p style={{ fontSize: '11px', color: th.textFaint, marginBottom: '14px', lineHeight: '1.6' }}>Édition approfondie de chaque action — effets sur les métriques, feedbacks, signaux de compétence, déclencheurs.</p>
            {(data.steps || []).map((step, si) => (
              <div key={si} style={{ marginBottom: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '6px 10px', background: `${accent}10`, borderRadius: T.rSm, border: `1px solid ${accent}20` }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: accent }}>Étape {si + 1}</span>
                  <span style={{ fontSize: '11px', color: th.text }}>{step.title}</span>
                  <span style={{ fontSize: '10px', color: th.textFaint, marginLeft: 'auto' }}>{(step.actions || []).length} actions</span>
                </div>
                {(step.actions || []).map((act, ai) => (
                  <CollapseSection key={ai} title={act.label?.substring(0, 55) + (act.label?.length > 55 ? '…' : '') || `Action ${ai + 1}`} icon={act.id?.startsWith('diag_') ? '△' : act.id?.startsWith('position_') || act.id?.startsWith('posture_') ? '◉' : '▣'} accent={accent} th={th}>

                    {/* Identité */}
                    <Fd><Lbl th={th}>Identifiant (id)</Lbl><Inp val={act.id} onChange={v => set(`steps.${si}.actions.${ai}.id`, v)} th={th} mono /></Fd>
                    <Fd><Lbl th={th}>Label affiché</Lbl><Inp val={act.label} onChange={v => set(`steps.${si}.actions.${ai}.label`, v)} th={th} multiline rows={2} /></Fd>
                    <Fd><Lbl th={th}>Feedback court (après sélection)</Lbl><Inp val={act.feedback} onChange={v => set(`steps.${si}.actions.${ai}.feedback`, v)} th={th} multiline rows={2} /></Fd>

                    {/* Effets sur les métriques */}
                    <div style={{ marginBottom: '14px' }}>
                      <p style={{ fontSize: '11px', fontWeight: 700, color: th.textFaint, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Effets sur les métriques</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px,1fr))', gap: '6px' }}>
                        {Object.keys(data.metrics || {}).map(mk => {
                          const currentVal = (act.effect || {})[mk] ?? 0;
                          return (
                            <div key={mk} style={{ padding: '8px 10px', borderRadius: T.rXs, background: th.surface, border: `1px solid ${currentVal !== 0 ? (currentVal > 0 ? `${th.amber}30` : `${th.danger}30`) : th.border}` }}>
                              <p style={{ fontSize: '9px', color: th.textFaint, margin: '0 0 5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{data.metrics[mk]?.label || mk}</p>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input type="number" min={-50} max={50} value={currentVal}
                                  onChange={e => set(`steps.${si}.actions.${ai}.effect.${mk}`, parseInt(e.target.value, 10) || 0)}
                                  style={{ width: '55px', padding: '4px 6px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: currentVal > 0 ? th.amber : currentVal < 0 ? th.danger : th.text, fontSize: '12px', fontWeight: 700, outline: 'none', fontFamily: 'monospace', textAlign: 'center' }} />
                                <span style={{ fontSize: '9px', color: th.textFaint }}>/ ±50</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Deep Feedback */}
                    <SubCard th={th} accent={th.sky}>
                      <p style={{ fontSize: '11px', fontWeight: 700, color: th.sky, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        ◇ Deep Feedback
                        <span style={{ fontSize: '9px', fontWeight: 400, color: th.textFaint }}>(affiché dans la colonne de droite)</span>
                      </p>
                      <Fd><Lbl th={th}>Pourquoi c'est important</Lbl><Inp val={act.deepFeedback?.whyItMatters} onChange={v => set(`steps.${si}.actions.${ai}.deepFeedback.whyItMatters`, v)} th={th} multiline rows={3} /></Fd>
                      <Fd><Lbl th={th}>Ce que ça produit (whatHappened)</Lbl><Inp val={act.deepFeedback?.whatHappened} onChange={v => set(`steps.${si}.actions.${ai}.deepFeedback.whatHappened`, v)} th={th} multiline rows={3} /></Fd>
                      <Fd><Lbl th={th}>Ce que ça coûte (whatItCosts)</Lbl><Inp val={act.deepFeedback?.whatItCosts} onChange={v => set(`steps.${si}.actions.${ai}.deepFeedback.whatItCosts`, v)} th={th} multiline rows={2} /></Fd>
                      <Fd><Lbl th={th}>Condition d'amplification (boostCondition)</Lbl><Inp val={act.deepFeedback?.boostCondition} onChange={v => set(`steps.${si}.actions.${ai}.deepFeedback.boostCondition`, v)} th={th} multiline rows={2} /></Fd>
                    </SubCard>

                    {/* Signal de compétence */}
                    <SubCard th={th} accent={th.amber}>
                      <p style={{ fontSize: '11px', fontWeight: 700, color: th.amber, marginBottom: '10px' }}>▣ Signal de compétence</p>
                      <Fd><Lbl th={th}>Nom de la compétence</Lbl><Inp val={act.competencySignal?.name} onChange={v => set(`steps.${si}.actions.${ai}.competencySignal.name`, v)} th={th} /></Fd>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <Fd><Lbl th={th}>Niveau</Lbl>
                          <select value={act.competencySignal?.level || 'débutant'} onChange={e => set(`steps.${si}.actions.${ai}.competencySignal.level`, e.target.value)} style={{ width: '100%', padding: '7px 10px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: th.text, fontSize: '12px', outline: 'none', cursor: 'pointer' }}>
                            {['débutant','intermédiaire','avancé','expert'].map(l => <option key={l} value={l}>{l}</option>)}
                          </select>
                        </Fd>
                        <Fd><Lbl th={th}>Descripteur</Lbl><Inp val={act.competencySignal?.descriptor} onChange={v => set(`steps.${si}.actions.${ai}.competencySignal.descriptor`, v)} th={th} /></Fd>
                      </div>
                    </SubCard>

                    {/* Prérequis */}
                    <Fd>
                      <Lbl th={th}>Prérequis (IDs séparés par des virgules)</Lbl>
                      <Inp val={(act.prerequisites || []).join(', ')} onChange={v => set(`steps.${si}.actions.${ai}.prerequisites`, v.split(',').map(x => x.trim()).filter(Boolean))} th={th} mono />
                    </Fd>

                    {/* Familles à activer */}
                    <Fd>
                      <Lbl th={th}>Familles à activer (toggleFamilies)</Lbl>
                      <Inp val={(act.toggleFamilies || []).join(', ')} onChange={v => set(`steps.${si}.actions.${ai}.toggleFamilies`, v.split(',').map(x => x.trim()).filter(Boolean))} th={th} mono />
                    </Fd>
                    <Fd>
                      <Lbl th={th}>Familles à mettre en évidence (highlightFamilies)</Lbl>
                      <Inp val={(act.highlightFamilies || []).join(', ')} onChange={v => set(`steps.${si}.actions.${ai}.highlightFamilies`, v.split(',').map(x => x.trim()).filter(Boolean))} th={th} mono />
                    </Fd>
                  </CollapseSection>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* ── FAMILLES & INTERACTIONS ── */}
        {tab === 'families' && (
          <div>
            <p style={{ fontSize: '11px', color: th.textFaint, marginBottom: '14px', lineHeight: '1.6' }}>Les familles sont les acteurs ou systèmes du scénario. Les interactions décrivent les relations entre eux.</p>

            {/* Familles */}
            <p style={{ fontSize: '11px', fontWeight: 700, color: th.textFaint, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Familles ({(data.initialState?.families || []).length})</p>
            {(data.initialState?.families || []).map((f, fi) => (
              <CollapseSection key={fi} title={f.name} icon="◉" accent={accent} th={th}>
                <Fd><Lbl th={th}>Nom</Lbl><Inp val={f.name} onChange={v => set(`initialState.families.${fi}.name`, v)} th={th} /></Fd>
                <Fd><Lbl th={th}>Description</Lbl><Inp val={f.description} onChange={v => set(`initialState.families.${fi}.description`, v)} th={th} multiline rows={2} /></Fd>
                <Fd><Lbl th={th}>Rôle narratif</Lbl><Inp val={f.narrativeRole} onChange={v => set(`initialState.families.${fi}.narrativeRole`, v)} th={th} multiline rows={2} /></Fd>
                <Fd><Lbl th={th}>Couleur (hex)</Lbl>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input type="color" value={f.color || '#5fb3ff'} onChange={e => set(`initialState.families.${fi}.color`, e.target.value)} style={{ width: '36px', height: '30px', borderRadius: T.rXs, border: `1px solid ${th.border}`, background: 'none', cursor: 'pointer' }} />
                    <Inp val={f.color} onChange={v => set(`initialState.families.${fi}.color`, v)} th={th} mono />
                  </div>
                </Fd>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <Fd>
                    <Lbl th={th}>Active au départ ?</Lbl>
                    <input type="checkbox" checked={!!f.isActive} onChange={e => set(`initialState.families.${fi}.isActive`, e.target.checked)} style={{ accentColor: th.orange, width: '14px', height: '14px', cursor: 'pointer', marginTop: '8px' }} />
                  </Fd>
                  <Fd>
                    <Lbl th={th}>Toxique au départ ?</Lbl>
                    <input type="checkbox" checked={!!f.isToxic} onChange={e => set(`initialState.families.${fi}.isToxic`, e.target.checked)} style={{ accentColor: th.danger, width: '14px', height: '14px', cursor: 'pointer', marginTop: '8px' }} />
                  </Fd>
                </div>
              </CollapseSection>
            ))}

            {/* Interactions */}
            <p style={{ fontSize: '11px', fontWeight: 700, color: th.textFaint, margin: '16px 0 8px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Interactions initiales ({(data.initialState?.interactions || []).length})</p>
            {(data.initialState?.interactions || []).map((inter, ii) => (
              <CollapseSection key={ii} title={`${inter.source} → ${inter.target}`} icon="→" accent={th.sky} th={th}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <Fd><Lbl th={th}>Source</Lbl><Inp val={inter.source} onChange={v => set(`initialState.interactions.${ii}.source`, v)} th={th} /></Fd>
                  <Fd><Lbl th={th}>Cible</Lbl><Inp val={inter.target} onChange={v => set(`initialState.interactions.${ii}.target`, v)} th={th} /></Fd>
                </div>
                <Fd><Lbl th={th}>Type de relation</Lbl>
                  <select value={inter.type || 'structuration'} onChange={e => set(`initialState.interactions.${ii}.type`, e.target.value)} style={{ width: '100%', padding: '7px 10px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: th.text, fontSize: '12px', outline: 'none', cursor: 'pointer' }}>
                    {['structuration','alimentation','érosion','réduction','renforcement','clarification'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Fd>
                <Fd>
                  <Lbl th={th}>Force ({inter.strength?.toFixed(2) ?? 0})</Lbl>
                  <input type="range" min={-1} max={1} step={0.05} value={inter.strength ?? 0} onChange={e => set(`initialState.interactions.${ii}.strength`, parseFloat(e.target.value))} style={{ width: '100%', accentColor: th.sky }} />
                </Fd>
              </CollapseSection>
            ))}
          </div>
        )}

        {/* ── SOLUTIONS ── */}
        {tab === 'solutions' && (
          <div>
            <p style={{ fontSize: '11px', color: th.textFaint, marginBottom: '14px', lineHeight: '1.6' }}>Les solutions définissent les interprétations de fin de parcours selon les actions activées par l'utilisateur.</p>
            {(data.solutions || []).map((sol, si) => (
              <CollapseSection key={si} title={sol.name || `Solution ${si + 1}`} icon="◇" accent={accent} th={th}>
                <Fd><Lbl th={th}>Nom</Lbl><Inp val={sol.name} onChange={v => set(`solutions.${si}.name`, v)} th={th} /></Fd>
                <Fd><Lbl th={th}>Description</Lbl><Inp val={sol.description} onChange={v => set(`solutions.${si}.description`, v)} th={th} multiline rows={2} /></Fd>
                <Fd><Lbl th={th}>Difficulté</Lbl>
                  <select value={sol.difficulty || 'intermédiaire'} onChange={e => set(`solutions.${si}.difficulty`, e.target.value)} style={{ width: '100%', padding: '7px 10px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: th.text, fontSize: '12px', outline: 'none', cursor: 'pointer' }}>
                    {['facile','intermédiaire','avancé'].map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Fd>
                <Fd><Lbl th={th}>Actions requises (IDs, virgules)</Lbl><Inp val={(sol.requiredActions || []).join(', ')} onChange={v => set(`solutions.${si}.requiredActions`, v.split(',').map(x => x.trim()).filter(Boolean))} th={th} mono multiline rows={2} /></Fd>

                <SubCard th={th} accent={th.sky}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: th.sky, marginBottom: '10px' }}>Interprétation</p>
                  <Fd><Lbl th={th}>Résumé court</Lbl><Inp val={sol.interpretation?.shortSummary} onChange={v => set(`solutions.${si}.interpretation.shortSummary`, v)} th={th} multiline rows={2} /></Fd>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <Fd>
                      <Lbl th={th}>Potentiel théorique (%)</Lbl>
                      <input type="number" min={0} max={100} value={sol.interpretation?.theoreticalPromise ?? 80} onChange={e => set(`solutions.${si}.interpretation.theoreticalPromise`, parseInt(e.target.value, 10))} style={{ width: '100%', padding: '7px 10px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: th.text, fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} />
                    </Fd>
                    <Fd>
                      <Lbl th={th}>Probabilité réelle (%)</Lbl>
                      <input type="number" min={0} max={100} value={sol.interpretation?.realWorldProbability ?? 60} onChange={e => set(`solutions.${si}.interpretation.realWorldProbability`, parseInt(e.target.value, 10))} style={{ width: '100%', padding: '7px 10px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: th.text, fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} />
                    </Fd>
                  </div>
                  <Fd><Lbl th={th}>Tonalité</Lbl>
                    <select value={sol.interpretation?.tone || 'analytique'} onChange={e => set(`solutions.${si}.interpretation.tone`, e.target.value)} style={{ width: '100%', padding: '7px 10px', borderRadius: T.rXs, background: th.surfaceHov, border: `1px solid ${th.border}`, color: th.text, fontSize: '12px', outline: 'none', cursor: 'pointer' }}>
                      {Object.keys(TONE_COLOR).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Fd>
                  <Fd><Lbl th={th}>Insight clé</Lbl><Inp val={sol.interpretation?.keyInsight} onChange={v => set(`solutions.${si}.interpretation.keyInsight`, v)} th={th} multiline rows={2} /></Fd>
                  <Fd><Lbl th={th}>Message pédagogique</Lbl><Inp val={sol.interpretation?.pedagogicalMessage} onChange={v => set(`solutions.${si}.interpretation.pedagogicalMessage`, v)} th={th} multiline rows={3} /></Fd>
                </SubCard>
              </CollapseSection>
            ))}
          </div>
        )}

        {/* ── JSON ── */}
        {tab === 'json' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <p style={{ fontSize: '11px', color: th.amber, fontWeight: 500 }}>Export brut — lecture seule</p>
              <button onClick={() => { navigator.clipboard?.writeText(JSON.stringify(data, null, 2)); }} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: T.rXs, background: th.amberDim, border: `1px solid ${th.amber}28`, color: th.amber, cursor: 'pointer', fontFamily: 'inherit' }}>
                Copier
              </button>
            </div>
            <pre style={{ fontSize: '10px', color: th.textSub, lineHeight: '1.6', overflowX: 'auto', background: th.surface, padding: '12px', borderRadius: T.rSm, border: `1px solid ${th.border}`, maxHeight: '520px', overflowY: 'auto' }}>
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Status bar */}
      <div style={{ padding: '8px 14px', borderTop: `1px solid ${th.border}`, flexShrink: 0, background: th.surface, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '11px', color: dirty ? th.amber : th.textFaint }}>
          {dirty ? '⬤ Modifications non appliquées' : '✓ Synchronisé'}
        </span>
        {dirty && (
          <button onClick={() => { setData(caseData); setDirty(false); }} style={{ fontSize: '11px', color: th.textFaint, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}>
            Annuler
          </button>
        )}
      </div>
    </div>
  );
}

function LandingScreen({ templates, onLaunch, isDark, onTheme, th, accent }) {
  const { user } = useAuth();
  const { runCount, hasReachedLimit, incrementRunVisitor } = useVisitorRuns();
  const { runsUsedThisMonth, maxRuns, hasReachedLimitForRun, incrementRun, isUnlimited } = useRunUsage(user);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [hoveredRun, setHoveredRun] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [profile, setProfile] = useState(null);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const audioRef = useRef(null);
  const navigate = useNavigate();
  const ctaRef = useRef(null);
  const categoryRunsRef = useRef(null);

  const MAX_FREE_RUNS = 2;

  const toggleMusic = () => {
    if (!audioRef.current) return;
    
    if (isMusicPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => {
        console.log("Lecture audio bloquée par le navigateur :", err);
      });
    }
    setIsMusicPlaying(!isMusicPlaying);
  };

  const getQuotaDisplay = () => {
    if (!user) {
      return `${MAX_FREE_RUNS - runCount} essais gratuits`;
    }

    const trialEndsAt = user.subscription?.trialEndsAt;
    const isTrial = trialEndsAt ? new Date(trialEndsAt) > new Date() : false;

    if (isTrial) {
      return `${runsUsedThisMonth} / ${maxRuns} runs (essai en cours)`;
    }

    if (isUnlimited) {
      return '✓ Illimité';
    }

    // Sécurité : si maxRuns est Infinity, on affiche "Illimité" même si on est passé ici
    if (maxRuns === Infinity || maxRuns > 1000) {
      return '✓ Illimité';
    }

    return `${runsUsedThisMonth} / ${maxRuns} runs ce mois`;
  };

  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const shuffledTemplates = useMemo(() => shuffleArray(templates), [templates]);

  const categoriesMap = useMemo(() => {
  const map = new Map();
  
  shuffledTemplates.forEach(template => {
    if (template.categories && Array.isArray(template.categories)) {
      template.categories.forEach(category => {
        if (!map.has(category)) {
          map.set(category, {
            name: category,
            templates: [],
            ...getCategoryAssets(category)
          });
        }
        // Vérifier si le template n'est pas déjà dans la liste
        const existingTemplates = map.get(category).templates;
        if (!existingTemplates.some(t => t.slug === template.slug)) {
          existingTemplates.push(template);
        }
      });
    }
  });
  
  return map;
  }, [shuffledTemplates]);

  const categories = useMemo(() => Array.from(categoriesMap.values()), [categoriesMap]);

  const allRuns = useMemo(() => {
    const seen = new Set();
    const result = [];
    shuffledTemplates.forEach(template => {
      if (!seen.has(template.slug)) {
        seen.add(template.slug);
        // trouver la première catégorie de ce template
        const cat = categories.find(c =>
          c.templates.some(t => t.slug === template.slug)
        );
        result.push({
          category: cat?.name || '',
          categoryColor: cat?.color || '#888',
          categoryIcon: cat?.icon || '',
          run: template,
        });
      }
    });
    return result;
  }, [shuffledTemplates, categories]);
  
  const currentRuns = useMemo(() => {
    if (!selectedCategory) return [];
    const runs = categoriesMap.get(selectedCategory)?.templates || [];
    // Dédoublonner par slug
    const seen = new Set();
    return runs.filter(run => {
      if (seen.has(run.slug)) return false;
      seen.add(run.slug);
      return true;
    });
  }, [selectedCategory, categoriesMap]);

  const scrollToCategoryRuns = () => {
    setTimeout(() => {
      if (categoryRunsRef.current) {
        const yOffset = -70;
        const y = categoryRunsRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleCategoryClick = (categoryName) => {
    if (selectedCategory === categoryName) {
      setSelectedCategory(null);
      setSelectedRun(null);
    } else {
      setSelectedCategory(categoryName);
      setSelectedRun(null);
      setProfile(null);
      scrollToCategoryRuns();
    }
  };

  const handleRunClick = (run) => {
    if (isTemplateLocked(run, user)) return;
    setSelectedRun(selectedRun?.slug === run.slug ? null : run);
    setProfile(null);
    if (ctaRef.current) {
      setTimeout(() => {
        ctaRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);
    }
  };

  const handleLaunch = async () => {
    if (!selectedRun || launching) return;

    // Vérification de limite (uniquement visuelle)
    if (user && !isUnlimited && hasReachedLimitForRun) {
      const isTrial = user.subscription?.trialEndsAt 
        ? new Date(user.subscription.trialEndsAt) > new Date()
        : false;

      alert(isTrial 
        ? `Vous avez atteint la limite de ${maxRuns} runs pendant votre période d'essai.`
        : `Vous avez atteint votre limite de ${maxRuns} runs ce mois-ci.`
      );
      return;
    }

    if (!user && hasReachedLimit) {
      alert("Vous avez atteint la limite d'essais gratuits. Créez un compte pour continuer.");
      return;
    }

    setLaunching(true);

    // Incrémentation locale pour l'UX immédiate
    if (user && !isUnlimited) {
      incrementRun();
    } else if (!user) {
      incrementRunVisitor();
    }

    await new Promise(r => setTimeout(r, 320));

    // Appel au parent (le vrai lancement)
    onLaunch(selectedRun, profile);

    setLaunching(false);
  };

  const formatProtocolId = (slug) => {
    const map = {
      'civilisation-philosophie': 'CVT-PHL',
      'education-famille': 'EDU-FAM',
      'scolarite-accompagnement': 'SCL-ACC',
      'art-creativite': 'ART-CRT',
      'engagement-citoyen': 'ENG-CIT',
      'acquisition-personnelle': 'ACQ-PER',
      'equilibre-solidaire': 'EQU-SOL',
      'mediation-societe': 'MED-SOC',
    };
    return map[slug] || slug.slice(0, 4).toUpperCase();
  };

  const meta = selectedRun ? getTemplateMeta(selectedRun.slug) : null;
  const profiles = selectedRun?.audience?.profiles || [];  

  const PerspectiveGrid = () => (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        backgroundImage: `
          linear-gradient(to right, rgba(34,211,238,0.05) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(245,158,11,0.05) 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px',
        maskImage: 'radial-gradient(ellipse at 50% 0%, black 40%, transparent 80%)',
        WebkitMaskImage: 'radial-gradient(ellipse at 50% 0%, black 40%, transparent 80%)',
      }}
    />
  );

  return (
    <div style={{      
      background: isDark ? "#0A0A0F" : "#F8F9FC",
      fontFamily: "'Inter', 'SF Mono', 'Fira Code', monospace",
      color: th.text,
      position: "relative",
      minHeight: "100vh",
    }}>
    
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        @keyframes scan {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-15px); }
          to { opacity: 1; transform: translateX(0); }
        }

        .category-chip {
          transition: all 0.2s ease;
          cursor: pointer;
          white-space: nowrap;
        }

        .run-card {
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
        }

        .run-card:hover {
          transform: translateY(-2px);
        }

        .grid-runs {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        @media (max-width: 768px) {
          .grid-runs {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }
      `}</style>

      {/* Grille de fond */}
      <PerspectiveGrid />
      <div style={{ 
        position: 'fixed', 
        inset: 0, 
        pointerEvents: 'none', 
        zIndex: 0, 
        background: 'radial-gradient(ellipse at 20% 0%, rgba(34,211,238,0.04) 0%, transparent 50%), radial-gradient(ellipse at 80% 100%, rgba(245,158,11,0.04) 0%, transparent 50%)' 
      }} />

      <audio
        ref={audioRef}
        loop
        preload="auto"
        style={{ display: 'none' }}
      >
        <source 
          src="/audio/landing-screen.mp3"
          type="audio/mpeg" 
        />
        Votre navigateur ne supporte pas l'audio.
      </audio>

      {/* Header */}
      <header style={{
        padding: "0 20px",
        height: "60px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid ${th.border}`,
        background: isDark ? "rgba(10, 10, 15, 0.95)" : "rgba(248, 249, 252, 0.95)",
        backdropFilter: "blur(12px)",
        position: "sticky",
        top: 0,
        zIndex: 100,
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        
        {/* Bouton Retour à gauche */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, padding: 0, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = T.textDim}
            onMouseLeave={e => e.currentTarget.style.color = T.textMuted}
          >
            <ArrowLeft size={13} />
            <Mono size={10} color="inherit">Dashboard</Mono>
          </button>

        {/* Centre : Statut LAB + Version */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "4px 10px",
            background: th.surfaceAlt,
            borderRadius: "6px",
            border: `1px solid ${th.border}`,
          }}>
            <div style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "#10B981",
              animation: "pulse 2s infinite",
            }} />
            <span style={{ 
              fontSize: "10px", 
              fontWeight: 600, 
              color: th.textMuted,
              letterSpacing: "0.05em",
            }}>
              PESACORE Architect LAB
            </span>
          </div>
          
          <span style={{ 
            fontSize: "10px", 
            color: th.textMuted,
            fontWeight: 400,
          }}>
            v1.0
          </span>
        </div>

        {/* Groupe de boutons à droite */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}>
          
          {/* Bouton Musique */}
          <button
            onClick={toggleMusic}
            style={{
              width: 32,
              height: 32,
              borderRadius: "6px",
              background: th.surfaceAlt,
              border: `1px solid ${th.border}`,
              color: isMusicPlaying ? th.orange : th.textSub,
              fontSize: "16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
            }}
            title={isMusicPlaying ? "Couper la musique d'ambiance" : "Activer la musique d'ambiance"}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = th.orange;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = th.border;
            }}
          >
            {isMusicPlaying ? "♪" : "♫"}
          </button>

          {/* Bouton Thème */}
          <button
            onClick={onTheme}
            style={{
              width: 32,
              height: 32,
              borderRadius: "6px",
              background: th.surfaceAlt,
              border: `1px solid ${th.border}`,
              color: th.textSub,
              fontSize: "13px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
            }}
            title={isDark ? "Mode clair" : "Mode sombre"}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = th.orange;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = th.border;
            }}
          >
            {isDark ? "☀️" : "🌙"}
          </button>
        </div>
      </header>
      

      {/* Hero — version interface (inspirée de CentreAjustementIntro) */}
<div style={{
  maxWidth: "1100px",
  margin: "0 auto",
  padding: "32px 24px 40px",
  position: "relative",
  zIndex: 1,
}}>
  <div style={{
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    opacity: 1,
  }}>
    {/* En-tête du hero — badges */}
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexWrap: "wrap",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
      }}>
        <span style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: th.orange,
          display: "inline-block",
        }} />
        <span style={{
          fontSize: "11px",
          fontWeight: 600,
          color: th.orange,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          Études interactives
        </span>
      </div>
      <span style={{
        fontSize: "11px",
        color: th.textFaint,
        fontFamily: "'JetBrains Mono', monospace",
        background: th.surfaceAlt,
        padding: "2px 9px",
        borderRadius: "20px",
        border: `1px solid ${th.border}`,
      }}>
        {templates.length} protocoles
      </span>
      <span style={{
        fontSize: "11px",
        color: th.textFaint,
        fontFamily: "'JetBrains Mono', monospace",
        background: th.surfaceAlt,
        padding: "2px 9px",
        borderRadius: "20px",
        border: `1px solid ${th.border}`,
      }}>
        {categories.length} domaines
      </span>
    </div>

    {/* Titre + description */}
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: "10px",
    }}>
      <h1 style={{
        fontSize: "clamp(28px, 3.5vw, 42px)",
        fontWeight: 800,
        color: th.text,
        lineHeight: 1.12,
        letterSpacing: "-0.025em",
        margin: 0,
        fontFamily: "'Inter', sans-serif",
      }}>
        Centre d'ajustement
      </h1>
      <p style={{
        fontSize: "15px",
        color: th.textMuted,
        lineHeight: 1.78,
        maxWidth: "560px",
        margin: 0,
        fontStyle: "italic",
      }}>
        Sélectionnez un domaine, puis un protocole. Chaque cas est un environnement 
        que vous contrôlez pour observer et analyser vos décisions.
      </p>
    </div>

    {/* Métriques / quotas */}
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "16px",
      fontSize: "11px",
      fontFamily: "'JetBrains Mono', monospace",
      color: th.textMuted,
    }}>
      <span>{templates.length} protocoles</span>
      <span>•</span>
      <span>{categories.length} domaines</span>
      <span>•</span>
      {(() => {
        const trialEndsAt = user?.subscription?.trialEndsAt;
        const isTrial = trialEndsAt ? new Date(trialEndsAt) > new Date() : false;

        let color = th.textMuted;
        if (isTrial) color = th.orange;
        else if (isUnlimited) color = '#10B981';

        return (
          <span style={{ color }}>
            {getQuotaDisplay()}
          </span>
        );
      })()}
    </div>
  </div>
</div>

{/* Catégories — chips (style interface) */}
<div style={{
  maxWidth: "1100px",
  margin: "0 auto",
  padding: "0 24px 28px",
  position: "relative",
  zIndex: 1,
}}>
  <div style={{
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    alignItems: "center",
  }}>
    <button
      onClick={() => {
        setSelectedCategory(null);
        setSelectedRun(null);
      }}
      style={{
        padding: "6px 16px",
        borderRadius: "20px",
        border: `1px solid ${!selectedCategory ? th.orange : th.border}`,
        background: !selectedCategory ? `${th.orange}08` : "transparent",
        color: !selectedCategory ? th.orange : th.textSub,
        fontSize: "11px",
        fontWeight: 500,
        cursor: "pointer",
        fontFamily: "'JetBrains Mono', monospace",
        transition: "all 0.15s",
      }}
    >
      Tous les cas
    </button>

    {categories.map((cat) => {
      const isActive = selectedCategory === cat.name;
      return (
        <button
          key={cat.name}
          onClick={() => handleCategoryClick(cat.name)}
          style={{
            padding: "6px 16px",
            borderRadius: "20px",
            border: `1px solid ${isActive ? cat.color : th.border}`,
            background: isActive ? `${cat.color}08` : "transparent",
            color: isActive ? cat.color : th.textSub,
            fontSize: "11px",
            fontWeight: 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontFamily: "'JetBrains Mono', monospace",
            transition: "all 0.15s",
          }}
        >
          <span style={{ fontSize: "14px" }}>{cat.icon}</span>
          {cat.name}
        </button>
      );
    })}
  </div>
</div>


{/* Grille des runs — format tableau de bord avec lignes marquées et tooltip au survol */}
<div ref={categoryRunsRef} style={{ position: "relative", zIndex: 1 }}>
  <div style={{
    maxWidth: "1100px",
    margin: "0 auto",
    padding: "0 24px 48px",
  }}>
    {selectedCategory || allRuns.length > 0 ? (
      <div style={{
        marginBottom: "16px",
        paddingBottom: "10px",
        borderBottom: `2px solid ${selectedCategory 
          ? categoriesMap.get(selectedCategory)?.color + '40'
          : th.border}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
      }}>
        <h2 style={{
          fontSize: "13px",
          fontWeight: 600,
          color: selectedCategory 
            ? categoriesMap.get(selectedCategory)?.color 
            : th.textMuted,
          fontFamily: "'Inter', sans-serif",
        }}>
          {selectedCategory 
            ? `${selectedCategory} — ${currentRuns.length} protocole${currentRuns.length > 1 ? 's' : ''}`
            : `Découvrir — ${allRuns.length} protocoles en cours`}
        </h2>
        <span style={{
          fontSize: "9px",
          color: th.textFaint,
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          {selectedCategory ? currentRuns.length : allRuns.length} résultats
        </span>
      </div>
    ) : null}

    {/* Tableau avec fond et bordures marquées */}
    <div style={{
      background: th.surface,
      borderRadius: "8px",
      border: `1px solid ${th.border}`,
      //overflow: "hidden",
    }}>
      {/* En-tête de colonnes */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "32px 2.5fr 1fr 1fr 80px",
        gap: "12px",
        padding: "10px 16px",
        fontSize: "9px",
        fontWeight: 700,
        color: th.textMuted,
        fontFamily: "'JetBrains Mono', monospace",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        borderBottom: `2px solid ${th.border}`,
        background: th.surfaceAlt,
      }}>
        <span>#</span>
        <span>Protocole</span>
        <span>Domaine</span>
        <span>Durée</span>
        <span style={{ textAlign: "right" }}>Accès</span>
      </div>

      {/* Lignes — avec catégorie sélectionnée */}
      {selectedCategory && currentRuns.map((run, index) => {
        const isSelected = selectedRun?.slug === run.slug;
        const locked = isTemplateLocked(run, user);
        const runMeta = getTemplateMeta(run.slug);
        const protocolId = formatProtocolId(run.slug);
        const cat = categoriesMap.get(selectedCategory);
        const categoryColor = cat?.color || th.orange;

        return (
          <div
            key={run.slug}
            onClick={() => !locked && handleRunClick(run)}
            onMouseEnter={() => setHoveredRun(run.slug)}
            onMouseLeave={() => setHoveredRun(null)}
            style={{
              display: "grid",
              gridTemplateColumns: "32px 2.5fr 1fr 1fr 80px",
              gap: "12px",
              padding: "12px 16px",
              borderBottom: `1px solid ${th.border}`,
              background: isSelected 
                ? `${categoryColor}06`
                : hoveredRun === run.slug 
                  ? th.surfaceHov 
                  : "transparent",
              borderLeft: isSelected ? `3px solid ${categoryColor}` : "3px solid transparent",
              cursor: locked ? "not-allowed" : "pointer",
              opacity: locked ? 0.5 : 1,
              transition: "all 0.12s",
              alignItems: "center",
              position: "relative",
            }}
          >
            <span style={{
              fontSize: "11px",
              fontWeight: 500,
              color: isSelected ? categoryColor : th.textFaint,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {String(index + 1).padStart(2, '0')}
            </span>

            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}>
              <span style={{
                fontSize: "16px",
              }}>
                {runMeta?.icon || "📋"}
              </span>
              <div>
                <div style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: isSelected ? categoryColor : th.text,
                  fontFamily: "'Inter', sans-serif",
                }}>
                  {run.title}
                </div>
                <div style={{
                  fontSize: "9px",
                  color: th.textFaint,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {protocolId}
                </div>
              </div>
            </div>

            <span style={{
              fontSize: "10px",
              color: categoryColor,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {selectedCategory}
            </span>

            <span style={{
              fontSize: "10px",
              color: th.textMuted,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {run.estimatedTime || "—"}min
            </span>

            <div style={{
              display: "flex",
              justifyContent: "flex-end",
            }}>
              {!locked ? (
                <span style={{
                  fontSize: "8px",
                  fontWeight: 700,
                  color: categoryColor,
                  fontFamily: "'JetBrains Mono', monospace",
                  padding: "2px 10px",
                  borderRadius: "4px",
                  background: `${categoryColor}12`,
                  letterSpacing: "0.06em",
                }}>
                  FREE
                </span>
              ) : (
                <span style={{
                  fontSize: "11px",
                }}>
                  🔒
                </span>
              )}
            </div>

            {/* Tooltip au survol — descriptif */}
            {hoveredRun === run.slug && run.description && (
              <div style={{
                position: "absolute",
                bottom: "calc(100% + 8px)",
                left: "50%",
                transform: "translateX(-50%)",
                padding: "10px 14px",
                borderRadius: "6px",
                background: th.surface,
                border: `1px solid ${categoryColor}30`,
                boxShadow: `0 8px 24px ${isDark ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.15)'}`,
                fontSize: "11px",
                color: th.text,
                maxWidth: "300px",
                textAlign: "center",
                lineHeight: 1.5,
                zIndex: 10,
                fontFamily: "'Inter', sans-serif",
                pointerEvents: "none",
              }}>
                {run.description}
                <div style={{
                  position: "absolute",
                  bottom: "-6px",
                  left: "50%",
                  transform: "translateX(-50%) rotate(45deg)",
                  width: 10,
                  height: 10,
                  background: th.surface,
                  borderRight: `1px solid ${categoryColor}30`,
                  borderBottom: `1px solid ${categoryColor}30`,
                }} />
              </div>
            )}
          </div>
        );
      })}

      {/* Lignes — vue "Tous les cas" */}
      {!selectedCategory && allRuns.map(({ category, categoryColor, categoryIcon, run }, index) => {
        const isSelected = selectedRun?.slug === run.slug;
        const locked = isTemplateLocked(run, user);
        const runMeta = getTemplateMeta(run.slug);
        const protocolId = formatProtocolId(run.slug);

        return (
          <div
            key={run.slug}
            onClick={() => !locked && handleRunClick(run)}
            onMouseEnter={() => setHoveredRun(run.slug)}
            onMouseLeave={() => setHoveredRun(null)}
            style={{
              display: "grid",
              gridTemplateColumns: "32px 2.5fr 1fr 1fr 80px",
              gap: "12px",
              padding: "12px 16px",
              borderBottom: `1px solid ${th.border}`,
              background: isSelected 
                ? `${categoryColor}06`
                : hoveredRun === run.slug 
                  ? th.surfaceHov 
                  : "transparent",
              borderLeft: isSelected ? `3px solid ${categoryColor}` : "3px solid transparent",
              cursor: locked ? "not-allowed" : "pointer",
              opacity: locked ? 0.5 : 1,
              transition: "all 0.12s",
              alignItems: "center",
              position: "relative",
            }}
          >
            <span style={{
              fontSize: "11px",
              fontWeight: 500,
              color: isSelected ? categoryColor : th.textFaint,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {String(index + 1).padStart(2, '0')}
            </span>

            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}>
              <span style={{
                fontSize: "16px",
              }}>
                {runMeta?.icon || "📋"}
              </span>
              <div>
                <div style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: isSelected ? categoryColor : th.text,
                  fontFamily: "'Inter', sans-serif",
                }}>
                  {run.title}
                </div>
                <div style={{
                  fontSize: "9px",
                  color: th.textFaint,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {protocolId}
                </div>
              </div>
            </div>

            <span style={{
              fontSize: "10px",
              color: categoryColor,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              <span style={{ marginRight: "4px" }}>{categoryIcon}</span>
              {category}
            </span>

            <span style={{
              fontSize: "10px",
              color: th.textMuted,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {run.estimatedTime || "—"}min
            </span>

            <div style={{
              display: "flex",
              justifyContent: "flex-end",
            }}>
              {!locked ? (
                <span style={{
                  fontSize: "8px",
                  fontWeight: 700,
                  color: categoryColor,
                  fontFamily: "'JetBrains Mono', monospace",
                  padding: "2px 10px",
                  borderRadius: "4px",
                  background: `${categoryColor}12`,
                  letterSpacing: "0.06em",
                }}>
                  FREE
                </span>
              ) : (
                <span style={{
                  fontSize: "11px",
                }}>
                  🔒
                </span>
              )}
            </div>

            {/* Tooltip au survol — descriptif */}
            {hoveredRun === run.slug && run.description && (
              <div style={{
                position: "absolute",
                bottom: "calc(100% + 8px)",
                left: "50%",
                transform: "translateX(-50%)",
                padding: "10px 14px",
                borderRadius: "6px",
                background: th.surface,
                border: `1px solid ${categoryColor}30`,
                boxShadow: `0 8px 24px ${isDark ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.15)'}`,
                fontSize: "11px",
                color: th.text,
                maxWidth: "300px",
                textAlign: "center",
                lineHeight: 1.5,
                zIndex: 10,
                fontFamily: "'Inter', sans-serif",
                pointerEvents: "none",
              }}>
                {run.description}
                <div style={{
                  position: "absolute",
                  bottom: "-6px",
                  left: "50%",
                  transform: "translateX(-50%) rotate(45deg)",
                  width: 10,
                  height: 10,
                  background: th.surface,
                  borderRight: `1px solid ${categoryColor}30`,
                  borderBottom: `1px solid ${categoryColor}30`,
                }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
</div>

{/* CTA — style interface */}
<div ref={ctaRef} style={{ 
  maxWidth: "600px",
  margin: "0 auto",
  padding: "0 24px 60px",
  position: "relative",
  zIndex: 1,
  animation: "fadeUp 0.4s ease-out",
}}>
  <GuideHint
    zoneId="landing"
    content={GUIDE_CONTENT.zones.find(z => z.id === "landing")}
    accent={accent}
    th={th}
  />
  {selectedRun && profiles.length > 0 && (
    <div style={{
      marginBottom: "20px",
      padding: "16px 20px",
      borderRadius: "10px",
      background: th.surface,
      border: `1px solid ${meta?.color}20`,
    }}>
      <p style={{
        fontSize: "9px", 
        fontWeight: 700,
        color: meta?.color, 
        letterSpacing: "0.08em",
        marginBottom: "12px",
        textTransform: "uppercase",
        fontFamily: "'Inter', sans-serif",
      }}>
        PROFIL
      </p>
      
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {profiles.map(p => {
          const isActive = profile?.id === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setProfile(isActive ? null : p)}
              style={{
                textAlign: "left",
                padding: "12px 16px",
                borderRadius: "8px",
                border: `1px solid ${isActive ? meta?.color : th.border}`,
                background: isActive ? `${meta?.color}08` : "transparent",
                cursor: "pointer",
                transition: "all 0.2s",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              <p style={{
                fontSize: "12px", 
                fontWeight: 600,
                color: isActive ? meta?.color : th.text,
                marginBottom: "4px",
              }}>
                {p.label}
              </p>
              <p style={{
                fontSize: "11px", 
                color: th.textSub,
              }}>
                {p.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  )}

  <button
    onClick={handleLaunch}
    disabled={!selectedRun || launching}
    style={{
      width: "100%",
      padding: "14px 24px",
      borderRadius: "8px",
      border: `1px solid ${selectedRun && meta ? meta.color : th.border}`,
      background: selectedRun && meta ? `${meta.color}12` : th.surfaceAlt,
      color: selectedRun && meta ? meta.color : th.textFaint,
      fontSize: "13px",
      fontWeight: 600,
      cursor: selectedRun ? "pointer" : "not-allowed",
      fontFamily: "'Inter', sans-serif",
      transition: "all 0.2s",
    }}
  >
    {launching ? (
      <>
        <div style={{
          width: 12, 
          height: 12, 
          borderRadius: "50%",
          border: "2px solid currentColor",
          borderTopColor: "transparent", 
          animation: "rotate 0.7s linear infinite",
          display: "inline-block",
          marginRight: "8px",
        }} />
        Lancement...
      </>
    ) : selectedRun ? (
      `⟡ LANCER ${formatProtocolId(selectedRun.slug)} →`
    ) : (
      "⟡ SÉLECTIONNER UN PROTOCOLE"
    )}
  </button>

  <div style={{
    marginTop: "12px",
    padding: "10px 16px",
    background: th.surfaceAlt,
    borderRadius: "6px",
    fontSize: "9px",
    fontFamily: "'JetBrains Mono', monospace",
    color: th.textMuted,
    textAlign: "center",
  }}>
    20-45 min • sauvegarde auto
  </div>
</div>
      
    </div>
  );
}

function getCategoryAssets(categoryName) {
  const assets = {
    'Éducation': { icon: '🎓', color: '#3B82F6', shortDescription: 'Formation, pédagogie et apprentissage' },
    'Famille': { icon: '🏠', color: '#EC4899', shortDescription: 'Relations, éducation et vie familiale' },
    'Société': { icon: '🌍', color: '#10B981', shortDescription: 'Citoyenneté, communautés et lien social' },
    'Travail': { icon: '💼', color: '#F59E0B', shortDescription: 'Carrière, management et organisation' },
    'Art': { icon: '🎨', color: '#8B5CF6', shortDescription: 'Création, expression et culture' },
    'Santé': { icon: '💚', color: '#22C55E', shortDescription: 'Bien-être, soins et prévention' },
    'Environnement': { icon: '🌱', color: '#14B8A6', shortDescription: 'Écologie, durable et transition' },
    'Justice': { icon: '⚖️', color: '#EF4444', shortDescription: 'Droit, équité et citoyenneté' },
    'Finance': { icon: '💹', color: '#321575', shortDescription: 'Finance, éthique et formation' },
    'Histoire': { icon: '🎓', color: '#3B82F6', shortDescription: 'Formation, pédagogie et apprentissage' },
  };
    return assets[categoryName] || { icon: '🔬', color: '#F59E0B', shortDescription: 'Explorez ce domaine' };
}

function buildEngine(cd) {
  return {
    params:        { ...cd.initialState.parameters },
    initialParams: { ...cd.initialState.parameters },
    families:      cd.initialState.families.map(f => ({ ...f })),
    interactions:  [...(cd.initialState.interactions || [])],
    completed:     new Set(),
    stepIdx:       0,
    phaseIdx:      0,
    finished:      false,
    snap:          null,
    canBack:       false,
  };
}

function snap(s) {
  return {
    params:       { ...s.params },
    families:     s.families.map(f => ({ ...f })),
    interactions: [...s.interactions],
    completed:    new Set(s.completed),
    stepIdx:      s.stepIdx,
    phaseIdx:     s.phaseIdx,
  };
}

function engineReducer(state, action) {
  switch (action.type) {
    case "APPLY_ACTION": {
      const { cd, id } = action;
      if (state.completed.has(id)) return state;
      const step = cd.steps[state.stepIdx];
      const act  = step?.actions?.find(a => a.id === id);
      if (!act) return state;
      if ((act.prerequisites || []).some(p => !state.completed.has(p))) return state;
      const np = { ...state.params };
      Object.entries(act.effect || {}).forEach(([k, v]) => {
        np[k] = Math.max(0, Math.min(100, (np[k] ?? 50) + v));
      });
      let fams = state.families;
      if (act.toggleFamilies?.length)
        fams = state.families.map(f => act.toggleFamilies.includes(f.name) ? { ...f, isActive: true, isToxic: false } : f);
      let ints = state.interactions;
      if (act.addInteractions?.length) {
        ints = [...ints];
        act.addInteractions.forEach(i => {
          if (!ints.some(x => x.source === i.source && x.target === i.target)) ints.push({ ...i });
        });
      }
      const nc = new Set(state.completed); nc.add(id);
      return { ...state, params: np, families: fams, interactions: ints, completed: nc, snap: snap(state), canBack: true };
    }
    case "ADVANCE": {
      const { cd } = action;
      const phases = cd.steps[state.stepIdx]?.phases || [];
      const prev   = snap(state);
      if (state.phaseIdx + 1 < phases.length) return { ...state, phaseIdx: state.phaseIdx + 1, snap: prev, canBack: true };
      if (state.stepIdx + 1 < cd.steps.length) return { ...state, stepIdx: state.stepIdx + 1, phaseIdx: 0, snap: prev, canBack: true };
      return { ...state, finished: true, snap: prev, canBack: true };
    }
    case "BACK": {
      if (!state.canBack || !state.snap) return state;
      const s = state.snap;
      return { ...state, params: s.params, families: s.families, interactions: s.interactions, completed: s.completed, stepIdx: s.stepIdx, phaseIdx: s.phaseIdx, finished: false, snap: null, canBack: false };
    }
    case "RESTART": return buildEngine(action.cd);
    default: return state;
  }
}

function useCountdown(duration, onEnd, active) {
  const [r, setR]  = useState(duration);
  const iRef       = useRef(null);
  const cbRef      = useRef(onEnd);
  const fired      = useRef(false);
  useEffect(() => { cbRef.current = onEnd; });
  useEffect(() => {
    if (iRef.current) { clearInterval(iRef.current); iRef.current = null; }
    if (!active || !duration) { setR(duration || 0); return; }
    fired.current = false; setR(duration);
    iRef.current = setInterval(() => {
      setR(r => {
        const n = r - 1;
        if (n <= 0) { clearInterval(iRef.current); if (!fired.current) { fired.current = true; setTimeout(() => cbRef.current(), 0); } return 0; }
        return n;
      });
    }, 1000);
    return () => { if (iRef.current) clearInterval(iRef.current); };
  }, [duration, active]); // eslint-disable-line
  return r;
}

function buildFamilyColors(cd) {
  const m = {};
  (cd.initialState?.families || []).forEach((f, i) => { m[f.name] = f.color || FAMILY_PALETTE[i % FAMILY_PALETTE.length]; });
  return m;
}

function buildActionSets(cd) {
  const diagIds    = new Set(cd.metadata?.diagIds    || []);
  const profileIds = new Set(cd.metadata?.profileIds || []);
  if (!diagIds.size && cd.steps?.length)
    cd.steps[0]?.actions?.forEach(a => { if (a.id.startsWith("diag_")) diagIds.add(a.id); });
  if (!profileIds.size && cd.steps?.length)
    cd.steps[cd.steps.length - 1]?.actions?.forEach(a => { if (/^(posture_|position_|config_)/.test(a.id)) profileIds.add(a.id); });
  return { diagIds, profileIds };
}

function buildStatMeta(cd) {
  const IC = ["◎","⬡","◈","◇","▣","◉","△","—"];
  return Object.fromEntries(Object.entries(cd.metrics || {}).map(([k, v], i) =>
    [k, { icon: v.icon || IC[i % IC.length], label: v.label || k, description: v.description || "" }]
  ));
}

function buildConfig(completed, params, cd) {
  const { diagIds, profileIds } = buildActionSets(cd);
  const all = [...completed];
  
  const featureAll = (cd.steps || []).flatMap(s => s.actions || [])
    .filter(a => !diagIds.has(a.id) && !profileIds.has(a.id))
    .map(a => a.id);
  
  const featureDone = all.filter(id => !diagIds.has(id) && !profileIds.has(id));
  const uniqueFeatureAll = [...new Set(featureAll)];

  // ── GARDE-FOU ZÉLÉ ─────────────────────────────────────────────
  // Si l'utilisateur a sélectionné > 80% des actions disponibles
  // dans une même étape, c'est probablement du clic-tout
  const zealotSteps = (cd.steps || []).filter(step => {
    const stepFeature = (step.actions || [])
      .filter(a => !diagIds.has(a.id) && !profileIds.has(a.id));
    if (stepFeature.length < 3) return false; // étape trop courte pour détecter
    const doneInStep = stepFeature.filter(a => completed.has(a.id));
    return doneInStep.length / stepFeature.length > 0.8;
  });
  const zealotRatio = zealotSteps.length / Math.max(1, cd.steps.length);
  const isZealot = zealotRatio > 0.5; // plus de la moitié des étapes saturées

  // Score brut
  const rawScore = uniqueFeatureAll.length
    ? Math.round(featureDone.length / uniqueFeatureAll.length * 100)
    : 0;

  // Pénalité si comportement zélé : le score est cappé à 60%
  // (évite qu'un cliqueur compulsif atterrisse sur un débrief "full")
  const configScore = isZealot ? Math.min(rawScore, 60) : rawScore;

  // ── TYPE DE DÉBRIEF ─────────────────────────────────────────────
  const debriefType = configScore >= 70 ? "full"
    : configScore >= 35 ? "standard"
    : "starter";

  // ── SIGNAUX DIAG ────────────────────────────────────────────────
  const diagSignals = all.filter(id => diagIds.has(id)).map(id => {
    const act = (cd.steps || []).flatMap(s => s.actions || []).find(a => a.id === id);
    return {
      id,
      label: act?.label || id,
      feedback: act?.feedback || "",
      deepFeedback: act?.deepFeedback || null,
      competencySignal: act?.competencySignal || null,
    };
  });

  return {
    configScore,
    rawScore,       // ← conserver pour debug/affichage
    isZealot,       // ← nouveau flag exposé
    debriefType,
    profile: debriefType,
    completedActions: all,
    metrics: { ...params },
    diagSignals,
  };
}

function matchSolution(cd, completed) {
  if (!cd.solutions?.length) return null;

  // 1. Correspondance exacte (toutes les actions requises présentes)
  const exact = cd.solutions.find(s =>
    (s.requiredActions || []).length > 0 &&
    (s.requiredActions || []).every(id => completed.has(id))
  );
  if (exact) return exact;

  // 2. Correspondance partielle — seuil minimum de 60%
  const MATCH_THRESHOLD = 0.6;
  const ranked = cd.solutions
    .filter(s => (s.requiredActions || []).length > 0)
    .map(s => {
      const required = s.requiredActions || [];
      const matched = required.filter(id => completed.has(id)).length;
      return { s, ratio: matched / required.length, matched };
    })
    .filter(({ ratio }) => ratio >= MATCH_THRESHOLD)
    .sort((a, b) => b.ratio - a.ratio);

  if (ranked.length > 0) return ranked[0].s;

  // 3. Aucune correspondance suffisante → profil "neutre"
  // (ne retourne PAS systématiquement le dernier profil)
  return cd.solutions.find(s => s.isDefault)
    ?? cd.solutions[cd.solutions.length - 1]; 
    // fallback toléré seulement si 0 solutions avec requiredActions
}

function resolveAccent(token, th) {
  const map = { orange: th.orange, sky: th.sky, amber: th.amber, emerald: th.emerald, danger: th.danger };
  return map[token] || th.orange;
}

const Badge = ({ children, color, th, sm, glow }) => (
  <span style={{
    display: "inline-flex", alignItems: "center",
    padding: sm ? "2px 9px" : "3px 12px",
    borderRadius: T.rPill,
    fontSize: sm ? "10px" : "11px",
    fontWeight: 600, color,
    background: `${color}12`,
    border: `1px solid ${color}25`,
    boxShadow: glow ? `0 0 8px ${color}30` : "none",
    letterSpacing: "0.01em",
    transition: "all 0.2s",
  }}>
    {children}
  </span>
);

const SectionLabel = ({ children, th, accent }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
    {accent && <div style={{ width: "3px", height: "12px", borderRadius: "2px", background: accent, flexShrink: 0 }} />}
    <p style={{ fontSize: "10px", fontWeight: 700, color: th.textFaint, letterSpacing: "0.1em", textTransform: "uppercase", margin: 0 }}>
      {children}
    </p>
  </div>
);

const MetricBar = ({ label, val, prev, icon, desc, th, accent }) => {
  const delta = val - prev;
  const color = val >= 70 ? th.amber : val >= 40 ? th.sky : th.danger;
  return (
    <div title={desc || ""} style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ fontSize: "11px", color: th.textFaint, lineHeight: 1 }}>{icon}</span>
          <span style={{ fontSize: "11px", fontWeight: 500, color: th.textSub }}>{label}</span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
          {delta !== 0 && (
            <span style={{ fontSize: "10px", fontWeight: 700, color: delta > 0 ? th.amber : th.danger, background: delta > 0 ? th.amberDim : th.dangerDim, padding: "1px 5px", borderRadius: T.rPill }}>
              {delta > 0 ? "▲" : "▼"}{Math.abs(delta)}
            </span>
          )}
          <span style={{ fontSize: "15px", fontWeight: 700, color: th.text, fontVariantNumeric: "tabular-nums", fontFamily: "'DM Mono', monospace", animation: "ca-countUp 0.35s ease-out both", animationDelay: "0.1s", }}>{val}</span>
        </div>
      </div>
      <div style={{ height: "4px", borderRadius: "2px", background: `${color}14`, overflow: "hidden", position: "relative" }}>
        <div style={{ position: "absolute", height: "100%", width: `${prev}%`, background: `${color}20`, borderRadius: "2px" }} />
        <div style={{ height: "100%", width: `${val}%`, background: `linear-gradient(90deg, ${color}88, ${color})`, borderRadius: "2px", transition: "width 0.7s cubic-bezier(.4,0,.2,1)", position: "relative" }}>
          <div style={{ position: "absolute", right: 0, top: -1, width: "6px", height: "6px", borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}` }} />
        </div>
      </div>
    </div>
  );
};

const FamilyNode = ({ family, familyColors, highlighted, th, staggerIdx = 0 }) => {
  const color  = familyColors[family.name] || th.sky;
  const active = family.isActive && !family.isToxic;
  const delay  = staggerIdx * 70;
  
  return (
    <div
      title={family.narrativeRole || family.description}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", gap: "6px",
        opacity: active ? 1 : 0.18,
        transition: "opacity 0.5s ease",
        animation: `ca-nodeIn 0.4s cubic-bezier(.34,1.56,.64,1) ${delay}ms both`,
         minWidth: 0,       
         width: "100%",
      }}
    >
      <div style={{ position: "relative", width: 36, height: 36 }}>
        {highlighted && (
          <div style={{
            position: "absolute", inset: -4, borderRadius: "50%",
            border: `2px solid ${color}`,
            opacity: 0.7,
            animation: "ca-ring 0.9s ease-out infinite",
            
          }} />
        )}
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: active ? `${color}10` : th.surfaceAlt,
          border: `1.5px solid ${highlighted ? color : active ? `${color}30` : th.border}`,
          boxShadow: highlighted
            ? `0 0 24px ${color}65, 0 0 48px ${color}25`
            : active ? `0 0 8px ${color}20` : "none",
          
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.4s cubic-bezier(.4,0,.2,1)",
        }}>
          <div style={{
            width: 10, height: 10, borderRadius: "50%",
            background: active ? color : th.textFaint,
            boxShadow: active ? `0 0 8px ${color}` : "none",
            transition: "all 0.4s",
            animation: highlighted ? "ca-pulse 0.8s ease-in-out infinite" : "none",
            
          }} />
        </div>
      </div>
      <span style={{
        fontSize: "10px",      
        fontWeight: 600,
        color: active ? color : th.textFaint,
        textAlign: "center",
        maxWidth: "72px",     
        lineHeight: 1.25,  
        letterSpacing: "0.02em",
        wordBreak: "break-word",  
        display: "-webkit-box",
        WebkitLineClamp: 3,       
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}>
        {family.narrativeRole}  {/* était .split(" ").slice(0, 2).join(" ") — afficher le nom complet */}
      </span>
    </div>
  );
};

const ActionRow = ({ act, index, accent, done, disabled, onClick, th, animDelay = 0 }) => {
  const [hov, setHov] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled || done}
      onMouseEnter={() => !done && !disabled && setHov(true)}
      onMouseLeave={() => { setHov(false); setPressed(false); }}
      onMouseDown={() => !done && !disabled && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{
        width: "100%", textAlign: "left",
        padding: "14px 16px",
        border:       `1.5px solid ${done ? `${th.amber}28` : hov ? `${accent}50` : th.border}`,
        borderLeft:   `3px solid ${done ? th.amber : hov ? accent : "transparent"}`,
        background:   done ? `linear-gradient(135deg, ${th.amber}06, ${th.amber}03)` : hov ? `linear-gradient(135deg, ${accent}08, ${accent}04)` : th.surfaceAlt,
        cursor:       done || disabled ? "default" : "pointer",
        transform:    pressed ? "scale(0.992)" : "scale(1)",
        transition:   "all 0.14s cubic-bezier(.4,0,.2,1)",
        display:      "block", outline: "none",
        borderRadius: T.rSm,
        animation: `ca-actionIn 0.28s cubic-bezier(.34,1.36,.64,1) ${animDelay}ms both`,
        boxShadow:    hov ? `0 2px 16px ${accent}14` : "none",
      }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
        <div style={{ minWidth: 26, height: 26, borderRadius: T.rXs, background: done ? `${th.amber}16` : hov ? `${accent}18` : `${accent}10`, border: `1px solid ${done ? `${th.amber}28` : hov ? `${accent}40` : `${accent}18`}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: done ? "13px" : "11px", fontWeight: 700, color: done ? th.amber : disabled ? th.textFaint : accent, fontFamily: "'DM Mono', monospace", transition: "all 0.14s" }}>
          {done ? "✓" : String(index + 1).padStart(2, "0")}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: "13px", lineHeight: "1.58", color: done ? th.textSub : disabled ? th.textFaint : th.text, fontWeight: done ? 400 : 450, margin: 0 }}>{act.label}</p>
          {act.competencySignal && !done && (
            <div style={{ marginTop: "4px", display: "flex", alignItems: "center", gap: "5px" }}>
              <span style={{ display: "inline-block", width: 4, height: 4, borderRadius: "50%", background: `${accent}55` }} />
              <p style={{ fontSize: "10px", color: `${accent}`, margin: 0, fontFamily: "'DM Mono', monospace" }}>
                {act.competencySignal.name} · {act.competencySignal.level}
              </p>
            </div>
          )}
        </div>
        {!done && !disabled && (
          <span style={{ color: `${accent}50`, fontSize: "18px", flexShrink: 0, transform: hov ? "translateX(2px)" : "translateX(0)", transition: "transform 0.14s" }}>›</span>
        )}
      </div>
    </button>
  );
};

const FeedbackCard = ({ action, accent, th }) => {
  const [openHow,  setOpenHow]  = useState(false);
  const [openCost, setOpenCost] = useState(false);
  const [openBoost,setOpenBoost]= useState(false);
  const df = action?.deepFeedback;

  const toggleHow = () => {
    if (openHow) {
      
      setOpenHow(false);
      setOpenCost(false);
    } else {
      setOpenHow(true);
    }
  };  

  // Reset à chaque nouvelle action
  const prevId = useRef(action?.id);
  useEffect(() => {
    if (action?.id !== prevId.current) {
      setOpenHow(false);
      setOpenCost(false);
      setOpenBoost(false);
      prevId.current = action?.id;
    }
  }, [action?.id]);

  if (!df) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>

      {/* ── 1. whyItMatters — toujours visible ─────────── */}
      {df.whyItMatters && (
        <div style={{
          padding: "14px 0 16px",
          borderBottom: `1px solid ${th.border}`,
          animation: "ca-reveal 0.3s ease-out both",
        }}>
          <p style={{
            fontSize: "13px", fontWeight: 500,
            color: th.text, lineHeight: "1.68", margin: 0,
          }}>
            {df.whyItMatters}
          </p>
        </div>
      )}

      {/* ── 2. whatHappened — déclencheur ──────────────── */}
      {df.whatHappened && (
        <div style={{ borderBottom: openHow ? `1px solid ${th.border}` : "none" }}>
          <button
            onClick={toggleHow}
            style={{
              width: "100%", textAlign: "left",
              padding: "13px 0",
              display: "flex", alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
              background: "none", border: "none",
              cursor: "pointer",
              color: openHow ? th.text : accent,
              transition: "color 0.15s",
            }}
          >
            <span style={{
              fontSize: "12px", fontWeight: 600,
              letterSpacing: "0.01em",
            }}>
              Comment ça fonctionne
            </span>
            <span style={{
              fontSize: "16px",
              transform: openHow ? "rotate(45deg)" : "rotate(0)",
              transition: "transform 0.2s cubic-bezier(.34,1.56,.64,1)",
              color: `${accent}80`,
              flexShrink: 0,
            }}>+</span>
          </button>

          {openHow && (
            <div style={{
              paddingBottom: "14px",
              animation: "ca-reveal 0.25s ease-out both",
            }}>
              <p style={{
                fontSize: "12px", color: th.textSub,
                lineHeight: "1.78", margin: 0,
              }}>
                {df.whatHappened}
              </p>

              {/* ── 3. whatItCosts — apparaît après "comment" ── */}
              {df.whatItCosts && (
                <button
                  onClick={() => setOpenCost(o => !o)}
                  style={{
                    marginTop: "12px",
                    display: "flex", alignItems: "center", gap: "6px",
                    background: "none", border: "none",
                    cursor: "pointer", padding: 0,
                    color: openCost ? th.danger : `${th.danger}80`,
                    fontSize: "12px", fontWeight: 600,
                    transition: "color 0.15s",
                    fontFamily: "inherit",
                  }}
                >
                  <span style={{
                    fontSize: "10px", padding: "2px 6px",
                    borderRadius: T.rPill,
                    border: `1px solid ${th.danger}30`,
                    color: "inherit",
                    transition: "border-color 0.15s",
                  }}>
                    {openCost ? "−" : "!"}
                  </span>
                  Ce que ça coûte
                </button>
              )}

              {openCost && df.whatItCosts && (
                <div style={{
                  marginTop: "10px",
                  padding: "11px 13px",
                  borderRadius: T.rXs,
                  background: th.dangerDim,
                  border: `1px solid ${th.danger}18`,
                  animation: "ca-reveal 0.2s ease-out both",
                }}>
                  <p style={{
                    fontSize: "12px", color: th.textSub,
                    lineHeight: "1.68", margin: 0,
                  }}>
                    {df.whatItCosts}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── 4. boostCondition — déclencheur indépendant ─ */}
      {df.boostCondition && (
        <div>
          <button
            onClick={() => setOpenBoost(o => !o)}
            style={{
              width: "100%", textAlign: "left",
              padding: "13px 0",
              display: "flex", alignItems: "center",
              justifyContent: "space-between", gap: "10px",
              background: "none", border: "none",
              cursor: "pointer",
              color: openBoost ? th.amber : `${th.amber}90`,
              transition: "color 0.15s",
              borderTop: `1px solid ${th.border}`,
            }}
          >
            <span style={{
              fontSize: "12px", fontWeight: 600,
              display: "flex", alignItems: "center", gap: "6px",
            }}>
              <span style={{ fontSize: "11px" }}>💡</span>
              Condition d'amplification
            </span>
            <span style={{
              fontSize: "16px",
              transform: openBoost ? "rotate(45deg)" : "rotate(0)",
              transition: "transform 0.2s cubic-bezier(.34,1.56,.64,1)",
              color: `${th.amber}60`,
              flexShrink: 0,
            }}>+</span>
          </button>

          {openBoost && (
            <div style={{
              paddingBottom: "14px",
              animation: "ca-reveal 0.25s ease-out both",
            }}>
              <p style={{
                fontSize: "12px", color: th.amber,
                lineHeight: "1.58", margin: 0,
              }}>
                {df.boostCondition}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── 5. competencySignal — pied fixe ─────────────── */}
      {action.competencySignal && (
        <div style={{
          display: "flex", alignItems: "center", gap: "8px",
          borderTop: `1px solid ${th.border}`,
          paddingTop: "12px", marginTop: "2px",
        }}>
          <Badge color={accent} th={th} sm>
            {action.competencySignal.level}
          </Badge>
          <span style={{ fontSize: "11px", color: th.textSub }}>
            {action.competencySignal.descriptor}
          </span>
        </div>
      )}
    </div>
  );
};

const TICON = { "bac": "🎓", "révisions": "📑", "assistante_maternelle": "🏠", "petite enfance": "🧒", "identité professionnelle": "🧭", "default": "◎" };
const ticon = t => { for (const tag of (t.tags || [])) { if (TICON[tag]) return TICON[tag]; } return TICON.default; };

const TemplateMenu = ({ templates, currentSlug, onSelect, accent, userPlan, th, user }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState("");
  const ref             = useRef(null);

  const isPaid = userPlan === "academia" || userPlan === "entreprise" || userPlan === "pro";  

  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const filtered = q.trim() 
    ? templates.filter(t =>
        t.title.toLowerCase().includes(q.toLowerCase()) ||
        (t.tags || []).some(g => g.toLowerCase().includes(q.toLowerCase()))
      ) 
    : templates;

  const cur = templates.find(t => t.slug === currentSlug);

  // Nouvelle fonction : peut-on cliquer sur ce template ?
  const canSelect = (template) => {
    if (!template) return false;
    return !isTemplateLocked(template, user);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: "flex", alignItems: "center", gap: "6px",
        padding: "5px 12px", borderRadius: T.rSm,
        background: open ? `${accent}14` : th.surfaceAlt,
        border: `1px solid ${open ? `${accent}45` : th.border}`,
        color: open ? accent : th.textSub,
        fontSize: "12px", fontWeight: 500, cursor: "pointer",
        transition: "all 0.14s", whiteSpace: "nowrap",
        boxShadow: open ? `0 0 12px ${accent}20` : "none",
      }}>
        <span>{cur ? ticon(cur) : "◎"}</span>
        <span style={{ maxWidth: "110px", overflow: "hidden", textOverflow: "ellipsis" }}>{cur?.title || "Templates"}</span>
        <span style={{ fontSize: "8px", opacity: 0.5, marginLeft: "1px" }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0, width: "310px",
          borderRadius: T.r, background: th.surface,
          border: `1px solid ${th.borderMed}`,
          boxShadow: "0 16px 56px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.25)",
          zIndex: 9999, overflow: "hidden",
          animation: "ca-fadeUp 0.16s ease-out both",
        }}>
          {/* Recherche */}
          <div style={{ padding: "10px 12px", borderBottom: `1px solid ${th.border}` }}>
            <input 
              autoFocus 
              value={q} 
              onChange={e => setQ(e.target.value)} 
              placeholder="Rechercher un template…" 
              style={{ width: "100%", padding: "7px 11px", borderRadius: T.rSm, background: th.surfaceAlt, border: `1px solid ${th.border}`, color: th.text, fontSize: "12px", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} 
            />
          </div>

          {/* Liste des templates */}
          <div style={{ maxHeight: "320px", overflowY: "auto", padding: "5px" }}>
            {filtered.map(t => {
              const isC = t.slug === currentSlug;
              const locked = isTemplateLocked(t);           // ta fonction existante
              const selectable = canSelect(t);

              return (
                <button 
                  key={t.slug} 
                  onClick={() => {
                    if (selectable) {
                      onSelect(t);
                      setOpen(false);
                      setQ("");
                    }
                  }}
                  disabled={!selectable}
                  style={{
                    width: "100%", 
                    textAlign: "left", 
                    padding: "9px 10px", 
                    borderRadius: T.rSm, 
                    border: "none", 
                    display: "block", 
                    background: isC ? `${accent}10` : "none", 
                    cursor: selectable ? "pointer" : "not-allowed",
                    opacity: selectable ? 1 : 0.6,
                    transition: "background 0.1s, opacity 0.1s"
                  }}
                  onMouseEnter={e => { if (selectable && !isC) e.currentTarget.style.background = th.surfaceHov; }}
                  onMouseLeave={e => { if (selectable && !isC) e.currentTarget.style.background = "none"; }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "9px" }}>
                    <span style={{ fontSize: "16px", flexShrink: 0 }}>{ticon(t)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "3px", flexWrap: "wrap" }}>
                        <span style={{ 
                          fontSize: "12px", 
                          fontWeight: 600, 
                          color: isC ? accent : th.text,
                          overflow: "hidden", 
                          textOverflow: "ellipsis", 
                          whiteSpace: "nowrap", 
                          maxWidth: "150px" 
                        }}>
                          {t.title}
                        </span>

                        {isC && <Badge color={accent} th={th} sm>actif</Badge>}

                        {/* Cadenas visible pour les templates bloqués */}
                        {locked && (
                          <span style={{ marginLeft: "auto", fontSize: "14px", opacity: 0.7 }}>🔒</span>
                        )}

                        {isPaid && !isC && !locked && (
                          <span style={{ fontSize: "10px", color: th.amber, background: th.amberDim, padding: "1px 7px", borderRadius: "10px", marginLeft: "auto" }}>
                            éditable
                          </span>
                        )}
                      </div>

                      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", alignItems: "center" }}>
                        {(t.tags || []).slice(0, 3).map(tag => (
                          <span key={tag} style={{ fontSize: "10px", color: th.textFaint, background: th.surfaceAlt, padding: "1px 7px", borderRadius: T.rPill, border: `1px solid ${th.border}` }}>
                            {tag}
                          </span>
                        ))}
                        {t.estimatedTime && <span style={{ fontSize: "10px", color: th.textFaint, marginLeft: "auto" }}>⏱ {t.estimatedTime} min</span>}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{ padding: "7px 12px", borderTop: `1px solid ${th.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "11px", color: th.textFaint }}>{filtered.length} template{filtered.length !== 1 ? "s" : ""}</span>
            {isPaid && <span style={{ fontSize: "11px", color: th.amber, fontWeight: 600 }}>✦ Édition activée</span>}
          </div>
        </div>
      )}
    </div>
  );
};


const Header = ({ caseData, eng, config, accent, th, isDark, fontIdx, onTheme, onFont, onBack, onRestart, onTemplateSelect, templates, finished, onToggleEditor, editorOpen, canEdit, user, userPlan, isMobile, onReturnToLanding, onToggleGuide }) => {
  const pct = finished ? 100 : Math.round(eng.stepIdx / Math.max(1, caseData.steps.length - 1) * 100);
  const PLAN_C = { freemium: th.textFaint, academia: th.sky, pro: th.amber };
  const pc = PLAN_C[userPlan] || th.textFaint;
  return (
    <header style={{ background: th.surfaceGlass, backdropFilter: th.glassBlur, WebkitBackdropFilter: th.glassBlur, borderBottom: `1px solid ${th.border}`, position: "sticky", top: 0, zIndex: 200 }}>
      <div style={{ height: "2px", background: th.border, position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: 0, height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${accent}80, ${accent})`, transition: "width 0.5s cubic-bezier(.4,0,.2,1)", boxShadow: `0 0 8px ${accent}50` }} />
      </div>
      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: isMobile ? "8px 12px" : "0 20px", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: "center", gap: isMobile ? "8px" : "14px", minHeight: isMobile ? "auto" : "52px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <div style={{ position: "relative", width: 8, height: 8, flexShrink: 0 }}>
              <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: accent, animation: "ca-pulse 2.5s ease-in-out infinite" }} />
              <div style={{ position: "absolute", inset: -3, borderRadius: "50%", background: `${accent}20`, animation: "ca-pulse 2.5s ease-in-out infinite 0.5s" }} />
            </div>
            <span style={{ fontSize: isMobile ? "13.5px" : "14px", fontWeight: 700, color: th.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: isMobile ? "160px" : "240px" }}>{caseData.name}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {config.configScore > 0 && !finished && <span style={{ fontSize: "11px", fontWeight: 700, color: th.amber, background: th.amberDim, padding: "3px 9px", borderRadius: "999px", border: `1px solid ${th.amber}25` }}>{config.configScore}%</span>}
            <span style={{ display: "inline-flex", fontSize: "10px", fontWeight: 700, color: pc, background: `${pc}15`, padding: "3px 9px", borderRadius: "999px", border: `1px solid ${pc}30`, whiteSpace: "nowrap" }}>{userPlan.toUpperCase()}</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "5px" : "6px", width: isMobile ? "100%" : "auto", overflowX: isMobile ? "auto" : "visible", padding: isMobile ? "4px 0" : 0, scrollbarWidth: "none" }}>
          <style>{`::-webkit-scrollbar { display: none; }`}</style>
          <TemplateMenu templates={templates} currentSlug={caseData.slug} onSelect={onTemplateSelect} accent={accent} userPlan={userPlan} th={th} user={user} />
          {!isMobile && canEdit && <button onClick={onToggleEditor} style={{ padding: "5px 11px", borderRadius: T.rSm, background: editorOpen ? `${th.orange}14` : th.surfaceAlt, border: `1px solid ${editorOpen ? `${th.orange}42` : th.border}`, color: editorOpen ? th.orange : th.textSub, fontSize: "12px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", transition: "all 0.14s", boxShadow: editorOpen ? `0 0 10px ${th.orange}18` : "none", whiteSpace: "nowrap", flexShrink: 0 }} onMouseEnter={e => e.currentTarget.style.background = editorOpen ? `${th.orange}20` : th.surfaceHov} onMouseLeave={e => e.currentTarget.style.background = editorOpen ? `${th.orange}14` : th.surfaceAlt}>✦ Éditer</button>}
          {!isMobile && <button onClick={() => onFont((fontIdx + 1) % 3)} style={{ padding: "5px 9px", borderRadius: T.rSm, background: fontIdx > 0 ? th.skyDim : th.surfaceAlt, border: `1px solid ${fontIdx > 0 ? `${th.sky}35` : th.border}`, color: fontIdx > 0 ? th.sky : th.textSub, fontSize: "12px", fontWeight: 700, cursor: "pointer", transition: "all 0.14s", whiteSpace: "nowrap", flexShrink: 0 }} onMouseEnter={e => e.currentTarget.style.background = th.surfaceHov} onMouseLeave={e => e.currentTarget.style.background = fontIdx > 0 ? th.skyDim : th.surfaceAlt}>{["A","A+","A⁺"][fontIdx] || "A"}</button>}
          {eng.canBack && !finished && <button onClick={onBack} style={{ padding: "5px 10px", borderRadius: T.rSm, background: "none", border: `1px solid ${th.border}`, color: th.textSub, fontSize: "12px", cursor: "pointer", transition: "all 0.14s", whiteSpace: "nowrap", flexShrink: 0 }} onMouseEnter={e => { e.currentTarget.style.borderColor = th.borderHov; e.currentTarget.style.color = th.text; }} onMouseLeave={e => { e.currentTarget.style.borderColor = th.border; e.currentTarget.style.color = th.textSub; }}>← {isMobile ? "" : "Retour"}</button>}
          <button onClick={onTheme} style={{ width: 32, height: 32, borderRadius: T.rSm, background: th.surfaceAlt, border: `1px solid ${th.border}`, color: th.textSub, fontSize: "14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.14s", flexShrink: 0 }} onMouseEnter={e => e.currentTarget.style.background = th.surfaceHov} onMouseLeave={e => e.currentTarget.style.background = th.surfaceAlt}>{isDark ? "☀︎" : "◑"}</button>
          <button onClick={onRestart} style={{ padding: "5px 10px", borderRadius: T.rSm, background: "none", border: `1px solid ${th.border}`, color: th.textFaint, fontSize: "12px", cursor: "pointer", transition: "all 0.14s", whiteSpace: "nowrap", flexShrink: 0 }} onMouseEnter={e => { e.currentTarget.style.color = th.textSub; e.currentTarget.style.borderColor = th.borderHov; }} onMouseLeave={e => { e.currentTarget.style.color = th.textFaint; e.currentTarget.style.borderColor = th.border; }}>Réinitialiser</button>
          <button onClick={onReturnToLanding} style={{ padding: "5px 10px", borderRadius: T.rSm, background: "none", border: `1px solid ${th.border}`, color: th.textFaint, fontSize: "12px", cursor: "pointer", transition: "all 0.14s", display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap", flexShrink: 0 }} onMouseEnter={e => { e.currentTarget.style.color = th.orange; e.currentTarget.style.borderColor = `${th.orange}40`; }} onMouseLeave={e => { e.currentTarget.style.color = th.textFaint; e.currentTarget.style.borderColor = th.border; }} title="Changer de cas">⊞ Cas</button>
          <GuideButton
            onClick={() => onToggleGuide()}
            accent={accent}
            th={th}
          />
        </div>
      </div>
    </header>
  );
};

function DebriefOverview({ config, sol, cd, th, audienceProfile }) {
  const tc  = sol?.interpretation?.tone ? (TONE_COLOR[sol.interpretation.tone] || th.orange) : th.orange;
  const gap = sol ? Math.abs((sol.interpretation?.theoreticalPromise ?? 0) - (sol.interpretation?.realWorldProbability ?? 0)) : null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <div style={{ borderLeft: `4px solid ${tc}`, paddingLeft: "22px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: tc, boxShadow: `0 0 8px ${tc}` }} />
          <Badge color={tc} th={th} glow>{sol?.name || config.profile}</Badge>
          {sol?.difficulty && <span style={{ fontSize: "11px", color: th.textFaint, background: th.surfaceAlt, padding: "2px 9px", borderRadius: T.rPill, border: `1px solid ${th.border}` }}>{sol.difficulty}</span>}
          <span style={{ fontSize: "10px", color: th.textFaint, marginLeft: "auto", fontFamily: "'DM Mono', monospace" }}>Score {config.configScore}%</span>
        </div>
        <h1 style={{ fontSize: "28px", fontWeight: 800, color: th.text, lineHeight: 1.18, margin: "0 0 12px", letterSpacing: "-0.02em" }}>
          {sol?.interpretation?.shortSummary || cd.goal}
        </h1>
        {sol?.description && <p style={{ fontSize: "14px", color: th.textSub, lineHeight: "1.78", margin: 0, fontStyle: "italic", maxWidth: "520px" }}>{sol.description}</p>}
      </div>
      {sol?.interpretation && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1px", background: th.border, borderRadius: T.r, overflow: "hidden", border: `1px solid ${th.border}` }}>
          {[
            { label: "Potentiel théorique", val: `${sol.interpretation.theoreticalPromise ?? "—"}%`, color: th.text },
            { label: "Écart de réalité",    val: gap != null ? `−${gap}%` : "—", color: gap != null && gap < 20 ? th.amber : th.danger },
            { label: "Probabilité réelle",  val: `${sol.interpretation.realWorldProbability ?? "—"}%`, color: tc },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ padding: "18px 20px", background: th.surface }}>
              <p style={{ fontSize: "10px", color: th.textFaint, margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</p>
              <p style={{ fontSize: "28px", fontWeight: 800, color, fontVariantNumeric: "tabular-nums", margin: 0, fontFamily: "'DM Mono', monospace", letterSpacing: "-0.02em" }}>{val}</p>
            </div>
          ))}
        </div>
      )}
      {audienceProfile && cd.steps?.length > 0 && (() => {
        // Chercher la note du dernier step non-diag
        const lastStep = [...cd.steps].reverse()
          .find(s => s.audienceNote?.[audienceProfile.id]);
        const note = lastStep?.audienceNote?.[audienceProfile.id];
        if (!note) return null;
        return (
          <div style={{
            padding: "14px 18px",
            borderRadius: T.rSm,
            background: `${tc}06`,
            border: `1px solid ${tc}20`,
            borderLeft: `3px solid ${tc}40`,
          }}>
            <p style={{
              fontSize: "10px", fontWeight: 700,
              color: tc, margin: "0 0 7px",
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              fontFamily: "'DM Mono', monospace",
            }}>
              Lecture {audienceProfile.label}
            </p>
            <p style={{
              fontSize: "13px", color: th.textSub,
              lineHeight: "1.72", margin: 0,
              fontStyle: "italic",
            }}>
              {note}
            </p>
          </div>
        );
      })()}
      {(sol?.interpretation?.keyInsight || sol?.interpretation?.pedagogicalMessage) && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          {sol.interpretation.keyInsight && (
            <div style={{ padding: "16px 18px", borderRadius: T.rSm, border: `1px solid ${th.amber}25`, background: `linear-gradient(135deg, ${th.amber}08, ${th.amber}04)` }}>
              <p style={{ fontSize: "10px", fontWeight: 700, color: th.amber, margin: "0 0 9px", textTransform: "uppercase", letterSpacing: "0.08em" }}>Insight clé</p>
              <p style={{ fontSize: "13px", color: th.text, lineHeight: "1.72", fontStyle: "italic", margin: 0 }}>"{sol.interpretation.keyInsight}"</p>
            </div>
          )}
          {sol.interpretation.pedagogicalMessage && (
            <div style={{ padding: "16px 18px", borderRadius: T.rSm, border: `1px solid ${th.border}`, background: th.surfaceAlt }}>
              <p style={{ fontSize: "10px", fontWeight: 700, color: th.textFaint, margin: "0 0 9px", textTransform: "uppercase", letterSpacing: "0.08em" }}>Lecture pédagogique</p>
              <p style={{ fontSize: "13px", color: th.textSub, lineHeight: "1.72", margin: 0 }}>{sol.interpretation.pedagogicalMessage}</p>
            </div>
          )}
        </div>
      )}
      {config.isZealot && (
        <div style={{
          padding: "12px 16px",
          borderRadius: T.rSm,
          background: th.amberDim,
          border: `1px solid ${th.amber}25`,
          borderLeft: `3px solid ${th.amber}`,
          display: "flex",
          gap: "10px",
          alignItems: "flex-start",
        }}>
          <span style={{ fontSize: "14px", flexShrink: 0 }}>⚡</span>
          <div>
            <p style={{ fontSize: "12px", fontWeight: 700, color: th.amber, margin: "0 0 4px" }}>
              Mode exploration détecté
            </p>
            <p style={{ fontSize: "12px", color: th.textSub, margin: 0, lineHeight: "1.65" }}>
              Vous avez activé la majorité des options disponibles. 
              Ce débrief reflète une configuration large — relancez le parcours 
              en ciblant les leviers qui correspondent à votre situation réelle 
              pour obtenir un profil plus précis.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function DebriefMetrics({ eng, cd, th }) {
  const sm = buildStatMeta(cd);
  return (
    <div>
      <h3 style={{ fontSize: "17px", fontWeight: 700, color: th.text, marginBottom: "20px", letterSpacing: "-0.01em" }}>Évolution des indicateurs</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: "10px" }}>
        {Object.entries(eng.params).map(([k, v]) => {
          const init  = eng.initialParams[k] ?? 50;
          const delta = v - init;
          const meta  = sm[k];
          if (!meta) return null;
          const color = v >= 70 ? th.amber : v >= 40 ? th.sky : th.danger;
          return (
            <div key={k} style={{ padding: "16px", borderRadius: T.rSm, background: th.surface, border: `1px solid ${th.border}`, transition: "border-color 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = th.borderMed}
              onMouseLeave={e => e.currentTarget.style.borderColor = th.border}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px" }}>
                <span style={{ fontSize: "14px", color: th.textFaint }}>{meta.icon}</span>
                <span style={{ fontSize: "11px", fontWeight: 600, color: th.textSub }}>{meta.label}</span>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: "6px", marginBottom: "10px" }}>
                <span style={{ fontSize: "30px", fontWeight: 800, color: th.text, fontFamily: "'DM Mono', monospace", letterSpacing: "-0.02em" }}>{v}</span>
                {delta !== 0 && <span style={{ fontSize: "12px", fontWeight: 700, color: delta > 0 ? th.amber : th.danger, background: delta > 0 ? th.amberDim : th.dangerDim, padding: "1px 6px", borderRadius: T.rPill }}>{delta > 0 ? "+" : ""}{delta}</span>}
              </div>
              <div style={{ height: "3px", background: `${color}14`, borderRadius: "2px", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${v}%`, background: `linear-gradient(90deg, ${color}70, ${color})`, transition: "width 0.7s", borderRadius: "2px" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "5px" }}>
                <span style={{ fontSize: "9px", color: th.textFaint }}>Départ : {init}</span>
                <span style={{ fontSize: "9px", color: th.textFaint }}>/ 100</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DebriefParcours({ eng, cd, th }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "26px" }}>
      <h3 style={{ fontSize: "17px", fontWeight: 700, color: th.text, letterSpacing: "-0.01em" }}>Reconstruction du parcours</h3>
      {cd.steps.map((step, si) => {
        const stepActions = (step.actions || []).filter(a => eng.completed.has(a.id));
        return (
          <div key={si} style={{ opacity: stepActions.length ? 1 : 0.4, transition: "opacity 0.3s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: stepActions.length ? th.skyDim : th.surfaceAlt, border: `1.5px solid ${stepActions.length ? th.sky : th.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "11px", fontWeight: 700, color: stepActions.length ? th.sky : th.textFaint }}>
                {si + 1}
              </div>
              <div>
                <p style={{ fontSize: "14px", fontWeight: 700, color: th.text, margin: 0, letterSpacing: "-0.01em" }}>{step.title}</p>
                {step.narrativeOverride && <p style={{ fontSize: "10px", color: th.sky, margin: 0, fontFamily: "'DM Mono', monospace", marginTop: "2px" }}>{step.narrativeOverride}</p>}
              </div>
            </div>
            {stepActions.length > 0 ? (
              <div style={{ marginLeft: "38px", display: "flex", flexDirection: "column", gap: "6px" }}>
                {stepActions.map(act => (
                  <div key={act.id} style={{ padding: "11px 14px", borderRadius: T.rXs, background: th.surface, border: `1px solid ${th.border}`, borderLeft: `2px solid ${th.sky}30` }}>
                    <p style={{ fontSize: "13px", color: th.textSub, margin: "0 0 4px", lineHeight: 1.5 }}>{act.label}</p>
                    {act.feedback && <p style={{ fontSize: "11px", color: th.sky, margin: 0, lineHeight: 1.5 }}>{act.feedback}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ marginLeft: "38px", fontSize: "12px", color: th.textFaint, fontStyle: "italic" }}>Aucune action activée à cette étape.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DebriefSignaux({ config, th }) {
  if (!config.diagSignals?.length) return <p style={{ fontSize: "13px", color: th.textSub }}>Aucun signal diagnostique enregistré.</p>;
  return (
    <div>
      <h3 style={{ fontSize: "17px", fontWeight: 700, color: th.text, marginBottom: "8px", letterSpacing: "-0.01em" }}>Signaux diagnostiques détectés</h3>
      <p style={{ fontSize: "13px", color: th.textSub, lineHeight: "1.72", marginBottom: "22px" }}>Ces signaux décrivent votre point de départ — les tensions ou freins identifiés lors du diagnostic initial.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {config.diagSignals.map((sig, i) => (
          <div key={i} style={{ padding: "14px 16px", borderRadius: T.rSm, background: th.surface, border: `1px solid ${th.border}`, borderLeft: `3px solid ${th.danger}35` }}>
            <div style={{ display: "flex", gap: "12px" }}>
              <span style={{ fontSize: "15px", flexShrink: 0, marginTop: "1px", color: th.danger }}>△</span>
              <div>
                <p style={{ fontSize: "13px", color: th.text, margin: "0 0 5px", lineHeight: 1.55, fontWeight: 450 }}>{sig.label}</p>
                {sig.feedback && <p style={{ fontSize: "12px", color: th.sky, margin: 0, lineHeight: 1.6 }}>{sig.feedback}</p>}
                {sig.deepFeedback?.whatItCosts && <p style={{ fontSize: "12px", color: th.textSub, margin: "6px 0 0", lineHeight: 1.6, fontStyle: "italic" }}>{sig.deepFeedback.whatItCosts}</p>}
                {sig.competencySignal && <div style={{ marginTop: "8px" }}><Badge color={th.danger} th={th} sm>{sig.competencySignal.level}</Badge></div>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DebriefTheorie({ sol, cd, th }) {
  return (
    <div>
      <h3 style={{ fontSize: "17px", fontWeight: 700, color: th.text, marginBottom: "20px", letterSpacing: "-0.01em" }}>Éclairage théorique</h3>
      {sol?.interpretation?.theoreticalFrameworks?.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {sol.interpretation.theoreticalFrameworks.map((fw, i) => (
            <div key={i} style={{ padding: "15px 17px", borderRadius: T.rSm, background: th.surface, border: `1px solid ${th.border}` }}>
              <p style={{ fontSize: "14px", fontWeight: 700, color: th.text, margin: "0 0 7px", letterSpacing: "-0.01em" }}>{fw.name || fw}</p>
              {fw.description && <p style={{ fontSize: "13px", color: th.textSub, margin: 0, lineHeight: 1.72 }}>{fw.description}</p>}
            </div>
          ))}
          
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {cd.steps.filter(s => s.epistemologicalQuestion).map((step, si) => (
            <div key={si} style={{ padding: "14px 16px", borderRadius: T.rSm, background: th.surface, border: `1px solid ${th.sky}18`, borderLeft: `3px solid ${th.sky}40` }}>
              <p style={{ fontSize: "10px", fontWeight: 700, color: th.sky, margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.08em" }}>{step.title}</p>
              <p style={{ fontSize: "13px", color: th.textSub, fontStyle: "italic", lineHeight: 1.72, margin: 0 }}>{step.epistemologicalQuestion}</p>
            </div>
          ))}
          {cd.simulationEngine?.causality?.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <p style={{ fontSize: "10px", fontWeight: 700, color: th.textFaint,
                marginBottom: "10px", textTransform: "uppercase",
                letterSpacing: "0.09em" }}>
                Causalités du système
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                {cd.simulationEngine.causality
                  .filter(c => c.type === "positive")  // garder les positives en débrief
                  .map((c, i) => (
                    <div key={i} style={{
                      padding: "10px 14px",
                      borderRadius: T.rXs,
                      background: th.surface,
                      border: `1px solid ${th.border}`,
                      display: "flex", gap: "10px", alignItems: "flex-start",
                    }}>
                      <span style={{
                        fontSize: "10px", fontWeight: 700,
                        color: th.amber, flexShrink: 0,
                        fontFamily: "'DM Mono', monospace",
                        marginTop: "1px",
                      }}>
                        {Math.round(c.weight * 100)}%
                      </span>
                      <div>
                        <span style={{ fontSize: "12px", color: th.textSub }}>
                          {c.cause}
                        </span>
                        <span style={{ fontSize: "12px", color: th.textFaint,
                          margin: "0 5px" }}>→</span>
                        <span style={{ fontSize: "12px", color: th.text }}>
                          {c.effect}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
          {!cd.steps.some(s => s.epistemologicalQuestion) && <p style={{ fontSize: "13px", color: th.textSub, fontStyle: "italic" }}>Aucun cadre théorique enregistré dans ce template.</p>}
          
        </div>
      )}
    </div>
  );
}

function DebriefDecisions({ eng, cd, config, th }) {
  const { diagIds, profileIds } = buildActionSets(cd);
  const allFeature = (cd.steps || []).flatMap(s => s.actions || []).filter(a => !diagIds.has(a.id) && !profileIds.has(a.id));
  const done   = allFeature.filter(a => eng.completed.has(a.id));
  const undone = allFeature.filter(a => !eng.completed.has(a.id));
  return (
    <div>
      <h3 style={{ fontSize: "17px", fontWeight: 700, color: th.text, marginBottom: "10px", letterSpacing: "-0.01em" }}>Analyse décisionnelle</h3>
      <p style={{ fontSize: "13px", color: th.textSub, lineHeight: "1.72", marginBottom: "22px" }}>
        {done.length} action{done.length !== 1 ? "s" : ""} activée{done.length !== 1 ? "s" : ""} sur {allFeature.length} disponibles — score de configuration : <strong style={{ color: th.text }}>{config.configScore}%</strong>.
      </p>
      {done.length > 0 && (
        <div style={{ marginBottom: "22px" }}>
          <p style={{ fontSize: "10px", fontWeight: 700, color: th.emerald, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.09em" }}>Leviers activés</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
            {done.map(act => (
              <div key={act.id} style={{ padding: "12px 15px", borderRadius: T.rXs, background: `${th.emerald}07`, border: `1px solid ${th.emerald}18`, display: "flex", gap: "11px", borderLeft: `3px solid ${th.emerald}40` }}>
                <span style={{ color: th.emerald, flexShrink: 0, fontWeight: 700, fontSize: "14px" }}>✓</span>
                <div>
                  <p style={{ fontSize: "13px", color: th.text, margin: "0 0 3px", lineHeight: 1.5 }}>{act.label}</p>
                  {act.deepFeedback?.whatHappened && <p style={{ fontSize: "12px", color: th.textSub, margin: 0, lineHeight: 1.6 }}>{act.deepFeedback.whatHappened}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {undone.length > 0 && (
        <div>
          <p style={{ fontSize: "10px", fontWeight: 700, color: th.textFaint, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.09em" }}>Non activés</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            {undone.map(act => (
              <div key={act.id} style={{ padding: "9px 14px", borderRadius: T.rXs, background: th.surfaceAlt, border: `1px solid ${th.border}`, display: "flex", gap: "10px", opacity: 0.55 }}>
                <span style={{ color: th.textFaint, flexShrink: 0 }}>○</span>
                <p style={{ fontSize: "12px", color: th.textSub, margin: 0, lineHeight: 1.5 }}>{act.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DebriefTransfert({ sol, cd, th }) {
  const content = sol?.interpretation?.transferability || sol?.interpretation?.recommendations;
  return (
    <div>
      <h3 style={{ fontSize: "17px", fontWeight: 700, color: th.text, marginBottom: "20px", letterSpacing: "-0.01em" }}>Transférabilité</h3>
      {Array.isArray(content) && content.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {content.map((item, i) => (
            <div key={i} style={{ padding: "14px 17px", borderRadius: T.rSm, background: th.surface, border: `1px solid ${th.border}`, display: "flex", gap: "11px" }}>
              <span style={{ color: th.sky, flexShrink: 0, fontSize: "15px" }}>◉</span>
              <p style={{ fontSize: "13px", color: th.textSub, margin: 0, lineHeight: 1.72 }}>{item}</p>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {(cd.counterfactuals || []).map((cf, i) => (
            <div key={i} style={{ padding: "14px 17px", borderRadius: T.rSm, background: th.surface, border: `1px solid ${th.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
                <p style={{ fontSize: "13px", fontWeight: 500, color: th.text, margin: 0, flex: 1, marginRight: "10px" }}>{cf.actionTitle}</p>
                <span style={{ fontSize: "13px", fontWeight: 700, color: th.amber, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>+{Math.round(cf.deltaProbability * 100)}%</span>
              </div>
              <p style={{ fontSize: "12px", color: th.textSub, margin: 0, lineHeight: 1.68 }}>{cf.description}</p>
            </div>
          ))}
          {!cd.counterfactuals?.length && <p style={{ fontSize: "13px", color: th.textSub, fontStyle: "italic", lineHeight: 1.72 }}>Les compétences développées dans ce parcours sont transférables dans d'autres contextes similaires.</p>}
        </div>
      )}
    </div>
  );
}

function DebriefNext({ config, cd, eng, onComplete, onRestart, onReturnToLanding, th }) {
  const [state, setState] = useState("idle");
  const { isUnlocked }    = useAtelierUnlock();
  const toUnlockIds = TEMPLATE_TO_ATELIERS[cd.slug] || [];
  const toUnlock    = ATELIERS_LIBRARY.filter(a => toUnlockIds.includes(a.id));
  const newUnlocks  = toUnlock.filter(a => !isUnlocked(a.id));
  const missed = (cd.counterfactuals || []).filter(cf => !eng.completed.has(cf.actionId));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      {toUnlock.length > 0 && (
        <div style={{ padding: "20px 22px", borderRadius: T.r, background: `linear-gradient(135deg, rgba(34,211,238,0.06), rgba(34,211,238,0.02))`, border: `1px solid rgba(34,211,238,0.2)`, boxShadow: `inset 0 1px 0 rgba(34,211,238,0.1)` }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
            <Sparkles size={14} color="#22d3ee" />
            <p style={{ fontSize: "12px", fontWeight: 700, color: "#22d3ee", margin: 0, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              {newUnlocks.length > 0 ? `${newUnlocks.length} atelier${newUnlocks.length > 1 ? 's' : ''} débloqué${newUnlocks.length > 1 ? 's' : ''}` : 'Ateliers associés'}
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
            {toUnlock.map((atelier) => {
              const unlocked = isUnlocked(atelier.id);
              const isNew    = newUnlocks.some(a => a.id === atelier.id);
              return (
                <div key={atelier.id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 14px", background: isNew ? `${atelier.accentColor}08` : "rgba(255,255,255,0.02)", border: `1px solid ${isNew ? `${atelier.accentColor}22` : "rgba(255,255,255,0.06)"}`, borderRadius: T.rXs, opacity: unlocked && !isNew ? 0.6 : 1 }}>
                  <span style={{ fontSize: "18px", flexShrink: 0 }}>{atelier.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: "13px", fontWeight: 600, color: isNew ? th.text : th.textSub, margin: 0 }}>{atelier.title}</p>
                    <p style={{ fontSize: "10px", color: th.textFaint, margin: "2px 0 0", fontFamily: "'DM Mono', monospace" }}>{atelier.subtitle}</p>
                  </div>
                  {unlocked && !isNew ? (
                    <span style={{ fontSize: "10px", color: "#10b981", background: "rgba(16,185,129,0.08)", padding: "2px 8px", borderRadius: "20px", border: "1px solid rgba(16,185,129,0.2)", flexShrink: 0 }}>Déjà débloqué</span>
                  ) : isNew ? (
                    <span style={{ fontSize: "10px", color: atelier.accentColor, background: `${atelier.accentColor}10`, padding: "2px 8px", borderRadius: "20px", border: `1px solid ${atelier.accentColor}25`, flexShrink: 0, fontWeight: 700 }}>✦ Nouveau</span>
                  ) : (
                    <Lock size={11} color={th.textFaint} style={{ flexShrink: 0 }} />
                  )}
                </div>
              );
            })}
          </div>
          <button onClick={async () => { await onComplete(config); }} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 16px", background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.25)", color: "#22d3ee", fontSize: "11px", fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase", transition: "all 0.15s", borderRadius: T.rXs }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(34,211,238,0.14)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(34,211,238,0.08)"}>
            Voir mes ateliers <ArrowRight size={11} />
          </button>
        </div>
      )}

      <div style={{ padding: "22px 24px", borderRadius: T.r, background: `linear-gradient(135deg, ${th.orange}10, ${th.orange}05)`, border: `1px solid ${th.orange}20`, boxShadow: `inset 0 1px 0 ${th.orange}12` }}>
        <h4 style={{ fontSize: "17px", fontWeight: 700, color: th.text, margin: "0 0 9px", letterSpacing: "-0.01em" }}>La suite vous attend</h4>
        <p style={{ fontSize: "13px", color: th.textSub, lineHeight: "1.72", margin: "0 0 18px" }}>Votre configuration est enregistrée. Accédez au tableau de bord pour la prochaine étape.</p>
        <button onClick={async () => { setState("loading"); try { await onComplete(config); } catch {} setState("done"); }} disabled={state !== "idle"}
          style={{ padding: "12px 22px", borderRadius: T.rSm, border: "none", background: state === "done" ? th.amberDim : `linear-gradient(135deg, ${th.orange}, ${th.orange}cc)`, color: state === "done" ? th.amber : "#fff", fontSize: "14px", fontWeight: 700, cursor: state !== "idle" ? "default" : "pointer", transition: "all 0.2s", letterSpacing: "-0.01em", boxShadow: state === "idle" ? `0 4px 20px ${th.orange}30` : "none" }}>
          {state === "loading" ? "Chargement…" : state === "done" ? "✓ Enregistré" : "Accéder au tableau de bord →"}
        </button>
      </div>

      {/* Rejouer un autre cas */}
      <div style={{ padding: "16px 18px", borderRadius: T.rSm, background: th.surface, border: `1px solid ${th.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
        <div>
          <p style={{ fontSize: "13px", fontWeight: 600, color: th.text, margin: "0 0 4px" }}>Explorer un autre cas ?</p>
          <p style={{ fontSize: "12px", color: th.textSub, margin: 0 }}>Retournez à la sélection pour choisir un nouveau territoire.</p>
        </div>
        <button onClick={onReturnToLanding} style={{ padding: "9px 16px", borderRadius: T.rSm, background: th.surfaceAlt, border: `1px solid ${th.border}`, color: th.textSub, fontSize: "12px", fontWeight: 600, cursor: "pointer", transition: "all .14s", whiteSpace: "nowrap", flexShrink: 0 }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = th.orange; e.currentTarget.style.color = th.orange; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = th.border; e.currentTarget.style.color = th.textSub; }}>
          ⊞ Choisir un cas →
        </button>
      </div>

      {missed.length > 0 && (
        <div>
          <h4 style={{ fontSize: "14px", fontWeight: 600, color: th.text, marginBottom: "12px" }}>À explorer lors de la prochaine session</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
            {missed.map((cf, i) => (
              <div key={i} style={{ padding: "10px 14px", borderRadius: T.rXs, border: `1px solid ${th.border}`, background: th.surface, display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: th.textFaint, flexShrink: 0, marginTop: "5px" }} />
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: "13px", color: th.textSub }}>{cf.actionTitle}</span>
                  {cf.description && <p style={{ fontSize: "12px", color: th.textFaint, margin: "3px 0 0", lineHeight: 1.55 }}>{cf.description}</p>}
                </div>
                <span style={{ fontSize: "12px", fontWeight: 700, color: th.amber, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>+{Math.round(cf.deltaProbability * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "8px" }}>
        <button onClick={onRestart} style={{ padding: "9px 16px", borderRadius: T.rSm, background: "none", border: `1px solid ${th.border}`, color: th.textFaint, fontSize: "12px", cursor: "pointer", transition: "all 0.14s" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = th.borderHov; e.currentTarget.style.color = th.textSub; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = th.border; e.currentTarget.style.color = th.textFaint; }}>
          ↺ Recommencer ce cas
        </button>
      </div>
    </div>
  );
}

function DebriefMain({ config, sol, cd, eng, th, accent, onComplete, onRestart, onReturnToLanding, user, userPlan, audienceProfile }) {
  const sections  = DEBRIEF_SECTIONS[config.debriefType] || DEBRIEF_SECTIONS.starter;
  const [active, setActive] = useState(sections[0]);
  const ref = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => { setActive(sections[0]); }, [config.debriefType]); // eslint-disable-line
  useEffect(() => { ref.current?.scrollTo({ top: 0, behavior: "smooth" }); }, [active]);
  const curIdx = sections.indexOf(active);
  const tc = sol?.interpretation?.tone ? (TONE_COLOR[sol.interpretation.tone] || th.orange) : th.orange;
  const P  = { config, sol, cd, eng, th, onComplete, onRestart, onReturnToLanding };

  const renderSection = id => {
    switch (id) {
      case "overview":   return <DebriefOverview   {...P} audienceProfile={audienceProfile} />;
      case "metrics":    return <DebriefMetrics    eng={eng} cd={cd} th={th} />;
      case "parcours":   return <DebriefParcours   eng={eng} cd={cd} th={th} />;
      case "signaux":    return <DebriefSignaux    config={config} th={th} />;
      case "theorie":    return <DebriefTheorie    sol={sol} cd={cd} th={th} />;
      case "decisions":  return <DebriefDecisions  eng={eng} cd={cd} config={config} th={th} />;
      case "transfert":  return <DebriefTransfert  sol={sol} cd={cd} th={th} />;
      case "next":       return <DebriefNext       {...P} />;
      default: return null;
    }
  };

  if (isMobile) {
    return (
      <div style={{ minHeight: "100vh", background: th.bg, paddingBottom: "80px" }}>
        {/* Header mobile */}
        <div style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: th.surfaceGlass,
          backdropFilter: "blur(12px)",
          padding: "14px 16px",
          borderBottom: `1px solid ${th.border}`,
          display: "flex",
          alignItems: "center",
          gap: "12px"
        }}>
          <button 
            onClick={() => curIdx > 0 && setActive(sections[curIdx - 1])}
            disabled={curIdx === 0}
            style={{ background: "none", border: "none", color: curIdx > 0 ? th.textSub : th.textFaint, fontSize: "20px", padding: "4px" }}
          >
            ←
          </button>

          <div style={{ flex: 1, textAlign: "center" }}>
            <p style={{ fontSize: "13px", fontWeight: 700, color: tc, margin: 0 }}>{SECTION_META[active]?.label}</p>
            <p style={{ fontSize: "10px", color: th.textFaint, margin: "2px 0 0" }}>
              {curIdx + 1} / {sections.length}
            </p>
          </div>

          <button 
            onClick={() => curIdx < sections.length - 1 && setActive(sections[curIdx + 1])}
            disabled={curIdx === sections.length - 1}
            style={{ background: "none", border: "none", color: curIdx < sections.length - 1 ? th.textSub : th.textFaint, fontSize: "20px", padding: "4px" }}
          >
            →
          </button>
        </div>

        {/* Contenu scrollable */}
        <div ref={ref} style={{ padding: "20px 16px", minHeight: "calc(100vh - 70px)" }}>
          <div key={active} style={{ animation: "ca-fadeUp 0.25s ease-out both" }}>
            {renderSection(active)}
          </div>
        </div>

        {/* Navigation en bas (dots) */}
        <div style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: th.surfaceGlass,
          backdropFilter: "blur(12px)",
          borderTop: `1px solid ${th.border}`,
          padding: "12px 16px",
          display: "flex",
          justifyContent: "center",
          gap: "6px",
          zIndex: 110
        }}>
          {sections.map((id, idx) => (
            <button
              key={id}
              onClick={() => setActive(id)}
              style={{
                width: idx === curIdx ? "28px" : "8px",
                height: "8px",
                borderRadius: "999px",
                background: idx === curIdx ? tc : th.border,
                border: "none",
                transition: "all 0.3s ease"
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: "calc(100vh - 54px)", background: th.bg, borderRadius: T.r, overflow: "hidden", boxShadow: T.shadow, border: `1px solid ${th.border}` }}>
      <aside style={{ width: "190px", flexShrink: 0, background: th.surface, borderRight: `1px solid ${th.border}`, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 16px", borderBottom: `1px solid ${th.border}` }}>
          <p style={{ fontSize: "9px", fontWeight: 700, color: th.textFaint, margin: "0 0 10px", textTransform: "uppercase", letterSpacing: "0.1em" }}>Rapport final</p>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px" }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: tc, boxShadow: `0 0 6px ${tc}` }} />
            <span style={{ fontSize: "12px", fontWeight: 700, color: tc, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sol?.name?.substring(0, 22) || config.profile}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
            
            <span style={{ fontSize: "10px", color: th.textFaint }}>Score</span>
            <span style={{ fontSize: "12px", fontWeight: 800, color: tc, fontFamily: "'DM Mono', monospace" }}>{config.configScore}%</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: "10px", color: th.textFaint }}>Type</span>
            <Badge color={tc} th={th} sm>{config.debriefType}</Badge>
          </div>
          <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: `1px solid ${th.border}` }}>
            <ExportButton config={config} sol={sol} cd={cd} eng={eng} th={th} userPlan={userPlan} user={user} />
          </div>
        </div>
        <nav style={{ flex: 1, padding: "8px 6px", display: "flex", flexDirection: "column", gap: "2px", overflowY: "auto" }}>
          {sections.map((id) => {
            const isA = id === active;
            const m   = SECTION_META[id];
            return (
              <button key={id} onClick={() => setActive(id)}
                style={{ width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: T.rSm, border: "none", background: isA ? `${tc}12` : "none", borderLeft: `2px solid ${isA ? tc : "transparent"}`, color: isA ? tc : th.textSub, fontSize: "12px", fontWeight: isA ? 700 : 400, cursor: "pointer", transition: "all 0.12s", display: "flex", alignItems: "center", gap: "7px" }}
                onMouseEnter={e => { if (!isA) e.currentTarget.style.background = th.surfaceHov; }}
                onMouseLeave={e => { if (!isA) e.currentTarget.style.background = "none"; }}>
                <span style={{ fontSize: "12px", opacity: isA ? 1 : 0.6 }}>{m?.icon}</span>
                <span>{m?.label}</span>
              </button>
            );
          })}
        </nav>
        <div style={{ padding: "8px 14px", borderTop: `1px solid ${th.border}`, display: "flex", gap: "4px", justifyContent: "center", alignItems: "center" }}>
          {sections.map(id => (
            <button key={id} onClick={() => setActive(id)} style={{ width: id === active ? "16px" : "5px", height: "5px", borderRadius: T.rPill, background: id === active ? tc : th.border, border: "none", cursor: "pointer", transition: "all 0.22s cubic-bezier(.4,0,.2,1)", padding: 0 }} />
          ))}
        </div>
      </aside>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ padding: "12px 24px", borderBottom: `1px solid ${th.border}`, background: th.surfaceGlass, backdropFilter: th.glassBlur, WebkitBackdropFilter: th.glassBlur, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
            <span style={{ fontSize: "16px" }}>{SECTION_META[active]?.icon}</span>
            <h2 style={{ fontSize: "14px", fontWeight: 700, color: th.text, margin: 0, letterSpacing: "-0.01em" }}>{SECTION_META[active]?.label}</h2>
          </div>
          <span style={{ fontSize: "10px", color: th.textFaint, fontFamily: "'DM Mono', monospace" }}>
            {new Date().toLocaleDateString("fr-FR")} · {config.debriefType.toUpperCase()}
          </span>
        </div>
        <div ref={ref} style={{ flex: 1, overflowY: "auto", scrollbarWidth: "thin" }}>
          <div style={{ maxWidth: "800px", margin: "0 auto", padding: "30px 28px" }}>
            <div key={active} style={{ animation: "ca-fadeUp 0.22s ease-out both" }}>{renderSection(active)}</div>
          </div>
        </div>
        <div style={{ padding: "10px 24px", borderTop: `1px solid ${th.border}`, background: th.surface, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <button onClick={() => curIdx > 0 && setActive(sections[curIdx - 1])} style={{ fontSize: "12px", fontWeight: 600, color: curIdx > 0 ? th.textSub : "transparent", background: "none", border: "none", cursor: curIdx > 0 ? "pointer" : "default", padding: "4px 0", transition: "color 0.14s" }}
            onMouseEnter={e => { if (curIdx > 0) e.currentTarget.style.color = th.text; }}
            onMouseLeave={e => { if (curIdx > 0) e.currentTarget.style.color = th.textSub; }}>
            ← Précédent
          </button>
          <span style={{ fontSize: "10px", color: th.textFaint, fontFamily: "'DM Mono', monospace" }}>{curIdx + 1} / {sections.length}</span>
          <button onClick={() => curIdx < sections.length - 1 && setActive(sections[curIdx + 1])} style={{ fontSize: "12px", fontWeight: 600, color: curIdx < sections.length - 1 ? th.textSub : "transparent", background: "none", border: "none", cursor: curIdx < sections.length - 1 ? "pointer" : "default", padding: "4px 0", transition: "color 0.14s" }}
            onMouseEnter={e => { if (curIdx < sections.length - 1) e.currentTarget.style.color = th.text; }}
            onMouseLeave={e => { if (curIdx < sections.length - 1) e.currentTarget.style.color = th.textSub; }}>
            Suivant →
          </button>
        </div>
      </div>
    </div>
  );
}

const MobileMetricsBar = ({ params, initialParams, statMeta, accent, th }) => {
  const entries = Object.entries(params);
  return (
    <div style={{ display: "flex", overflowX: "auto", gap: "8px", padding: "10px 14px", borderBottom: `1px solid ${th.border}`, scrollbarWidth: "none", WebkitOverflowScrolling: "touch", background: th.surface }} className="ca-mobile-metrics">
      <style>{`.ca-mobile-metrics::-webkit-scrollbar { display: none; }`}</style>
      {entries.map(([k, v]) => {
        const init  = initialParams[k] ?? 50;
        const delta = v - init;
        const color = v >= 70 ? th.amber : v >= 40 ? th.sky : th.danger;
        const meta  = statMeta[k];
        if (!meta) return null;
        return (
          <div key={k} style={{ flexShrink: 0, minWidth: "72px", padding: "8px 10px", borderRadius: T.rSm, background: th.surfaceAlt, border: `1px solid ${th.border}`, display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "4px" }}>
              <span style={{ fontSize: "10px", color: th.textFaint }}>{meta.icon}</span>
              {delta !== 0 && <span style={{ fontSize: "9px", fontWeight: 700, color: delta > 0 ? th.amber : th.danger }}>{delta > 0 ? "▲" : "▼"}{Math.abs(delta)}</span>}
            </div>
            <span style={{ fontSize: "18px", fontWeight: 800, color: th.text, fontFamily: "'DM Mono', monospace", letterSpacing: "-0.02em", lineHeight: 1 }}>{v}</span>
            <span style={{ fontSize: "9px", color: th.textFaint, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{meta.label}</span>
            <div style={{ height: "2px", background: `${color}18`, borderRadius: "1px" }}>
              <div style={{ height: "100%", width: `${v}%`, background: color, borderRadius: "1px", transition: "width 0.5s" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MobileStepHeader = ({ stepIdx, totalSteps, stepTitle, accent, th, config }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", background: th.surfaceGlass, backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", borderBottom: `1px solid ${th.border}`, position: "sticky", top: "54px", zIndex: 100 }}>
    <div style={{ display: "flex", gap: "3px", alignItems: "center" }}>
      {Array.from({ length: totalSteps }, (_, i) => (
        <div key={i} style={{ width: i === stepIdx ? "14px" : "5px", height: "5px", borderRadius: "3px", background: i < stepIdx ? `${th.amber}70` : i === stepIdx ? accent : `${th.textFaint}25`, transition: "all 0.3s cubic-bezier(.4,0,.2,1)", boxShadow: i === stepIdx ? `0 0 6px ${accent}70` : "none" }} />
      ))}
    </div>
    <span style={{ flex: 1, fontSize: "12px", fontWeight: 700, color: th.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: "-0.01em" }}>{stepTitle}</span>
    {config.configScore > 0 && <span style={{ fontSize: "11px", fontWeight: 800, color: th.amber, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>{config.configScore}%</span>}
  </div>
);

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [breakpoint]);
  return isMobile;
}

const AudienceNote = ({ step, profile, th, accent }) => {
  if (!profile || !step?.audienceNote) return null;
  const note = step.audienceNote[profile.id];
  if (!note) return null;

  return (
    <div style={{
      padding: "12px 16px",
      borderRadius: "6px",
      background: `${accent}06`,
      border: `1px solid ${accent}18`,
      borderLeft: `3px solid ${accent}35`,
      display: "flex",
      flexDirection: "column", // Changé de row à column sur mobile
      gap: "8px",
      alignItems: "flex-start",
      animation: "lab-fadeUp 0.2s ease-out both",
    }}>
      <div style={{
        fontSize: "9px",
        fontWeight: 700,
        color: accent,
        fontFamily: "'JetBrains Mono', monospace",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        background: `${accent}10`,
        padding: "2px 8px",
        borderRadius: "4px",
        display: "inline-block",
      }}>
        {profile.label}
      </div>
      <p style={{
        fontSize: "13px",
        color: th.textSub,
        lineHeight: "1.6",
        margin: 0,
        fontStyle: "italic",
      }}>
        {note}
      </p>
    </div>
  );
};



const STORAGE_KEY = "pesacore_guide_seen";

function getSeenZones() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function markZoneSeen(zoneId) {
  try {
    const seen = getSeenZones();
    seen[zoneId] = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
  } catch {}
}

// ── Hint flottant (premier passage) ────────────────────────────────────────
export function GuideHint({ zoneId, content, accent, th }) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const seen = getSeenZones();
    if (!seen[zoneId]) {
      timerRef.current = setTimeout(() => setVisible(true), 800);
    }
    return () => clearTimeout(timerRef.current);
  }, [zoneId]);

  const dismiss = () => {
    setVisible(false);
    markZoneSeen(zoneId);
  };

  if (!visible) return null;

  return (
    <div style={{
      position: "relative",
      padding: "12px 14px",
      borderRadius: "8px",
      background: `${accent}08`,
      border: `1px solid ${accent}25`,
      borderLeft: `3px solid ${accent}`,
      marginBottom: "12px",
      animation: "lab-fadeUp 0.28s ease-out both",
    }}>
      <div style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
      }}>
        <span style={{
          fontSize: "11px",
          color: `${accent}90`,
          fontFamily: "'JetBrains Mono', monospace",
          flexShrink: 0,
          marginTop: "1px",
        }}>⟡</span>
        <p style={{
          fontSize: "12.5px",
          color: th.textSub,
          lineHeight: "1.7",
          margin: 0,
          flex: 1,
        }}>
          {content.hint}
        </p>
        <button
          onClick={dismiss}
          style={{
            background: "none",
            border: "none",
            color: th.textFaint,
            cursor: "pointer",
            fontSize: "14px",
            padding: "0 2px",
            flexShrink: 0,
            lineHeight: 1,
          }}
          title="Ne plus afficher"
        >×</button>
      </div>
      <button
        onClick={dismiss}
        style={{
          marginTop: "10px",
          fontSize: "10px",
          color: `${accent}80`,
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          fontFamily: "'JetBrains Mono', monospace",
          display: "block",
        }}
      >
        compris, ne plus afficher →
      </button>
    </div>
  );
}

// ── Panneau guide permanent ─────────────────────────────────────────────────
export function GuidePanel({ isOpen, onClose, accent, th, guideContent, isDark }) {
  const [activeZone, setActiveZone] = useState(null);
  const [resetDone, setResetDone] = useState(false);
  

  if (!isOpen) return null;

  const zone = guideContent.zones.find(z => z.id === activeZone);
  const handleClose = () => {
    setActiveZone(null);
    onClose();
  };

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      zIndex: 1000,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "flex-end",
      padding: "54px 0 0",
      pointerEvents: "none",
    }}>
      {/* Backdrop léger */}
      <div
        onClick={handleClose}
        style={{
          position: "fixed",
          inset: 0,
          background: isDark
            ? "rgba(63, 47, 47, 0.32)"   // was 0.18
            : "rgba(0,0,0,0.25)",
          pointerEvents: "all",
          zIndex: 0,
        }}
      />
      {/* Panneau */}
      <div style={{
        position: "relative",
        zIndex: 1,
        width: "340px",
        height: "calc(100vh - 54px)",
        background: isDark ? "#081d3a" : "#ffffff",  // solide, jamais transparent
        borderLeft: `1px solid ${th.border}`,
        boxShadow: isDark
          ? "-8px 0 32px rgba(0,0,0,0.45)"
          : "-8px 0 32px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
        pointerEvents: "all",
        animation: "lab-slideRight 0.3s cubic-bezier(.34,1.2,.64,1) both",
        overflowY: "auto",
      }}>

        {/* Header */}
        <div style={{
          padding: "18px 20px 14px",
          borderBottom: `1px solid ${th.border}`,
          position: "sticky",
          top: 0,
          background: isDark ? "#010d1d" : "#ffffff",
          zIndex: 1,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <p style={{
                fontSize: "9px",
                fontWeight: 700,
                color: `${accent}`,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                margin: "0 0 4px",
                fontFamily: "'JetBrains Mono', monospace",
              }}>⟡ GUIDE DE L'EXPÉRIENCE</p>
              <p style={{
                fontSize: "13px",
                color: th.textSub,
                margin: 0,
                lineHeight: "1.5",
              }}>
                Comment lire et traverser ce run
              </p>
            </div>
            <button
              onClick={handleClose}
              style={{
                background: "none",
                border: "none",
                color: th.textFaint,
                cursor: "pointer",
                fontSize: "18px",
                padding: "4px",
                lineHeight: 1,
              }}
            >×</button>
          </div>
        </div>

        {/* Liste des zones */}
        <div style={{ padding: "14px 20px", flex: 1 }}>
          {!activeZone ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <p style={{
                fontSize: "11px",
                color: th.textFaint,
                marginBottom: "10px",
                lineHeight: "1.6",
                fontStyle: "italic",
              }}>
                Sélectionnez une zone pour comprendre comment elle fonctionne.
              </p>
              {guideContent.zones.map(z => (
                <button
                  key={z.id}
                  onClick={() => setActiveZone(z.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px 14px",
                    borderRadius: "6px",
                    background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                    border: `1px solid ${th.border}`,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "border-color 0.15s",
                    color: th.text,
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = `${accent}40`}
                  onMouseLeave={e => e.currentTarget.style.borderColor = th.border}
                >
                  <span style={{
                    fontSize: "10px",
                    color: `${accent}70`,
                    fontFamily: "'JetBrains Mono', monospace",
                    flexShrink: 0,
                  }}>⟡</span>
                  <span style={{ fontSize: "13px", fontWeight: 500 }}>{z.label}</span>
                  <span style={{
                    marginLeft: "auto",
                    fontSize: "14px",
                    color: th.textFaint,
                  }}>→</span>
                </button>
              ))}
            </div>
          ) : (
            <div style={{ animation: "lab-fadeUp 0.2s ease-out both" }}>
              <button
                onClick={() => setActiveZone(null)}
                style={{
                  background: "none",
                  border: "none",
                  color: th.textFaint,
                  cursor: "pointer",
                  fontSize: "11px",
                  padding: "0 0 14px",
                  fontFamily: "'JetBrains Mono', monospace",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >← retour</button>

              <p style={{
                fontSize: "9px",
                fontWeight: 700,
                color: `${accent}`,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                margin: "0 0 8px",
                fontFamily: "'JetBrains Mono', monospace",
              }}>⟡ {zone?.label}</p>

              <p style={{
                fontSize: "13.5px",
                color: th.text,
                lineHeight: "1.78",
                marginBottom: "16px",
              }}>
                {zone?.full}
              </p>

              <div style={{
                padding: "11px 13px",
                borderRadius: "6px",
                background: `${accent}06`,
                borderLeft: `2px solid ${accent}`,
              }}>
                <p style={{
                  fontSize: "11.5px",
                  color: th.textSub,
                  lineHeight: "1.7",
                  margin: 0,
                  fontStyle: "italic",
                }}>
                  {zone?.hint}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 20px",
          borderTop: `1px solid ${th.border}`,
          position: "sticky",
          bottom: 0,
          background: isDark ? "#010d1d" : "#ffffff",
        }}>
          <button
            onClick={() => {
              localStorage.removeItem(STORAGE_KEY);
              setResetDone(true);
              setTimeout(() => setResetDone(false), 2000);
            }}
            style={{
              fontSize: "10px",
              color: resetDone ? accent : th.textFaint,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              fontFamily: "'JetBrains Mono', monospace",
              transition: "color 0.2s",
            }}
          >
            {resetDone ? "✓ hints réinitialisés" : "↺ réinitialiser les hints"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bouton d'accès permanent (à placer dans le Header) ──────────────────────
export function GuideButton({ onClick, accent, th }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        padding: "5px 11px",
        borderRadius: "5px",
        background: "none",
        border: `1px solid ${th.border}`,
        cursor: "pointer",
        color: th.textSub,
        fontSize: "11px",
        fontFamily: "'JetBrains Mono', monospace",
        transition: "all 0.15s",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = `${accent}40`;
        e.currentTarget.style.color = accent;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = th.border;
        e.currentTarget.style.color = th.textSub;
      }}
      title="Guide de l'expérience"
    >
      <span style={{ fontSize: "12px" }}>?</span>
      <span>Guide</span>
    </button>
  );
}

export default function CentreAjustement({ initialCaseData = null, themeMode = "dark", onComplete = null, dashboardUrl = "/" }) {
  const templates = useMemo(() => ALL_TEMPLATES, []);
  const { user }  = useAuth();
  const { unlockFromRun, pendingUnlock, clearPending } = useAtelierUnlock();
  const { incrementRun, isUnlimited } = useRunUsage(user);


  const userPlan  = user?.subscription?.plan ?? "freemium";

  // ── État de navigation : landing → parcours → débrief ──────────────────
  const [launched, setLaunched] = useState(!!initialCaseData);
  const [cd, setCd]             = useState(initialCaseData);

  const [eng, dispatch]      = useReducer(engineReducer, null, () =>
    buildEngine(initialCaseData || ALL_TEMPLATES[0])
  );
  const [fbId, setFb]        = useState(null);
  const [hl, setHL]          = useState(new Set());
  const [isDark, setDark]    = useState(themeMode === "dark");
  const [fontIdx, setFont]   = useState(0);
  const [editorOpen, setEd]  = useState(false);
  const [transKey, setTransKey] = useState(0);
  const [audienceProfile, setAudienceProfile] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);

  const mobileFbRef = useRef(null);
  const isMobile = useIsMobile();

  const th      = isDark ? T : TL;
  const canEdit = ['academia','entreprise','pro'].includes(userPlan);
  const FS      = [1, 1.13, 1.28][fontIdx] || 1;

  const activeCd = cd || ALL_TEMPLATES[0];

  const familyColors            = useMemo(() => buildFamilyColors(activeCd), [activeCd]);
  const { profileIds }          = useMemo(() => buildActionSets(activeCd), [activeCd]);
  const statMeta                = useMemo(() => buildStatMeta(activeCd), [activeCd]);

  const step        = activeCd.steps[eng.stepIdx];
  const phase       = step?.phases?.[eng.phaseIdx];
  const accentKey   = AMBIANCE_ACCENT[phase?.ambiance] || AMBIANCE_ACCENT.default;
  const accent      = resolveAccent(accentKey, th);
  const isAction    = phase?.type === "action";
  const isNarrative = !isAction;
  const isFinished  = eng.finished;

  const config = useMemo(() => buildConfig(eng.completed, eng.params, activeCd), [eng.completed, eng.params, activeCd]);
  const sol    = useMemo(() => isFinished ? matchSolution(activeCd, eng.completed) : null, [isFinished, eng.completed, activeCd]);
  const phasePill = phase.title;

  

  useEffect(() => {
    if (launched) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant"
      });
    }
  }, [launched, eng.stepIdx, eng.phaseIdx, isFinished]);

  const available = useMemo(() => {
    if (!isAction || !phase?.actionIds) return [];
    return phase.actionIds
      .map(id => step?.actions?.find(a => a.id === id))
      .filter(Boolean)
      .filter(a => !eng.completed.has(a.id))
      .filter(a => (a.prerequisites || []).every(p => eng.completed.has(p)));
  }, [isAction, phase, step, eng.completed]);

  const done = useMemo(() => {
    if (!phase?.actionIds) return [];
    return phase.actionIds
      .map(id => step?.actions?.find(a => a.id === id))
      .filter(Boolean)
      .filter(a => eng.completed.has(a.id));
  }, [phase, step, eng.completed]);

  const isFinalStep = phase?.actionIds?.some(id => profileIds.has(id)) || false;
  const hasChosen   = [...profileIds].some(id => eng.completed.has(id));
  const canAdvance  = !isFinalStep || hasChosen;

  const fbAction = fbId ? step?.actions?.find(a => a.id === fbId) || null : null;
  const timerDur = isNarrative && (phase?.duration || 0) > 0 ? phase.duration : 0;
  const remaining = useCountdown(timerDur, () => dispatch({ type: "ADVANCE", cd: activeCd }), !!timerDur);

  

  const handleLaunch = useCallback(async (template, selectedProfile = null) => {
  // === INCRÉMENTATION DU COMPTEUR ICI ===
  if (user && !isUnlimited) {
    // Mise à jour locale immédiate via le hook
    incrementRun();
    
    // Persistance dans la base (important)
    try {
      await api.patch("/users/me", {
        usage: {
          runs: {
            countThisMonth: (user.usage?.runs?.countThisMonth ?? 0) + 1,
            lastResetDate: new Date().toISOString()
          }
        }
      });
    } catch (err) {
      console.error("Erreur lors de l'incrémentation du compteur de runs :", err);
      // On continue même si la persistance échoue
    }
  }

  // === Lancement du run ===
  setCd(template);
  dispatch({ type: "RESTART", cd: template });
  setFb(null);
  setHL(new Set());
  setAudienceProfile(selectedProfile);
  setLaunched(true);

}, [user, isUnlimited, incrementRun]);

  const handleReturnToLanding = useCallback(() => {
    setLaunched(false);
    setCd(null);
    setFb(null);
    setHL(new Set());
    setEd(false);
    setAudienceProfile(null);
  }, []);

  const handleTemplate = useCallback(t => {
    setCd(t);
    dispatch({ type: "RESTART", cd: t });
    setFb(null);
    setHL(new Set());
  }, []);

  const handleEditorUpdate = useCallback(nd => {
    setCd(nd);
    dispatch({ type: "RESTART", cd: nd });
    setFb(null);
    setHL(new Set());
  }, []);

  const handleAction = useCallback(id => {
    const act = step?.actions?.find(a => a.id === id);
    if (!act) return;
    dispatch({ type: "APPLY_ACTION", cd: activeCd, id });
    setFb(id);
    if (act.highlightFamilies?.length) {
      setHL(new Set(act.highlightFamilies));
      setTimeout(() => setHL(new Set()), 4500);
    }
    if (isMobile) {
      setTimeout(() => {
        mobileFbRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 80);
    }
  }, [step, activeCd, isMobile]);

  const handleAdvance = useCallback(() => {
    if (!canAdvance) return;
    setFb(null);
    dispatch({ type: "ADVANCE", cd: activeCd });
    setTransKey(k => k + 1);
  }, [canAdvance, activeCd]);

  const handleBack = useCallback(() => {
    setFb(null);
    dispatch({ type: "BACK" });
  }, []);

  const handleComplete = useCallback(async cfg => {
    try {
      unlockFromRun(activeCd.slug, cfg.configScore);
      if (onComplete) { await onComplete(cfg); return; }
      await new Promise(r => setTimeout(r, 5600));
      window.location.href = `${dashboardUrl}?score=${cfg.configScore}&profile=${cfg.profile}`;
    } catch {
      window.location.href = dashboardUrl;
    }
  }, [onComplete, dashboardUrl, activeCd.slug, unlockFromRun]);

  const restart = useCallback(() => {
    dispatch({ type: "RESTART", cd: activeCd });
    setFb(null);
    setHL(new Set());
  }, [activeCd]);

  const has3col   = !!fbAction && !isFinished && !editorOpen;
  
  if (!launched || !cd) {
    return (
      <LandingScreen
        templates={templates}
        onLaunch={handleLaunch}
        isDark={isDark}
        onTheme={() => setDark(d => !d)}
        th={th}
      />
    );
  }

  return (
        <div style={{ minHeight: "100vh", background: th.bg, fontFamily: "'DM Sans','Helvetica Neue',system-ui,sans-serif", color: th.text, transition: "background 0.35s, color 0.35s" }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        @keyframes lab-pulse {
          0%,100% { opacity: .4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }

        @keyframes lab-scan {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }

        @keyframes lab-slideRight {
          from { opacity: 0; transform: translateX(32px) scale(0.98); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }

        @keyframes lab-fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes lab-slideRight {
          from { opacity: 0; transform: translateX(32px) scale(0.98); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }

        @keyframes lab-fillBar {
          from { width: 0; }
          to { width: 100%; }
        }

        @keyframes lab-countUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes lab-nodeIn {
          from { opacity: 0; transform: translateY(10px) scale(0.88); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes lab-actionIn {
          from { opacity: 0; transform: translateX(18px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes lab-spin {
          to { transform: rotate(360deg); }
        }

        .lab-fu { animation: lab-fadeUp 0.24s ease-out forwards; }
        
        .lab-grid {
          display: grid;
          gap: 20px;
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px 24px;
          align-items: start;
        }
        
        .lab-grid--3col { grid-template-columns: 260px 1fr 300px; }
        .lab-grid--2col { grid-template-columns: 260px 1fr; }
        .lab-grid--editor { grid-template-columns: 260px 1fr 380px; }

        .lab-left { 
          display: flex; 
          flex-direction: column; 
          gap: 16px; 
          position: sticky; 
          top: 76px;
        }
        
        .lab-right { 
          display: flex; 
          flex-direction: column; 
          gap: 16px; 
          position: sticky; 
          top: 76px;
        }
        
        .lab-editor { 
          position: sticky; 
          top: 54px; 
          height: calc(100vh - 54px); 
          display: flex; 
          flex-direction: column;
        }
        
        .lab-card { 
          transition: border-color 0.2s, box-shadow 0.2s;
          position: relative;
          overflow: hidden;
        }
        
        .lab-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, transparent, ${accent}, transparent);
          animation: lab-scan 4s linear infinite;
          opacity: 0;
          transition: opacity 0.3s;
        }
        
        .lab-card:hover::before {
          opacity: 0.5;
        }

        .lab-card:hover { 
          border-color: ${accent}40 !important; 
        }

        /* Grille fond laboratoire */
        .lab-bg-grid {
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 0;
        }

        textarea, input, select { font-family: inherit; }
        
        ::-webkit-scrollbar { width: 3px; height: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.055); border-radius: 2px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.1); }

        @media (max-width: 1024px) {
          .lab-grid,
          .lab-grid--3col,
          .lab-grid--2col,
          .lab-grid--editor {
            grid-template-columns: 1fr !important;
            padding: 0 0 80px 0 !important;
            gap: 0 !important;
          }
          .lab-left { display: none !important; }
          .lab-editor { display: none !important; }
          .lab-right { display: flex !important; position: static !important; padding: 0 16px 16px; }
        }
      `}</style>

      {/* Fond laboratoire */}
      <div className="lab-bg-grid">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="lab-grid" width="30" height="30" patternUnits="userSpaceOnUse">
              <path d="M 30 0 L 0 0 0 30" fill="none" stroke={th.border} strokeWidth="0.5" opacity="0.15"/>
            </pattern>
            <pattern id="lab-dots" width="15" height="15" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="0.8" fill={th.border} opacity="0.1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#lab-grid)" />
          <rect width="100%" height="100%" fill="url(#lab-dots)" />
        </svg>
      </div>

      <Header
        caseData={cd} eng={eng} config={config} accent={accent}
        th={th} isDark={isDark} fontIdx={fontIdx}
        onTheme={() => setDark(d => !d)} onFont={setFont}
        onBack={handleBack} onRestart={restart}
        onTemplateSelect={handleTemplate} templates={templates}
        finished={isFinished} onToggleEditor={() => setEd(o => !o)}
        editorOpen={editorOpen} canEdit={canEdit} userPlan={userPlan}
        isMobile={isMobile}
        onReturnToLanding={handleReturnToLanding}
        onToggleGuide={() => setGuideOpen(o => !o)}        
      />

      {!isFinished ? (
        <>
          {isMobile ? (
            <div>
              <MobileMetricsBar params={eng.params} initialParams={eng.initialParams} statMeta={statMeta} accent={accent} th={th} />
              <MobileStepHeader stepIdx={eng.stepIdx} totalSteps={cd.steps.length} stepTitle={step?.title} accent={accent} th={th} config={config} />
              <div key={`s${eng.stepIdx}p${eng.phaseIdx}-${transKey}`} className="lab-fu" style={{ padding: "16px 14px", display: "flex", flexDirection: "column", gap: "16px" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", flexWrap: "wrap" }}>
                    <Badge color={accent} th={th} glow>{phasePill}</Badge>
                  </div>
                  {step?.narrativeOverride && <p style={{ fontSize: "10px", fontWeight: 700, color: `${accent}65`, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "6px", fontFamily: "'JetBrains Mono', monospace" }}>{step.narrativeOverride}</p>}
                  <h2 style={{ fontSize: `${Math.round(20 * FS)}px`, fontWeight: 800, color: th.text, lineHeight: 1.22, margin: "0 0 8px", letterSpacing: "-0.02em" }}>{step?.title}</h2>
                  {step?.problematisation && <p style={{ fontSize: `${Math.round(13 * FS)}px`, color: th.textSub, lineHeight: "1.74", fontStyle: "italic", margin: 0 }}>{step.problematisation}</p>}
                </div>
                {/* Reste du contenu mobile inchangé mais avec police JetBrains Mono */}
                {step?.epistemologicalQuestion && isAction && (
                  <div style={{ padding: "11px 14px", borderRadius: "6px", background: `${accent}07`, borderLeft: `3px solid ${accent}40` }}>
                    <p style={{ fontSize: `${Math.round(12 * FS)}px`, color: th.textSub, fontStyle: "italic", lineHeight: "1.7", margin: 0 }}>{step.epistemologicalQuestion}</p>
                  </div>
                )}
                {isNarrative && (
                  <div className="lab-card" style={{ padding: "18px", borderRadius: "8px", background: th.surface, border: `1px solid ${th.border}`, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                    <GuideHint
                      zoneId="narrative"
                      content={GUIDE_CONTENT.zones.find(z => z.id === "narrative")}
                      accent={accent}
                      th={th}
                    />
                    <p style={{ fontSize: `${Math.round(14 * FS)}px`, color: th.text, lineHeight: "1.84", marginBottom: timerDur ? "16px" : "18px" }}>{phase?.narrative || step?.description}</p>
                    {timerDur > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
                        <div style={{ padding: "5px 12px", borderRadius: "4px", background: `${accent}10`, border: `1px solid ${accent}25`, display: "inline-flex", alignItems: "baseline", gap: "3px" }}>
                          <span style={{ fontSize: "20px", fontWeight: 800, color: accent, fontFamily: "'JetBrains Mono', monospace" }}>{remaining}</span>
                          <span style={{ fontSize: "10px", color: `${accent}65`, fontFamily: "'JetBrains Mono', monospace" }}>s</span>
                        </div>
                        <span style={{ fontSize: "12px", color: th.textFaint }}>Passage automatique…</span>
                      </div>
                    )}
                    <AudienceNote
                      step={step}
                      profile={audienceProfile}
                      th={th}
                      accent={accent}
                    />
                    <button onClick={handleAdvance} style={{ padding: "13px 20px", borderRadius: "6px", border: "none", width: "100%", background: `linear-gradient(135deg, ${accent}, ${accent}cc)`, color: "#fff", fontSize: "14px", fontWeight: 700, cursor: "pointer", transition: "opacity 0.14s", boxShadow: `0 4px 20px ${accent}35`, letterSpacing: "-0.01em" }}>
                      {timerDur > 0 ? `Continuer maintenant (${remaining}s)` : "Continuer →"}
                    </button>
                  </div>
                )}
                {isAction && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <GuideHint
                      zoneId="actions"
                      content={GUIDE_CONTENT.zones.find(z => z.id === "actions")}
                      accent={accent}
                      th={th}
                    />
                    {phase?.narrative && <p style={{ fontSize: `${Math.round(13 * FS)}px`, color: th.textSub, lineHeight: "1.74" }}>{phase.narrative}</p>}
                    {phase?.hint && (
                      <div style={{ padding: "10px 13px", borderRadius: "6px", background: th.amberDim, border: `1px solid ${th.amber}18`, display: "flex", gap: "8px", alignItems: "flex-start" }}>
                        <span style={{ fontSize: "13px", flexShrink: 0 }}>💡</span>
                        <p style={{ fontSize: `${Math.round(12 * FS)}px`, color: th.textSub, lineHeight: "1.68", margin: 0 }}>{phase.hint}</p>
                      </div>
                    )}
                    <AudienceNote
                      step={step}
                      profile={audienceProfile}
                      th={th}
                      accent={accent}
                    />
                    {available.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>                        
                        {available.map((act, idx) => <ActionRow key={act.id} act={act} index={idx} accent={accent} done={false} disabled={false} onClick={() => handleAction(act.id)} th={th} animDelay={idx * 65} />)}
                      </div>
                    )}
                    {done.length > 0 && (
                      <div>
                        <p style={{ fontSize: "10px", fontWeight: 700, color: th.textFaint, marginBottom: "7px", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'JetBrains Mono', monospace" }}>✓ ACTIVÉS</p>
                        {done.map((act, idx) => <ActionRow key={act.id} act={act} index={idx} accent={accent} done onClick={() => {}} th={th} />)}
                      </div>
                    )}
                    {fbAction && (
                      <div
                        ref={mobileFbRef} 
                        style={{ padding: "16px", borderRadius: "8px", background: th.surface, border: `1px solid ${th.border}`, borderTop: `3px solid ${accent}`, boxShadow: th.shadowGlow(accent), animation: "lab-fadeUp 0.2s ease-out both" }}>
                        <SectionLabel th={th} accent={accent}>⟡ ANALYSE D'IMPACT</SectionLabel>
                        <FeedbackCard action={fbAction} accent={accent} th={th} />
                      </div>
                    )}
                    {available.length > 1 && (
                      <div style={{
                        padding: "10px 14px",
                        borderRadius: "6px",
                        background: `${accent}08`,
                        border: `1px solid ${accent}25`,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: `${Math.round(12.5 * FS)}px`,
                        color: `${accent}dd`,
                        lineHeight: "1.5",
                        cursor: "default",
                        fontFamily: "'JetBrains Mono', monospace",
                      }}>
                        <span style={{ fontSize: "15px" }}>🔄</span>
                        <span>
                          Diagnostics cumulables — activez plusieurs options
                        </span>
                      </div>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: "7px", marginTop: "4px" }}>
                      <button onClick={handleAdvance} disabled={!canAdvance}
                        style={{ padding: "13px 20px", borderRadius: "6px", border: "none", width: "100%", background: canAdvance ? `linear-gradient(135deg, ${accent}, ${accent}cc)` : th.surfaceAlt, color: canAdvance ? "#fff" : th.textFaint, fontSize: "14px", fontWeight: 700, cursor: canAdvance ? "pointer" : "not-allowed", opacity: canAdvance ? 1 : 0.5, transition: "all 0.18s", boxShadow: canAdvance ? `0 4px 20px ${accent}30` : "none" }}>
                        {canAdvance ? "⟡ CONTINUER →" : "⊘ SÉLECTIONNEZ UNE OPTION"}
                      </button>
                      {isFinalStep && !hasChosen && <p style={{ fontSize: "12px", color: th.danger, textAlign: "center", padding: "8px 12px", background: th.dangerDim, borderRadius: "4px", border: `1px solid ${th.danger}18`, fontFamily: "'JetBrains Mono', monospace" }}>⚠ POSTURE REQUISE POUR FINALISER</p>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={`lab-grid ${editorOpen ? "lab-grid--editor" : has3col ? "lab-grid--3col" : "lab-grid--2col"}`}>
              
              {/* COL 1 — Sidebar laboratoire */}
              <div className="lab-left">
                <div className="lab-card" style={{
                  padding: "16px 18px", borderRadius: "8px",
                  background: th.surface, border: `1px solid ${th.border}`,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}>
                  <SectionLabel th={th} accent={accent}>⟡ ÉCOSYSTÈME</SectionLabel>
                  
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "10px 6px",
                    marginBottom: "8px",
                  }}>
                    {eng.families.map((f, i) => (
                      <FamilyNode
                        key={f.name} family={f}
                        familyColors={familyColors}
                        highlighted={hl.has(f.name)}
                        th={th} staggerIdx={i}
                      />
                    ))}
                  </div>
                  
                  {eng.interactions.length > 0 && (
                    <div style={{
                      marginTop: "12px",
                      paddingTop: "10px",
                      borderTop: `1px solid ${th.border}`,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                    }}>
                      {[...new Set(eng.interactions.map(i => i.type))].map(type => {
                        const ICON = {
                          "structuration": "→",
                          "alimentation": "↗",
                          "érosion": "⊗",
                          "réduction": "↓",
                          "réparation": "↺",
                          "fertilisation": "✦",
                          "renforcement": "⇑",
                          "clarification": "◎",
                        };
                        const count = eng.interactions.filter(i => i.type === type).length;
                        return (
                          <div key={type} style={{
                            display: "flex", alignItems: "center", gap: "4px",
                            padding: "3px 8px", borderRadius: "4px",
                            background: th.surfaceAlt,
                            border: `1px solid ${th.border}`,
                          }}>
                            <span style={{ fontSize: "10px", color: th.textFaint }}>{ICON[type] || "·"}</span>
                            <span style={{ fontSize: "10px", color: th.textSub, fontFamily: "'JetBrains Mono', monospace" }}>{type}</span>
                            <span style={{ fontSize: "9px", color: th.textFaint, background: th.surface, padding: "0 4px", borderRadius: "4px" }}>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                <div className="lab-card" style={{ 
                  padding: "16px", borderRadius: "8px", 
                  background: th.surface, border: `1px solid ${th.border}`,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}>
                  <SectionLabel th={th} accent={accent}>⟡ INDICATEURS</SectionLabel>
                  <GuideHint
                    zoneId="indicateurs"
                    content={GUIDE_CONTENT.zones.find(z => z.id === "indicateurs")}
                    accent={accent}
                    th={th}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                    {Object.entries(eng.params).map(([k, v]) => (
                      <MetricBar 
                        key={k} 
                        label={statMeta[k]?.label || k} 
                        icon={statMeta[k]?.icon} 
                        desc={statMeta[k]?.description} 
                        val={v} 
                        prev={eng.initialParams[k] ?? 50} 
                        th={th} 
                        accent={accent} 
                      />
                    ))}
                  </div>
                </div>
                
                <div className="lab-card" style={{ 
                  padding: "16px", borderRadius: "8px", 
                  background: th.surface, border: `1px solid ${th.border}`,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}>
                  <SectionLabel th={th} accent={accent}>⟡ PROTOCOLE</SectionLabel>
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {cd.steps.map((s, i) => {
                      const done2 = i < eng.stepIdx;
                      const cur   = i === eng.stepIdx;
                      return (
                        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                          <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                            <div style={{ 
                              width: 6, height: 6, borderRadius: "50%", 
                              background: done2 ? th.amber : cur ? accent : `${th.textFaint}20`, 
                              boxShadow: cur ? `0 0 6px ${accent}65` : done2 ? `0 0 4px ${th.amber}40` : "none", 
                              transition: "all 0.4s", 
                              marginTop: "4px" 
                            }} />
                            {i < cd.steps.length - 1 && <div style={{ width: "1px", height: "16px", background: done2 ? `${th.amber}35` : th.border, transition: "background 0.4s", marginTop: "3px" }} />}
                          </div>
                          <span style={{ 
                            fontSize: "11px", 
                            color: done2 ? th.textFaint : cur ? th.text : th.textSub, 
                            fontWeight: cur ? 700 : 400, 
                            lineHeight: "1.4", 
                            paddingBottom: i < cd.steps.length - 1 ? "8px" : 0,
                            fontFamily: "'JetBrains Mono', monospace",
                          }}>
                            {s.title}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {!canEdit && (
                  <div style={{ 
                    padding: "14px 16px", borderRadius: "6px", 
                    background: `linear-gradient(135deg, ${th.amber}08, ${th.amber}04)`, 
                    border: `1px solid ${th.amber}22` 
                  }}>
                    <p style={{ fontSize: "10px", fontWeight: 700, color: th.amber, marginBottom: "6px", display: "flex", alignItems: "center", gap: "5px", fontFamily: "'JetBrains Mono', monospace" }}>
                      ✦ ACCÈS ÉDITEUR
                    </p>
                    <p style={{ fontSize: "11px", color: th.textSub, lineHeight: "1.65", marginBottom: "11px" }}>
                      Créez et modifiez vos protocoles avec l'éditeur intégré.
                    </p>
                    <button style={{ 
                      fontSize: "10px", fontWeight: 600, color: th.amber, 
                      background: th.amberDim, border: `1px solid ${th.amber}28`, 
                      padding: "5px 13px", borderRadius: "4px", cursor: "pointer", 
                      transition: "all 0.14s", fontFamily: "'JetBrains Mono', monospace",
                    }}>
                      → DÉBLOQUER L'ÉDITEUR
                    </button>
                  </div>
                )}
              </div>

              {/* COL 2 — Contenu principal */}
              <div key={`s${eng.stepIdx}p${eng.phaseIdx}-${transKey}`} className="lab-fu" style={{ display: "flex", flexDirection: "column", gap: "20px", minWidth: 0 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px", flexWrap: "wrap" }}>
                    <Badge color={accent} th={th} glow>{phasePill}</Badge>
                    <span style={{ fontSize: "10px", color: th.textFaint, fontFamily: "'JetBrains Mono', monospace" }}>étape {eng.stepIdx + 1}/{cd.steps.length}</span>
                    {config.configScore > 0 && (
                      <span style={{ marginLeft: "auto", fontSize: "10px", fontWeight: 700, color: th.amber, fontFamily: "'JetBrains Mono', monospace", background: `${th.amber}10`, padding: "2px 8px", borderRadius: "4px" }}>
                        score {config.configScore}%
                      </span>
                    )}
                  </div>
                  {step?.narrativeOverride && (
                    <p style={{ fontSize: "10px", fontWeight: 700, color: `${accent}65`, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "8px", fontFamily: "'JetBrains Mono', monospace" }}>
                      {step.narrativeOverride}
                    </p>
                  )}
                  <h2 style={{ fontSize: `${Math.round(24 * FS)}px`, fontWeight: 800, color: th.text, lineHeight: 1.25, margin: "0 0 12px", letterSpacing: "-0.02em" }}>
                    {step?.title}
                  </h2>
                  {step?.problematisation && (
                    <p style={{ fontSize: `${Math.round(14 * FS)}px`, color: th.textSub, lineHeight: "1.7", fontStyle: "italic", margin: 0, borderLeft: `2px solid ${accent}40`, paddingLeft: "14px" }}>
                      {step.problematisation}
                    </p>
                  )}
                </div>
                
                {step?.epistemologicalQuestion && isAction && (
                  <div style={{ padding: "12px 16px", borderRadius: "6px", background: `${accent}05`, borderLeft: `3px solid ${accent}50` }}>
                    <p style={{ fontSize: `${Math.round(12.5 * FS)}px`, color: th.textSub, fontStyle: "italic", lineHeight: "1.7", margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>
                      {step.epistemologicalQuestion}
                    </p>
                  </div>
                )}
                
                {isNarrative && (
                  <div className="lab-card" style={{ 
                    padding: "24px 28px", borderRadius: "8px", 
                    background: th.surface, border: `1px solid ${th.border}`,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                  }}>
                    <p style={{ fontSize: `${Math.round(15 * FS)}px`, color: th.text, lineHeight: "1.8", marginBottom: timerDur ? "20px" : "0" }}>
                      {phase?.narrative || step?.description}
                    </p>
                    {timerDur > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: "14px", marginTop: "20px", paddingTop: "16px", borderTop: `1px solid ${th.border}` }}>
                        <div style={{ padding: "6px 14px", borderRadius: "6px", background: `${accent}10`, border: `1px solid ${accent}25`, display: "inline-flex", alignItems: "baseline", gap: "4px" }}>
                          <span style={{ fontSize: "22px", fontWeight: 800, color: accent, fontFamily: "'JetBrains Mono', monospace" }}>{remaining}</span>
                          <span style={{ fontSize: "10px", color: `${accent}65`, fontFamily: "'JetBrains Mono', monospace" }}>secondes</span>
                        </div>
                        <span style={{ fontSize: "12px", color: th.textFaint, fontFamily: "'JetBrains Mono', monospace" }}>passage automatique</span>
                      </div>
                    )}
                    {phase?.revealDelta && (() => {
                      const entries = Object.entries(eng.params).map(([k, v]) => ({ k, v, d: v - (eng.initialParams[k] ?? 50) })).filter(e => e.d !== 0).sort((a, b) => Math.abs(b.d) - Math.abs(a.d));
                      if (!entries.length) return null;
                      return (
                        <div style={{ 
                          display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(110px,1fr))", 
                          gap: "10px", marginTop: "20px", paddingTop: "16px", borderTop: `1px solid ${th.border}` 
                        }}>
                          {entries.map(({ k, v, d }) => (
                            <div key={k} style={{ padding: "10px 12px", borderRadius: "6px", background: th.surfaceAlt, border: `1px solid ${th.border}` }}>
                              <p style={{ fontSize: "9px", color: th.textFaint, margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'JetBrains Mono', monospace" }}>{statMeta[k]?.label || k}</p>
                              <div style={{ display: "flex", alignItems: "baseline", gap: "5px" }}>
                                <span style={{ fontSize: "20px", fontWeight: 800, color: th.text, fontFamily: "'JetBrains Mono', monospace" }}>{v}</span>
                                <span style={{ fontSize: "10px", fontWeight: 700, color: d > 0 ? th.amber : th.danger }}>{d > 0 ? `+${d}` : d}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    <button onClick={handleAdvance}
                      style={{ 
                        marginTop: "20px", padding: "14px 24px", borderRadius: "6px", 
                        border: "none", width: "100%", background: `linear-gradient(135deg, ${accent}, ${accent}cc)`, 
                        color: "#fff", fontSize: "14px", fontWeight: 700, cursor: "pointer", 
                        transition: "all 0.2s", boxShadow: `0 4px 20px ${accent}35`, 
                        letterSpacing: "-0.01em", fontFamily: "'JetBrains Mono', monospace",
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                      onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                      onMouseDown={e => e.currentTarget.style.transform = "scale(0.98)"}
                      onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}>
                      {timerDur > 0 ? `⟡ CONTINUER (${remaining}s)` : "⟡ CONTINUER →"}
                    </button>
                  </div>
                )}
                
                {isAction && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                    {phase?.narrative && (
                      <p style={{ fontSize: `${Math.round(14 * FS)}px`, color: th.textSub, lineHeight: "1.7", background: th.surfaceAlt, padding: "16px", borderRadius: "8px", border: `1px solid ${th.border}` }}>
                        {phase.narrative}
                      </p>
                    )}
                    {phase?.hint && (
                      <div style={{ padding: "12px 16px", borderRadius: "6px", background: th.amberDim, border: `1px solid ${th.amber}20`, display: "flex", gap: "10px", alignItems: "flex-start" }}>
                        <span style={{ fontSize: "14px", flexShrink: 0 }}>💡</span>
                        <p style={{ fontSize: `${Math.round(12.5 * FS)}px`, color: th.textSub, lineHeight: "1.68", margin: 0 }}>{phase.hint}</p>
                      </div>
                    )}
                    <AudienceNote
                      step={step}
                      profile={audienceProfile}
                      th={th}
                      accent={accent}
                    />
                    {available.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <p style={{ fontSize: "10px", fontWeight: 700, color: th.textFaint, marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'JetBrains Mono', monospace" }}>
                          ⟡ DIAGNOSTICS DISPONIBLES
                        </p>
                        {available.map((act, idx) => (
                          <ActionRow 
                            key={act.id} act={act} index={idx} 
                            accent={accent} done={false} disabled={false} 
                            onClick={() => handleAction(act.id)} th={th} animDelay={idx * 65} 
                          />
                        ))}
                      </div>
                    )}
                    
                    {done.length > 0 && (
                      <div>
                        <p style={{ fontSize: "10px", fontWeight: 700, color: th.textFaint, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'JetBrains Mono', monospace" }}>
                          ✓ DIAGNOSTICS ACTIVÉS
                        </p>
                        {done.map((act, idx) => (
                          <ActionRow key={act.id} act={act} index={idx} accent={accent} done onClick={() => {}} th={th} />
                        ))}
                      </div>
                    )}
                    
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "8px" }}>
                      <button 
                        onClick={handleAdvance} 
                        disabled={!canAdvance}
                        style={{ 
                          padding: "14px 24px", borderRadius: "6px", border: "none", 
                          width: "100%", background: canAdvance ? `linear-gradient(135deg, ${accent}, ${accent}cc)` : th.surfaceAlt, 
                          color: canAdvance ? "#fff" : th.textFaint, fontSize: "14px", fontWeight: 700, 
                          cursor: canAdvance ? "pointer" : "not-allowed", opacity: canAdvance ? 1 : 0.5, 
                          transition: "all 0.2s", boxShadow: canAdvance ? `0 4px 20px ${accent}30` : "none",
                          fontFamily: "'JetBrains Mono', monospace",
                        }}
                      >
                        {canAdvance ? "⟡ CONTINUER LE PROTOCOLE →" : "⊘ ACTIVER UN DIAGNOSTIC"}
                      </button>
                      {isFinalStep && !hasChosen && (
                        <p style={{ 
                          fontSize: "11px", color: th.danger, textAlign: "center", 
                          padding: "10px 14px", background: th.dangerDim, borderRadius: "6px", 
                          border: `1px solid ${th.danger}20`, fontFamily: "'JetBrains Mono', monospace" 
                        }}>
                          ⚠ SÉLECTION VOTRE POSTURE POUR FINALISER
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* COL 3 — Feedback laboratoire */}
              {has3col && (
                <div className="lab-right" style={{ animation: "lab-slideRight 0.38s cubic-bezier(.34,1.4,.64,1) both" }}>
                  <div style={{
                    padding: "20px", borderRadius: "8px",
                    background: th.surface, border: `1px solid ${th.border}`,
                    boxShadow: th.shadowGlow(accent),
                    position: "relative",
                    overflow: "hidden",
                  }}>
                    <div style={{
                      position: "absolute", top: 0, left: 0,
                      height: "3px",
                      background: `linear-gradient(90deg, ${accent}cc, ${accent})`,
                      animation: "lab-fillBar 0.55s cubic-bezier(.22,1,.36,1) 0.1s both",
                    }} />

                    <div style={{ marginBottom: "16px", paddingBottom: "12px", borderBottom: `1px solid ${th.border}` }}>
                      <p style={{
                        fontSize: "9px", fontWeight: 700,
                        color: `${accent}70`,
                        textTransform: "uppercase", letterSpacing: "0.1em",
                        margin: "0 0 6px",
                        fontFamily: "'JetBrains Mono', monospace",
                      }}>⟡ DERNIER DIAGNOSTIC</p>
                      <p style={{
                        fontSize: "13px", fontWeight: 600,
                        color: th.text, lineHeight: "1.5", margin: 0,
                        fontFamily: "'Inter', sans-serif",
                      }}>
                        {fbAction?.label}
                      </p>
                    </div>

                    <SectionLabel th={th} accent={accent}>⟡ ANALYSE D'IMPACT</SectionLabel>
                    <GuideHint
                      zoneId="deepfeedback"
                      content={GUIDE_CONTENT.zones.find(z => z.id === "deepfeedback")}
                      accent={accent}
                      th={th}
                    />
                    <FeedbackCard action={fbAction} accent={accent} th={th} />
                  </div>
                </div>
              )}

              {/* COL 3 — Éditeur */}
              {editorOpen && canEdit && (
                <div className="lab-editor">
                  <EditorPanel 
                    caseData={cd} onUpdate={handleEditorUpdate} 
                    onClose={() => setEd(false)} user={user} 
                    userPlan={userPlan} th={th} 
                  />
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div style={{ 
          padding: "24px", 
          maxWidth: editorOpen ? "1440px" : "1200px", 
          margin: "0 auto", 
          display: "flex", 
          gap: "24px" 
        }}>
          <div style={{ flex: 1 }}>
            <DebriefMain 
              config={config} sol={sol} cd={cd} eng={eng} 
              th={th} onComplete={handleComplete} onRestart={restart} 
              user={user} userPlan={userPlan} onReturnToLanding={handleReturnToLanding}
              audienceProfile={audienceProfile} 
            />
          </div>
          {editorOpen && canEdit && (
            <div style={{ 
              width: '380px', flexShrink: 0, height: 'calc(100vh - 54px)', 
              position: 'sticky', top: 0, paddingTop: '24px',
            }}>
              <EditorPanel 
                caseData={cd} onUpdate={handleEditorUpdate} 
                onClose={() => setEd(false)} userPlan={userPlan} th={th} 
              />
            </div>
          )}
        </div>
      )}

      <AtelierUnlockPopup ateliers={pendingUnlock} onClose={clearPending} />
      <GuidePanel
        isOpen={guideOpen}
        onClose={() => setGuideOpen(false)}
        accent={accent}
        th={th}
        isDark={isDark}
        guideContent={GUIDE_CONTENT}
      />
    </div>
  );
}
